import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Toast } from 'primereact/toast';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import PageLoader from '../components/PageLoader';
import SoldCompPrototype from '../components/SoldCompPrototype';
import DashboardTableFilters from '../components/DashboardTableFilters';
import { AppActionGroup, AppButton, AppField, AppFormDialog } from '../components/authenticated-ui/AuthenticatedUi';
import ResearchRegionsWorkspace, { getCachedResearchRegionsPayload, loadResearchRegionsPayload } from '../components/ResearchRegionsWorkspace';
import { apiJson } from '../services/api';
import { dashboardFilterCount, EMPTY_DASHBOARD_FILTERS, filterDashboardRows, listingTypeOptions } from '../utils/dashboardTableFilters';
import { pairedRentArvRatioRange, pairedRentArvResults, potentialExtraBedroomRange } from '../utils/underwritingCase';
import './RentCastApiLab.css';

ChartJS.register(BarElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const initialForm = {
  market_ids: [], county_fips: [], zcta_targets: [], sourcing_zone_targets: [],
  radius: 25, status: 'Active', property_type: 'Single Family',
  price: '1:65000', limit: 500, request_cap: 1,
  bedrooms: '', bathrooms: '', square_footage: '', year_built: '', days_old: '',
};

function propertySearchTargetKey(target) {
  if (target.type === 'fema_county') return `fema_county:${target.fips}`;
  if (target.type === 'zcta') return `zcta:${target.state_abbr}:${target.zcta5 || target.id}`;
  if (target.type === 'lead_observation') return `observation:${target.id}`;
  return `${target.type}:${target.id}`;
}

function propertySearchTargetLabel(target) {
  if (target.type === 'research_market') return `Region · ${target.name || target.display_label || target.id}`;
  if (target.type === 'fema_county') return `County · ${target.name || target.fips}`;
  if (target.type === 'zcta') return `ZIP area · ${target.zcta5 || target.id}`;
  if (target.type === 'sourcing_zone') return `Polygon · ${target.name || target.id}`;
  return target.name || target.display_label || String(target.id);
}

// For Sale, Sold, and For Rent answer three different questions about three
// different sets of properties, and each carries its own price basis: a
// purchase budget, an after-repair value range, and a monthly rent. They are
// modeled as independent searches so no status can inherit another's price.
// Only For Sale is on by default, and only it has a default price; a wrong
// default for ARV or rent is worse than no filter at all.
const SEARCH_STATUSES = [
  {
    key: 'For_Sale',
    label: 'For sale',
    question: 'What can I buy?',
    priceLabel: 'Asking price',
    priceHint: '',
    unit: '$',
  },
  {
    key: 'Sold',
    label: 'Sold',
    question: 'What is it worth renovated?',
    priceLabel: 'Sold price',
    priceHint: 'ARV range, not purchase price',
    unit: '$',
  },
  {
    key: 'For_Rent',
    label: 'For rent',
    question: 'What will it rent for?',
    priceLabel: 'Monthly rent',
    priceHint: '',
    unit: '$/mo',
  },
];

// States that do not publicly disclose sale prices. A sold search there returns
// records with no closing price on either provider, so the request is blocked
// before it can spend a credit. Missouri varies by county and North Dakota is
// unresolved, so neither carries a blanket label. Mirrors
// SOLD_NON_DISCLOSURE_STATES in backend/property_api_discovery.py.
const soldNonDisclosureStates = new Set(['AK', 'ID', 'KS', 'LA', 'MS', 'MT', 'NM', 'TX', 'UT', 'WY']);

// Zillow documents 16 optional parameters as valid only for specific listing
// statuses, so each status's options dialog offers only its own. Mirrors
// ZILLOW_STATUS_SCOPED_PARAMETERS in backend/property_api_discovery.py, which
// rejects an out-of-scope option server-side.
const zillowStatusScopedOptions = {
  monthlyPayment: ['For_Sale'],
  downPayment: ['For_Sale'],
  listingType: ['For_Sale'],
  listingTypeOptions: ['For_Sale'],
  propertyStatus: ['For_Sale'],
  space: ['For_Rent'],
  move_in_date: ['For_Rent'],
  hideNoDateListings: ['For_Rent'],
  pets: ['For_Rent'],
  maxHOA: ['For_Sale', 'Sold'],
  incIncompleteHOA: ['For_Sale', 'Sold'],
  parkingSpots: ['For_Sale', 'Sold'],
  haveGarage: ['For_Sale', 'Sold'],
  hide55plusComm: ['For_Sale', 'Sold'],
  daysOnZillow: ['For_Sale', 'For_Rent'],
  soldInLast: ['Sold'],
};

export const optionAllowedForStatus = (optionKey, statusKey) => {
  const scoped = zillowStatusScopedOptions[optionKey];
  return !scoped || scoped.includes(statusKey);
};

// Sold defaults are measured rather than guessed. Against retained comps with a
// realistic 3bd/2ba +/-100sqft filter, a 90-day window returns 23 South Bend
// comps where 6 months returns 42, and 12 months adds nothing beyond 6. A
// 90-day default would therefore halve an active market's comp pool for
// freshness an appraiser does not require, and thin markets are worse: the
// Huntington WV pool is 8 comps at every window, so a narrow default is what
// pushes a market toward too few comps to underwrite on.
// `Newest` matches Zillow's own documented Sold default and decides WHICH
// records survive the 1,000-record retrieval cap in dense markets.
const SOLD_DEFAULT_WINDOW = '6_months';

const defaultSearches = () => ({
  For_Sale: { enabled: true, price: '1:65000', advanced_parameters: {} },
  Sold: { enabled: false, price: '', advanced_parameters: { sortOrder: 'Newest', soldInLast: SOLD_DEFAULT_WINDOW } },
  For_Rent: { enabled: false, price: '', advanced_parameters: {} },
});

const initialProviderForms = {
  zillapi: { radius: 5, price: '1:65000', property_type: 'single_family', result_limit: 1 },
  // RealtyAPI bills per request, not per returned record, so a full 200-record
  // page costs the same single credit as one record.
  realtyapi_realtor: { radius: 5, property_type: 'single_family', result_limit: 200, searches: defaultSearches() },
  realtyapi_zillow: { property_type: 'single_family', result_limit: 200, zillow_endpoint: 'search_byaddress', radius: 5, searches: defaultSearches() },
};

// The FBI county crime row is hidden on the API Lab for now; /research keeps it.
const API_LAB_SOURCE_AUDIT_KEYS = ['zillow_market_snapshot', 'fema_nri_counties'];

const providerLabels = {
  rentcast: 'RentCast', zillapi: 'ZillAPI', realtyapi_realtor: 'RealtyAPI', realtyapi_zillow: 'RealtyAPI · Zillow',
};
export const aggregateProviderPulls = (row = {}) => {
  const counts = row.provider_pull_counts && typeof row.provider_pull_counts === 'object'
    ? row.provider_pull_counts
    : Object.fromEntries((row.provider_keys || [row.provider_key || 'rentcast']).map((key) => [key, 1]));
  const grouped = new Map();
  Object.entries(counts).forEach(([providerKey, rawCount]) => {
    const sourceKey = providerKey.startsWith('realtyapi_') ? 'realtyapi' : providerKey;
    const current = grouped.get(sourceKey) || {
      sourceKey,
      iconKey: sourceKey === 'realtyapi' ? 'realtyapi_realtor' : providerKey,
      label: sourceKey === 'realtyapi' ? 'RealtyAPI' : (providerLabels[providerKey] || providerKey),
      count: 0,
    };
    current.count += Math.max(0, Number(rawCount) || 0);
    grouped.set(sourceKey, current);
  });
  return Array.from(grouped.values());
};
const providerDocumentationUrls = {
  rentcast: 'https://developers.rentcast.io',
  zillapi: 'https://zillapi.com/api/',
  realtyapi_realtor: 'https://realtor.realtyapi.io/openapi.json',
  realtyapi_zillow: 'https://www.realtyapi.io/dashboard?tab=playground&api=zillow&endpoint=searchbyaddress',
};
const zillowCoordinatesDocumentationUrl = 'https://www.realtyapi.io/dashboard?tab=playground&api=zillow&endpoint=searchbycoordinates';
const zillowTaxDocumentationUrl = 'https://www.realtyapi.io/dashboard?tab=playground&api=zillow&endpoint=taxinfo';

const realtyApiSources = [
  { label: 'Realtor.com', value: 'realtyapi_realtor' },
  { label: 'Zillow', value: 'realtyapi_zillow' },
  { label: 'Trulia · coming later', value: 'realtyapi_trulia', disabled: true },
];
const realtyApiEndpoints = {
  realtyapi_realtor: [{ label: 'GET /search/bycoordinates', value: 'listing_search' }],
  // Both Zillow routes share one filter contract. Coordinates use the selected
  // lead's retained location, so that route is offered for selected properties only.
  realtyapi_zillow: [
    { label: 'GET /search/byaddress', value: 'search_byaddress' },
    { label: 'GET /search/bycoordinates', value: 'search_bycoordinates', propertyOnly: true },
    { label: 'GET /taxinfo', value: 'taxinfo', propertyOnly: true },
  ],
};
const rentCastPropertyEndpoints = [
  { key: 'rent_comps', label: 'Rent comps', method: 'GET', path: '/v1/avm/rent/long-term', endpoint: 'https://api.rentcast.io/v1/avm/rent/long-term', operation_key: 'long_term_rent_estimate' },
  { key: 'arv_comps', label: 'Property value / ARV', method: 'GET', path: '/v1/avm/value', endpoint: 'https://api.rentcast.io/v1/avm/value', operation_key: 'property_value_estimate' },
  { key: 'property_tax', label: 'Property record / taxes', method: 'GET', path: '/v1/properties', endpoint: 'https://api.rentcast.io/v1/properties', operation_key: 'property_tax_lookup' },
];
const rentCastEndpointChoices = [
  ...rentCastPropertyEndpoints.map((option) => ({ label: `${option.method} ${option.path}`, value: option.key, keys: [option.key] })),
];
const rentCastPropertyTypes = ['Single Family', 'Condo', 'Townhouse', 'Manufactured', 'Multi-Family', 'Apartment'];
const createExecutionToken = () => window.crypto?.randomUUID?.()
  || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
const realtyApiPropertyTypes = {
  realtyapi_realtor: [
    ['Single family', 'single_family'], ['Multi-family', 'multi_family'], ['Condo', 'condo'],
    ['Townhouse', 'townhouse'], ['Land', 'land'], ['Manufactured', 'manufactured'],
    ['Farm', 'farm'], ['Co-op', 'coop'],
  ],
  realtyapi_zillow: [
    ['Houses', 'single_family'], ['Multi-family', 'multi_family'], ['Condos / co-ops', 'condo'],
    ['Townhomes', 'townhouse'], ['Lots / land', 'land'], ['Apartments', 'apartment'],
    ['Manufactured', 'manufactured'],
  ],
};
const zillowAdvancedOptions = [
  ['sortOrder', 'Sort order', ['Homes_for_you','Rental_Priority_Score','Price_High_to_Low','Price_Low_to_High','Newest','Bedrooms','Bathrooms','Square_Feet','Lot_Size','Year_Built']],
  ['monthlyPayment', 'Monthly payment'], ['downPayment', 'Down payment'],
  ['bed_min', 'Minimum bedrooms', ['No_Min','Studio','1','2','3','4','5']],
  ['bed_max', 'Maximum bedrooms', ['No_Max','Studio','1','2','3','4','5']],
  ['bathrooms', 'Bathrooms', ['Any','OnePlus','OneHalfPlus','TwoPlus','ThreePlus','FourPlus']],
  ['space', 'Space'], ['maxHOA', 'Maximum HOA', ['Any','No_HOA_Fee','50_dollars_month','100_dollars_month','200_dollars_month','300_dollars_month','400_dollars_month','500_dollars_month','600_dollars_month','700_dollars_month','800_dollars_month','900_dollars_month','1000_dollars_month']],
  ['incIncompleteHOA', 'Include incomplete HOA', null, 'boolean'],
  ['listingType', 'Listing type', ['Any','By_Agent','By_Owner_and_Other']],
  ['listingTypeOptions', 'Listing type options'], ['propertyStatus', 'Property status'],
  ['tours', 'Tours'], ['parkingSpots', 'Parking spots', ['Any','OnePlus','TwoPlus','ThreePlus','FourPlus']],
  ['haveGarage', 'Must have garage', null, 'boolean'], ['move_in_date', 'Move-in date'],
  ['hideNoDateListings', 'Hide listings without date', null, 'boolean'],
  ['squareFeetRange', 'Square-foot range'], ['lotSizeRange', 'Lot-size range'],
  ['yearBuiltRange', 'Year-built range'], ['mustHaveBasement', 'Basement', ['No','Yes_Finished','Yes_Unfinished','Yes_Both']],
  ['singleStoryOnly', 'Single story only', null, 'boolean'], ['hide55plusComm', 'Hide 55+ communities', null, 'boolean'],
  ['pets', 'Pets'], ['otherAmenities', 'Other amenities'], ['view', 'View'],
  ['daysOnZillow', 'Days on Zillow', ['Any','1_day','7_days','14_days','30_days','90_days','6_months','12_months','24_months','36_months']],
  ['soldInLast', 'Sold in last', ['Any','1_day','7_days','14_days','30_days','90_days','6_months','12_months','24_months','36_months']],
  ['keywords', 'Keywords'],
];
const realtorAdvancedOptions = [
  ['sortOrder', 'Sort order', ['Recommended','Newest','Oldest','Price_High_to_Low','Price_Low_to_High','Square_Feet','Lot_Size','Beds','Baths','Most_Recently_Sold','Open_House_Date']],
  ['bedsRange', 'Bedroom range'], ['bathsRange', 'Bathroom range'],
  ['sqftRange', 'Square-foot range'], ['lotSqftRange', 'Lot square-foot range'],
  ['yearBuiltRange', 'Year-built range'], ['hoaFeeMax', 'Maximum monthly HOA'],
  ['newConstruction', 'New construction', null, 'boolean'],
  ['foreclosure', 'Foreclosure', null, 'boolean'],
  ['pending', 'Pending / under contract', null, 'boolean'],
  ['seniorCommunity', 'Senior community', null, 'boolean'],
  ['openHouse', 'Upcoming open house', null, 'boolean'],
  ['hasTour', 'Has virtual tour', null, 'boolean'],
  ['hasPhotos', 'Has photos', null, 'boolean'],
  ['hasSpecials', 'Has specials / incentives', null, 'boolean'],
  ['petsAllowed', 'Pets allowed'], ['keywords', 'Keywords'],
  ['daysOnMarketMax', 'Maximum days on market'],
  ['priceReducedDays', 'Price reduced within days'],
];

// Zillow closing prices are not a reliable comp source in these documented
// non-disclosure states. MO varies by county; ND remains unresolved.
const zillowSoldNonDisclosureStates = new Set(['AK', 'ID', 'KS', 'LA', 'MS', 'MT', 'NM', 'TX', 'UT', 'WY']);

// Providers report the same concept in different shapes (RentCast "Single Family",
// RealtyAPI "single_family", ZillAPI "SINGLE_FAMILY"). Normalise for display only;
// the immutable observation keeps each provider's original wording.
export const displayLabel = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/[a-z]/.test(raw) && /[A-Z]/.test(raw) && !raw.includes('_')) return raw;
  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b[a-z]/g, (character) => character.toUpperCase());
};

const providerLogoUrls = {
  rentcast: '/provider-icons/rentcast.png',
  zillapi: '/provider-icons/zillapi.png',
  realtyapi_realtor: '/provider-icons/realtyapi.png',
  realtyapi_zillow: '/provider-icons/realtyapi.png',
};

const ProviderSourceMark = ({ providerKey, title }) => providerLogoUrls[providerKey]
  ? <img className="rcl-source-mark" src={providerLogoUrls[providerKey]} alt="" role="img" aria-label={`${providerLabels[providerKey] || providerKey} source`} title={title || providerLabels[providerKey] || providerKey} />
  : null;

const FastSelect = ({ value, options, onChange, ariaLabel }) => <select className="rcl-native-select" value={value ?? ''} onChange={(event) => {
  const selected = options.find((option) => String(option.value) === event.target.value);
  onChange(selected ? selected.value : event.target.value);
}} aria-label={ariaLabel}>
  {options.map((option) => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
</select>;

const recentApiSourceLabel = (pull) => {
  const status = pull?.request_parameters?.listingStatus || pull?.request_parameters?.searchType;
  const statusLabel = status === 'Sold' ? 'sold search' : status === 'For_Rent' ? 'rental search' : 'for-sale search';
  if (pull?.provider_key === 'realtyapi_zillow') return `RealtyAPI · Zillow ${statusLabel}`;
  if (pull?.provider_key === 'realtyapi_realtor') return `RealtyAPI · Realtor.com ${statusLabel}`;
  if (pull?.provider_key === 'zillapi') return 'ZillAPI · Zillow for-sale search';
  return 'RentCast · sale-listing search';
};

// One RealtyAPI request per enabled status per region. Falls back to the legacy
// run_comps shape so older callers keep projecting correctly.
export const realtyApiSearchesPerRegion = (config = {}) => {
  if (config.searches) {
    return SEARCH_STATUSES.filter(({ key }) => config.searches[key]?.enabled).length;
  }
  return config.run_comps ? 2 : 1;
};

export const providerSearchProjection = (providerKey, selectedRegionCount, config = {}, provider = {}) => {
  const regions = Math.max(0, Number(selectedRegionCount) || 0);
  const resultLimit = Math.max(1, Number(config.result_limit) || 1);
  const units = regions === 0
    ? 0
    : config.fixed_requests_per_target != null
      ? regions * Math.max(0, Number(config.fixed_requests_per_target) || 0)
      : providerKey === 'zillapi'
      ? regions * resultLimit
      : providerKey === 'rentcast'
        ? Math.max(regions, Number(config.request_cap) || regions)
        : regions * realtyApiSearchesPerRegion(config);
  const unit = provider?.plan?.billing_unit_type === 'result' ? 'credit' : 'request';
  const safeBefore = Math.max(0, Number(provider?.quota?.safe_available_units) || 0);
  return {
    units,
    unit,
    label: `${units} ${unit}${units === 1 ? '' : 's'}`,
    safeBefore,
    safeAfter: Math.max(0, safeBefore - units),
    exceedsSafeBalance: units > safeBefore,
  };
};

export const propertyApiCompletionSummary = (results = []) => {
  const completed = Array.isArray(results) ? results : [];
  const returned = completed.reduce((total, item) => total + Number(item?.result_count || 0), 0);
  const saved = completed.reduce((total, item) => total + Number(item?.normalized_record_count ?? item?.result_count ?? 0), 0);
  const additionalPages = completed.reduce((total, item) => total + Number(item?.pagination_evidence?.estimated_remaining_pages || 0), 0);
  const byOperation = completed.reduce((groups, item) => {
    const key = item?.operation_key || 'unknown';
    const current = groups[key] || { returned: 0, saved: 0 };
    current.returned += Number(item?.result_count || 0);
    current.saved += Number(item?.normalized_record_count ?? item?.result_count ?? 0);
    groups[key] = current;
    return groups;
  }, {});
  return { returned, saved, additionalPages, byOperation };
};

const completionOperationLabel = {
  sale_listing_search: 'For Sale leads', sold_comp_search: 'Sold comps', rental_comp_search: 'Rent comps',
  property_tax_lookup: 'Property tax',
};

const rangeOptions = [
  { label: 'Last 7 days', value: '7' }, { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' }, { label: 'All history', value: 'all' },
];

const localDayKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const chartDayKeys = (days, activityDays = [], now = new Date()) => {
  if (days === 'all') return Array.from(new Set(activityDays.filter(Boolean))).sort();
  const dayCount = Number(days);
  if (!Number.isFinite(dayCount) || dayCount < 1) return [];
  const end = new Date(now);
  end.setHours(12, 0, 0, 0);
  return Array.from({ length: dayCount }, (_, index) => {
    const date = new Date(end);
    date.setDate(end.getDate() - (dayCount - index - 1));
    return localDayKey(date);
  });
};

const aggregateRegionMetrics = (rows) => rows.reduce((total, row) => ({
  ...total,
  requested_at: !total.requested_at || String(row.requested_at) > String(total.requested_at) ? row.requested_at : total.requested_at,
  result_count: Number(total.result_count || 0) + Number(row.result_count || 0),
  new_leads: Number(total.new_leads || 0) + Number(row.new_leads || 0),
  duplicate_records: Number(total.duplicate_records || 0) + Number(row.duplicate_records ?? Math.max(Number(row.result_count || 0) - Number(row.new_leads || 0), 0)),
  unique_leads: Number(total.unique_leads || 0) + Number(row.unique_leads || 0),
  inside_rows: Number(total.inside_rows || 0) + Number(row.inside_rows || 0),
  outside_rows: Number(total.outside_rows || 0) + Number(row.outside_rows || 0),
}), {});

const bedroomSpaceLabels = {
  high_opportunity: 'High',
  review: 'Review',
  typical: 'Typical',
  insufficient_data: 'No data',
};

const CLEAN_DIALOG_TRANSITION = { timeout: 0 };
const DISPLAY_TIME_ZONE = 'America/Denver';

const money = (value) => value == null ? '—' : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const InlineOfferPrice = React.memo(function InlineOfferPrice({ row, value, askingPrice, hasOffer, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const committing = useRef(false);
  const cancelled = useRef(false);

  const beginEditing = (event) => {
    event.stopPropagation();
    setDraft(value);
    setError('');
    cancelled.current = false;
    setEditing(true);
  };
  const commit = async () => {
    if (cancelled.current || committing.current) return;
    committing.current = true;
    setSaving(true);
    setError('');
    try {
      // An empty field clears the offer and returns to the asking price.
      const cleared = draft === '' || draft === null || draft === undefined;
      await onSave(row, cleared ? null : Number(draft) || 0);
      setEditing(false);
    } catch (saveError) {
      setError(saveError.message || 'Unable to save offer price.');
    } finally {
      committing.current = false;
      setSaving(false);
    }
  };

  const showAsking = hasOffer && askingPrice != null && Number(askingPrice) !== Number(value);
  if (!editing) return <span className="rcl-offer-price">
    <button type="button" className="rcl-cost-value" onClick={beginEditing} aria-label={`Edit offer price for ${row.formatted_address || 'property'}; ${money(value)}${hasOffer ? '' : ' (asking price)'}`}>
      <span>{money(value)}</span><i className="pi pi-pencil" aria-hidden="true" />
    </button>
    {showAsking && <small className="rcl-offer-price__asking">Ask {money(askingPrice)}</small>}
  </span>;

  return <span className="rcl-purchase-price-editor" onClick={(event) => event.stopPropagation()}>
    <span aria-hidden="true">$</span>
    <input
      type="number"
      min="0"
      max="9999999999.99"
      step="0.01"
      value={draft}
      aria-label={`Offer price for ${row.formatted_address || 'property'}`}
      autoFocus
      disabled={saving}
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          event.preventDefault();
          cancelled.current = true;
          setEditing(false);
          setError('');
        }
      }}
    />
    {saving && <i className="pi pi-spin pi-spinner" aria-label="Saving offer price" />}
    {error && <small role="alert">{error}</small>}
  </span>;
});
const moneyPerSquareFoot = (value) => value == null ? '—' : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dollars = (value) => Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const projectedIncrementalCost = (currentUsage, plannedCredits, includedCredits, overageCostPerCredit) => {
  const usage = Math.max(0, Number(currentUsage) || 0);
  const credits = Math.max(0, Number(plannedCredits) || 0);
  const included = Math.max(0, Number(includedCredits) || 0);
  const rate = Math.max(0, Number(overageCostPerCredit) || 0);
  return Math.max(0, usage + credits - included) * rate - Math.max(0, usage - included) * rate;
};

export const googlePropertySearchUrl = (address) => address
  ? `https://www.google.com/search?q=${encodeURIComponent(address)}`
  : null;

export const researchMarketPath = (marketSlug) => marketSlug
  ? `/research/markets/${encodeURIComponent(marketSlug)}`
  : null;

export const filterObservationsByMaxPrice = (observations, maximumPrice) => {
  if (maximumPrice == null || maximumPrice === '') return observations;
  const ceiling = Number(maximumPrice);
  if (!Number.isFinite(ceiling)) return observations;
  return observations.filter((row) => row.asking_price != null && Number(row.asking_price) <= ceiling);
};

export const filterObservationsByRun = (observations, runId) => runId
  ? observations.filter((row) => row.run_id === runId || (row.observation_run_ids || []).includes(runId))
  : observations;

export const filterNewObservationsByBatch = (observations, comparisonBatchId) => comparisonBatchId
  ? observations.filter((row) => (row.new_comparison_batch_ids || []).includes(comparisonBatchId))
  : observations;

export const filterObservationsByReview = (observations, reviewScope) => {
  if (reviewScope === 'all') return observations;
  if (reviewScope === 'active') return observations.filter((row) => (row.review_status || 'unreviewed') !== 'passed');
  return observations.filter((row) => (row.review_status || 'unreviewed') === reviewScope);
};

export const regionHistoryKey = (row) => String(row?.region_key ?? row?.market_id ?? 'unknown');

// The decision views answer "what did I just file?", so they lead with the newest
// decision rather than discovery order. Rows missing reviewed_at sort last so a
// legacy decision saved before the timestamp existed cannot displace a recent one.
export const sortObservationsByReviewedAt = (observations) => {
  const reviewedTime = (row) => {
    const parsed = new Date(row?.reviewed_at || '').getTime();
    return Number.isNaN(parsed) ? null : parsed;
  };
  return [...observations].sort((left, right) => {
    const leftTime = reviewedTime(left);
    const rightTime = reviewedTime(right);
    if (leftTime === rightTime) return 0;
    if (leftTime === null) return 1;
    if (rightTime === null) return -1;
    return rightTime - leftTime;
  });
};

export const filterHistoryPayloadByDays = (payload, days, now = new Date()) => {
  if (!payload || days === 'all') return payload;
  const dayCount = Number(days);
  if (!Number.isFinite(dayCount)) return payload;
  const cutoff = new Date(now);
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (dayCount - 1));
  const inRange = (value) => {
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) && date >= cutoff;
  };
  return {
    ...payload,
    timeline: (payload.timeline || []).filter((row) => inRange(`${row.day}T23:59:59`)),
    provider_timeline: (payload.provider_timeline || []).filter((row) => inRange(`${row.day}T23:59:59`)),
    region_history: (payload.region_history || []).filter((row) => inRange(row.requested_at)),
    latest_request: payload.latest_request && inRange(payload.latest_request.requested_at) ? payload.latest_request : null,
  };
};

const payloadDayKey = (value) => {
  const text = String(value || '');
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return localDayKey(value);
};

export const filterTablePayloadByDays = (payload, days) => {
  if (!payload || days === 'all') return payload;
  const dayCount = Number(days);
  if (!Number.isFinite(dayCount) || dayCount < 1) return payload;
  const end = payload.history_today ? new Date(`${payload.history_today}T12:00:00`) : new Date();
  const cutoff = new Date(end);
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (dayCount - 1));
  const observations = (payload.observations || []).filter((row) => {
    const observed = new Date(row.last_seen_at || row.requested_at || 0);
    return !Number.isNaN(observed.getTime()) && observed >= cutoff;
  });
  return {
    ...payload,
    observations,
    summary: {
      ...(payload.summary || {}),
      unique_leads: observations.length,
      rows_returned: observations.reduce((total, row) => total + Number(row.observation_count || 1), 0),
    },
  };
};

export const sortTooltipItemsByLinePosition = (first, second) => {
  const pixelFor = (item) => {
    const axisId = item.dataset?.yAxisID || 'y';
    const scale = item.chart?.scales?.[axisId];
    return scale?.getPixelForValue ? scale.getPixelForValue(item.parsed?.y) : 0;
  };
  return pixelFor(first) - pixelFor(second);
};

export const formatPulledDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit', timeZone: DISPLAY_TIME_ZONE });
};

export const formatPulledDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${formatPulledDate(value)} · ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: DISPLAY_TIME_ZONE })}`;
};

export const formatPullRecency = (value, now = new Date()) => {
  if (!value) return 'previously';
  const pulledAt = new Date(value);
  const current = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(pulledAt.getTime()) || Number.isNaN(current.getTime())) return 'previously';
  const elapsedDays = Math.max(0, Math.floor((current.getTime() - pulledAt.getTime()) / 86400000));
  if (elapsedDays === 0) return 'today';
  if (elapsedDays === 1) return 'yesterday';
  return `${elapsedDays} days ago`;
};

const regionChartPalette = ['#60a5fa', '#6ee7b7', '#fbbf24', '#c084fc', '#22d3ee', '#fb923c', '#a3e635', '#818cf8', '#2dd4bf', '#38bdf8', '#facc15', '#4ade80', '#a78bfa', '#06b6d4', '#84cc16', '#93c5fd', '#34d399', '#eab308', '#8b5cf6', '#14b8a6'];
const formatRunDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Provider logos drawn onto the region chart canvas. Images must already be
// decoded before Chart.js paints, so they are loaded once at module scope and
// the chart is redrawn when the last one resolves.
const providerLogoImages = {};
let providerLogosPending = 0;
const loadProviderLogoImages = (onAllLoaded) => {
  Object.entries(providerLogoUrls).forEach(([key, url]) => {
    if (providerLogoImages[key]) return;
    const image = new Image();
    providerLogosPending += 1;
    image.onload = () => {
      providerLogoImages[key] = image;
      providerLogosPending -= 1;
      if (providerLogosPending === 0 && onAllLoaded) onAllLoaded();
    };
    image.onerror = () => { providerLogosPending -= 1; };
    image.src = url;
  });
};

const regionBarLabelsPlugin = {
  id: 'rentcastRegionBarLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (dataset.segmentType !== 'new') return;
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((element, dataIndex) => {
        const metric = dataset.regionMetrics?.[dataIndex];
        if (!metric || !element || typeof element.y !== 'number') return;
        // Anchor to the drawn top of the whole stack (new + repeat segments) so
        // the label clears a stacked bar by the same margin as an unstacked one.
        const stackTop = chart.data.datasets.reduce((top, other, otherIndex) => {
          if (other.stack !== dataset.stack) return top;
          const otherElement = chart.getDatasetMeta(otherIndex).data?.[dataIndex];
          const otherY = otherElement?.y;
          return typeof otherY === 'number' && otherY < top ? otherY : top;
        }, element.y);
        const logo = dataset.logoReady ? providerLogoImages[dataset.providerKey] : null;
        const logoSize = 11;
        const gap = 4;
        ctx.save();
        ctx.fillStyle = '#e2e8f0';
        ctx.font = '700 9px sans-serif';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        // Tilted to avoid collisions when several regions are pulled in one run.
        // The label is rotated about its lower-left end and that end is pinned a
        // fixed distance above the bar, so every label clears its bar by the same
        // margin no matter how long the text or how tall the bar.
        const text = logo ? dataset.marketName : dataset.regionName;
        const angle = Math.PI * 0.31;
        ctx.translate(element.x + 3, stackTop - 8);
        ctx.rotate(-angle);
        if (logo) {
          ctx.drawImage(logo, 0, -logoSize / 2, logoSize, logoSize);
          ctx.fillText(text, logoSize + gap, 0);
        } else {
          ctx.fillText(text, 0, 0);
        }
        ctx.restore();
      });
    });
  },
};

export const formatRenewalDate = (value) => {
  if (!value) return 'Renewal date';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Renewal date';
  return `Renews ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

export const rentArvRatioRange = (rentScenario, arvScenario) => {
  return pairedRentArvRatioRange(rentScenario, arvScenario);
};

export const holdingCostTotal = (costs = {}) => [
  'financing_costs', 'property_taxes', 'insurance',
  'utilities', 'permits_and_inspections', 'lawn_snow_security', 'other_costs',
].reduce((total, key) => total + (Number(costs?.[key]) || 0), 0);

export const fixedCostTotal = (costs = {}) => [
  'cameras', 'smart_locks', 'internet', 'security_system',
  'database_management', 'other_costs',
].reduce((total, key) => total + (Number(costs?.[key]) || 0), 0);

export const effectiveOfferPrice = (row = {}) => {
  const saved = row.offer?.offer_price;
  if (saved !== null && saved !== undefined && saved !== '') {
    const numericSaved = Number(saved);
    if (Number.isFinite(numericSaved)) return numericSaved;
  }
  return Number(row.asking_price);
};

export const maxRehabAllowance = (arvScenario, askingPrice, fixedCosts = 0) => {
  const conservativeArv = Number(arvScenario?.low);
  const price = Number(askingPrice);
  const costs = Number(fixedCosts) || 0;
  if (!Number.isFinite(conservativeArv) || conservativeArv <= 0 || !Number.isFinite(price)) return null;
  return (conservativeArv * 0.75) - price - costs;
};

export const maxRehabRange = (arvScenario, purchasePrice, additionalCosts = 0) => {
  const low = maxRehabAllowance({ low: arvScenario?.low }, purchasePrice, additionalCosts);
  const high = maxRehabAllowance({ low: arvScenario?.high ?? arvScenario?.low }, purchasePrice, additionalCosts);
  return low == null || high == null ? null : { low: Math.min(low, high), high: Math.max(low, high) };
};

export const amortizedPayment = (principal, annualRatePct, termMonths) => {
  const amount = Number(principal);
  const months = Number(termMonths);
  const rate = Number(annualRatePct) / 100 / 12;
  if (!Number.isFinite(amount) || amount < 0 || !Number.isFinite(months) || months <= 0 || !Number.isFinite(rate) || rate < 0) return null;
  if (amount === 0) return 0;
  if (rate === 0) return amount / months;
  return amount * (rate * ((1 + rate) ** months)) / (((1 + rate) ** months) - 1);
};

export const projectedCashFlowRange = (rentScenario, arvScenario, defaults = {}, assumptions = {}) => {
  const lowRent = Number(rentScenario?.low);
  const highRent = Number(rentScenario?.high ?? rentScenario?.low);
  const lowArv = Number(arvScenario?.low);
  const highArv = Number(arvScenario?.high ?? arvScenario?.low);
  if (![lowRent, highRent, lowArv, highArv].every((value) => Number.isFinite(value) && value > 0)) return null;
  const ltv = Number(defaults.refi_ltv_pct ?? 75);
  const rate = Number(defaults.interest_rate_pct ?? 7.5);
  const term = Number(defaults.loan_term_months ?? 360);
  const overrideRaw = assumptions.expected_loan_amount;
  const hasOverride = overrideRaw !== null && overrideRaw !== undefined && overrideRaw !== '' && Number.isFinite(Number(overrideRaw));
  // Itemized so the modal can show how every form field reaches the result.
  const fixedItems = CASH_FLOW_FIXED_ITEMS.map(([key, label, source, months]) => {
    const entered = Number((source === 'defaults' ? defaults : assumptions)[key]) || 0;
    return { key, label, entered, perYear: months === 12, monthly: entered / months };
  });
  const fixedMonthly = fixedItems.reduce((total, item) => total + item.monthly, 0);
  const reserveItems = CASH_FLOW_RESERVE_ITEMS.map(([key, label]) => ({ key, label, pct: Number(defaults[key]) || 0 }));
  const reservePct = reserveItems.reduce((total, item) => total + item.pct, 0);
  // Percent-of-rent operating expenses (management), kept separate from reserves.
  const operatingPctItems = CASH_FLOW_OPERATING_PCT_ITEMS.map(([key, label]) => ({ key, label, pct: Number(defaults[key]) || 0 }));
  const operatingPct = operatingPctItems.reduce((total, item) => total + item.pct, 0);
  const calculate = (rent, arv) => {
    const loanAmount = hasOverride ? Number(overrideRaw) : arv * (ltv / 100);
    const mortgage = amortizedPayment(loanAmount, rate, term);
    if (mortgage == null) return null;
    const reserves = rent * (reservePct / 100);
    const operatingPercent = rent * (operatingPct / 100);
    const operatingTotal = operatingPercent + fixedMonthly;
    const percentageExpenses = reserves + operatingPercent;
    const noi = rent - reserves - operatingTotal;
    return { rent, arv, cashFlow: noi - mortgage, mortgage, loanAmount, noi, dscr: mortgage > 0 ? noi / mortgage : null, reserves, operatingPercent, operatingTotal, percentageExpenses, fixedMonthly };
  };
  const conservative = calculate(lowRent, highArv);
  const optimistic = calculate(highRent, lowArv);
  if (!conservative || !optimistic) return null;
  return {
    low: Math.min(conservative.cashFlow, optimistic.cashFlow),
    high: Math.max(conservative.cashFlow, optimistic.cashFlow),
    conservative, optimistic, reservePct, reserveItems, operatingPct, operatingPctItems, fixedItems, hasLoanOverride: hasOverride,
  };
};

// Spells out how each end of the ARV range becomes a refinance loan so the
// cash-flow tooltip can teach the math, not just report the result.
export const loanSizingSteps = (arvScenario, defaults = {}, assumptions = {}) => {
  const lowArv = Number(arvScenario?.low);
  const highArv = Number(arvScenario?.high ?? arvScenario?.low);
  if (![lowArv, highArv].every((value) => Number.isFinite(value) && value > 0)) return null;
  const ltvPct = Number(defaults.refi_ltv_pct ?? 75);
  const rate = Number(defaults.interest_rate_pct ?? 7.5);
  const term = Number(defaults.loan_term_months ?? 360);
  const overrideRaw = assumptions.expected_loan_amount;
  const override = overrideRaw !== null && overrideRaw !== undefined && overrideRaw !== '' && Number.isFinite(Number(overrideRaw))
    ? Number(overrideRaw) : null;
  const lane = (key, label, arv) => {
    const loan = override ?? arv * (ltvPct / 100);
    return {
      key, label, arv, loan,
      payment: amortizedPayment(loan, rate, term),
      equity: arv - loan,
      loanSharePct: Math.max(0, Math.min(100, (loan / arv) * 100)),
    };
  };
  return {
    ltvPct, rate, term, override,
    lanes: [lane('low', 'Low ARV', lowArv), lane('high', 'High ARV', highArv)],
  };
};

// Operator rent adjustment for Projected Cash Flow only. It never changes comp
// evidence, comp rent columns, or 1% Rule screens. One value or a low-high range.
export const cashFlowRentAdjustment = (assumptions = {}) => {
  const low = Number(assumptions?.rent_override_low);
  const high = Number(assumptions?.rent_override_high);
  const hasLow = Number.isFinite(low) && low > 0;
  const hasHigh = Number.isFinite(high) && high > 0;
  if (!hasLow && !hasHigh) return null;
  const first = hasLow ? low : high;
  const second = hasHigh ? high : first;
  return { low: Math.min(first, second), high: Math.max(first, second), isAdjusted: true };
};

// Line-by-line cash-flow math shared by the tooltip and the assumptions modal,
// so both teach the same subtraction from rent down to cash flow.
export const CashFlowMathTable = ({ calculation, ltvPct, rentAdjusted = false, rentSource = null, propertyTaxSource = null, className = '' }) => {
  if (!calculation) return null;
  const cases = [calculation.conservative, calculation.optimistic];
  const minus = (value) => (value > 0 ? `−${money(value)}` : money(0));
  const reserves = calculation.reserveItems.filter((item) => item.pct > 0);
  const operating = calculation.fixedItems.filter((item) => item.entered > 0);
  const sourceLabel = (label, source) => source === 'rentcast'
    ? <span className="rcl-source-label">{label}<ProviderSourceMark providerKey="rentcast" /></span>
    : label;
  const loanLabel = calculation.hasLoanOverride ? 'loan override' : `${Number(ltvPct)}% of ARV`;
  // Each section is a list of rows; the first row carries a rotated section
  // label spanning the section, to the left of its color bar.
  const section = (name, rows) => rows.map(([key, label, values, rowClass = ''], index) => <tr key={key} className={rowClass}>
    {index === 0 && <th scope="rowgroup" rowSpan={rows.length} className="rcl-cash-flow-math__section">{name && <span>{name}</span>}</th>}
    <th scope="row">{label}</th>{values.map((value, valueIndex) => <td key={valueIndex}>{value}</td>)}
  </tr>);
  return <table className={`rcl-cash-flow-math ${className}`.trim()} aria-label="How projected cash flow is calculated">
    <thead><tr><td aria-hidden="true" /><th scope="col">Monthly</th><th scope="col">Conservative</th><th scope="col">Optimistic</th></tr></thead>
    <tbody className="is-income">{section('Income', [
      ['rent', sourceLabel(rentAdjusted ? 'Rent (your adjustment)' : 'Rent (comps)', rentAdjusted ? null : rentSource), cases.map((item) => money(item.rent))],
    ])}</tbody>
    <tbody className="is-reserves">{section('Reserves', [
      ...reserves.map((item) => [item.key, `${item.label} ${item.pct}%`, cases.map((entry) => minus(entry.rent * item.pct / 100))]),
      ['reserves-total', `Reserves total (${calculation.reservePct}% of rent)`, cases.map((item) => minus(item.reserves)), 'is-subtotal'],
    ])}</tbody>
    <tbody className="is-operating">{section('Operating', [
      ...calculation.operatingPctItems.filter((item) => item.pct > 0).map((item) => [item.key, `${item.label} ${item.pct}% of rent`, cases.map((entry) => minus(entry.rent * item.pct / 100))]),
      ...operating.map((item) => [item.key, sourceLabel(item.perYear ? `${item.label} ${money(item.entered)}/yr ÷ 12` : item.label, item.key === 'annual_property_taxes' ? propertyTaxSource : null), cases.map(() => minus(item.monthly))]),
      ['operating-total', 'Operating total', cases.map((item) => minus(item.operatingTotal)), 'is-subtotal'],
    ])}</tbody>
    <tbody className="is-noi">{section(null, [
      ['noi', 'NOI = Rent − reserves − operating', cases.map((item) => money(item.noi)), 'is-result'],
    ])}</tbody>
    <tbody className="is-debt">{section('Debt', [
      ['mortgage', `Mortgage P&I (${loanLabel})`, cases.map((item) => minus(item.mortgage))],
    ])}</tbody>
    <tbody className="is-cash-flow">{section('Net', [
      ['cash-flow', 'Cash flow = NOI − P&I', cases.map((item) => money(item.cashFlow)), 'is-total'],
      ['dscr', <><strong>DSCR = NOI ÷ P&amp;I</strong><span className="rcl-cash-flow-math__hint">Lenders shoot for ≥ 1.20–1.25</span></>, cases.map((item) => (item.dscr == null ? '—' : item.dscr.toFixed(2))), 'is-note'],
    ])}</tbody>
  </table>;
};

const HOLDING_COST_OPTIONS = [
  ['financing_costs', 'Financing costs'],
  ['property_taxes', 'Property taxes'],
  ['insurance', 'Insurance'],
  ['utilities', 'Utilities'],
  ['permits_and_inspections', 'Permits and inspections'],
  ['lawn_snow_security', 'Lawn, snow, and security'],
  ['other_costs', 'Other holding costs'],
];

const FIXED_COST_OPTIONS = [
  ['cameras', 'Cameras'],
  ['smart_locks', 'Smart locks'],
  ['internet', 'Internet'],
  ['security_system', 'Security system'],
  ['database_management', 'Database management'],
  ['other_costs', 'Other fixed costs'],
];

// Starting values for a user who has never saved cash-flow defaults; the
// backend payload and the database column defaults use the same numbers.
export const CASH_FLOW_DEFAULT_VALUES = {
  refi_ltv_pct: 75, interest_rate_pct: 7.5, loan_term_months: 360,
  vacancy_pct: 5, maintenance_pct: 0, capex_pct: 5,
  // $75/mo is a conservative typical broadband price; owner pays Wi-Fi at every property.
  virtual_assistant_pct: 5, monthly_internet: 75,
};

// Percent-of-rent reserves, then fixed operating costs: [key, label, source, months per entry].
const CASH_FLOW_RESERVE_ITEMS = [
  ['vacancy_pct', 'Vacancy'],
  ['maintenance_pct', 'Maintenance'],
  ['capex_pct', 'CapEx'],
];
// Management is an operating expense charged as a percent of rent, not a reserve.
const CASH_FLOW_OPERATING_PCT_ITEMS = [
  ['virtual_assistant_pct', 'Virtual assistant'],
];
const CASH_FLOW_FIXED_ITEMS = [
  ['annual_property_taxes', 'Property taxes', 'assumptions', 12],
  ['annual_insurance', 'Insurance', 'assumptions', 12],
  ['monthly_hoa', 'HOA', 'assumptions', 1],
  ['monthly_owner_utilities', 'Owner-paid utilities', 'assumptions', 1],
  ['monthly_lawn_snow', 'Lawn and snow', 'assumptions', 1],
  ['monthly_security', 'Security monitoring', 'assumptions', 1],
  ['monthly_other', 'Other monthly', 'assumptions', 1],
  ['monthly_internet', 'Internet', 'defaults', 1],
];

// Loan term is stored in months but can be typed as years or months.
export const loanTermFromYears = (years) => {
  const value = Number(years);
  return Number.isFinite(value) && value > 0 ? Math.round(value * 12) : 0;
};
export const loanTermYears = (months) => {
  const value = Number(months);
  return Number.isFinite(value) && value > 0 ? Math.round((value / 12) * 100) / 100 : 0;
};

// Shared defaults that are operating expenses; shown first in the operating section.
const CASH_FLOW_SHARED_OPERATING_KEYS = ['virtual_assistant_pct', 'monthly_internet'];

const CASH_FLOW_DEFAULT_OPTIONS = [
  ['refi_ltv_pct', 'Refinance LTV', 'percent'],
  ['interest_rate_pct', 'Interest rate', 'percent'],
  ['loan_term_months', 'Loan term', 'term'],
  ['vacancy_pct', 'Vacancy reserve', 'percent'],
  ['maintenance_pct', 'Maintenance reserve', 'percent'],
  ['capex_pct', 'Capital expenditure reserve', 'percent'],
  ['virtual_assistant_pct', 'Virtual assistant management', 'percent'],
  ['monthly_internet', 'Internet (monthly)', 'currency'],
];

const CASH_FLOW_PROPERTY_OPTIONS = [
  ['annual_property_taxes', 'Annual property taxes'],
  ['annual_insurance', 'Annual insurance'],
  ['monthly_hoa', 'Monthly HOA'],
  ['monthly_owner_utilities', 'Owner-paid utilities'],
  ['monthly_lawn_snow', 'Lawn and snow'],
  ['monthly_security', 'Security monitoring'],
  ['monthly_other', 'Other monthly expenses'],
];

export const maxRehabPerSqft = (allowance, squareFootage) => {
  const sqft = Number(squareFootage);
  if (!Number.isFinite(allowance) || !Number.isFinite(sqft) || sqft <= 0) return null;
  return allowance / sqft;
};

export const rehabPerSqftTier = (value) => {
  if (!Number.isFinite(value)) return null;
  if (value < 20) return 'tight';
  if (value < 40) return 'moderate';
  if (value < 65) return 'standard';
  return 'high';
};

export const compFailureAriaLabel = (type, failure) => `${type === 'rent' ? 'Rent' : 'ARV'} comps failed: ${failure?.message || 'Request failed.'}`;

export const apiEvidenceRows = (results) => Array.isArray(results)
  ? results.map((result, index) => ({ ...result, evidence_key: result?.id || `legacy-${index}` }))
  : [];

export const groupDatasetEvidencePages = (entries = []) => {
  const groups = new Map();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const parameters = entry.request_parameters || {};
    const identityParameters = Object.fromEntries(Object.entries(parameters).filter(([key]) => key !== 'page'));
    const key = [entry.comparison_batch_id || entry.request_id, entry.provider_key, entry.operation_key, entry.endpoint, JSON.stringify(identityParameters)].join('|');
    if (!groups.has(key)) groups.set(key, { ...entry, group_key: key, pages: [] });
    groups.get(key).pages.push(entry);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    pages: group.pages.sort((a, b) => Number(a.request_parameters?.page || 1) - Number(b.request_parameters?.page || 1)),
  }));
};

export const evidenceOperationTitle = (operationKey, parameters = {}) => {
  const status = String(parameters.searchType || parameters.listingStatus || '').toLowerCase();
  if (operationKey === 'sold_comp_search' || status === 'sold') return 'Sold – GET';
  if (operationKey === 'rental_comp_search' || status === 'for_rent') return 'Rent – GET';
  return 'For Sale – GET';
};

// The call that discovered the lead leads the evidence timeline, then every
// other call newest-first. A market-wide comp pull is frequently newer than
// the listing search that produced the lead, so chronology alone would bury
// the originating request.
export const sortEvidenceTimeline = (entries) => [...(entries || [])].sort((a, b) => {
  if (a.pinnedFirst !== b.pinnedFirst) return a.pinnedFirst ? -1 : 1;
  return new Date(b.requestedAt || 0) - new Date(a.requestedAt || 0);
});

const scenarioLabel = (scenario) => `${scenario?.bedrooms ?? '—'}×${scenario?.bathrooms ?? '—'}`;
const layoutKeyPart = (value) => value == null || value === '' ? 'any' : String(Number(value));
const compactMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  if (Math.abs(amount) >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${Math.round(amount).toLocaleString('en-US')}`;
};
const moneyRange = (scenario, compact = false) => {
  if (!scenario) return '—';
  const format = compact ? compactMoney : money;
  return Number(scenario.low) === Number(scenario.high) ? format(scenario.low) : `${format(scenario.low)}–${format(scenario.high)}`;
};
export const percentRange = (range) => range
  ? `${(range.lowRentLowArv * 100).toFixed(2)}%–${(range.highRentHighArv * 100).toFixed(2)}%`
  : '—';

export default function RentCastApiLab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [providerForms, setProviderForms] = useState(initialProviderForms);
  const [selectedProviders, setSelectedProviders] = useState({ rentcast: false, zillapi: false, realtyapi_realtor: false, realtyapi_zillow: false });
  const [composerProvider, setComposerProvider] = useState('realtyapi_zillow');
  const [composerPurpose, setComposerPurpose] = useState('For_Sale');
  const [realtyRequestVariants, setRealtyRequestVariants] = useState({ realtyapi_zillow: [], realtyapi_realtor: [] });
  const [rentCastRequestOrder, setRentCastRequestOrder] = useState(['rent_comps']);
  const [providerExcludedTargetKeys, setProviderExcludedTargetKeys] = useState({ rentcast: [], zillapi: [] });
  const [previewRevision, setPreviewRevision] = useState(0);
  const [propertyApiDrafts, setPropertyApiDrafts] = useState({});
  const propertyRequestDraftKeyRef = useRef('');
  const [realtyApiSource, setRealtyApiSource] = useState('realtyapi_zillow');
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [chartDays, setChartDaysState] = useState('30');
  const [tableDays, setTableDaysState] = useState('90');
  const [expandedRows, setExpandedRows] = useState(null);
  const [sourceEvidence, setSourceEvidence] = useState({});
  const sourceEvidenceRef = useRef({});
  // Which timeline entries are open, keyed by `${observationId}:${entryKey}`.
  // Only the lead's own listing observations start open.
  const [openEvidenceEntries, setOpenEvidenceEntries] = useState({});
  // Comp payloads average about 1MB, so each one is fetched only when its own
  // timeline entry is expanded rather than with the row.
  const [compRequestPayloads, setCompRequestPayloads] = useState({});
  const compRequestPayloadsRef = useRef({});
  const [evidencePageSelections, setEvidencePageSelections] = useState({});
  const crimeSummaryDetailsRef = useRef({});
  const [selectedObservations, setSelectedObservations] = useState([]);
  const [underwritingCases, setUnderwritingCases] = useState({});
  const [underwritingBusy, setUnderwritingBusy] = useState(false);
  const [portfolioProperties, setPortfolioProperties] = useState({});
  const [portfolioLead, setPortfolioLead] = useState(null);
  const [portfolioPurchaseDate, setPortfolioPurchaseDate] = useState('');
  const [portfolioNickname, setPortfolioNickname] = useState('');
  const [portfolioClosed, setPortfolioClosed] = useState(false);
  const [portfolioBusy, setPortfolioBusy] = useState(false);
  const [portfolioError, setPortfolioError] = useState('');
  const [scenarioSelections, setScenarioSelections] = useState({});
  const [soldCompLead, setSoldCompLead] = useState(null);
  const [rentalCompLead, setRentalCompLead] = useState(null);
  const [selectedZillowComps, setSelectedZillowComps] = useState({});
  const [selectedZillowCompRevisions, setSelectedZillowCompRevisions] = useState({});
  const [photoAlbums, setPhotoAlbums] = useState({});
  const photoAlbumRequests = useRef({});
  const [photoIndexes, setPhotoIndexes] = useState({});
  const [photoGallery, setPhotoGallery] = useState(null);
  const [failedPhotoUrls, setFailedPhotoUrls] = useState({});
  const [activeScenarioMenu, setActiveScenarioMenu] = useState(null);
  const [activeCompFailure, setActiveCompFailure] = useState(null);
  const [activeRentArv, setActiveRentArv] = useState(null);
  const [activeMaxRehab, setActiveMaxRehab] = useState(null);
  const [activeRehabPerSqft, setActiveRehabPerSqft] = useState(null);
  const [holdingCostsRow, setHoldingCostsRow] = useState(null);
  const [holdingCostsDraft, setHoldingCostsDraft] = useState({});
  const [holdingCostsSaving, setHoldingCostsSaving] = useState(false);
  const [holdingCostsError, setHoldingCostsError] = useState('');
  const [fixedCostsOpen, setFixedCostsOpen] = useState(false);
  const [fixedCostsDraft, setFixedCostsDraft] = useState({});
  const [fixedCostsSaving, setFixedCostsSaving] = useState(false);
  const [fixedCostsError, setFixedCostsError] = useState('');
  const [cashFlowRow, setCashFlowRow] = useState(null);
  const [cashFlowDefaultsDraft, setCashFlowDefaultsDraft] = useState({});
  const [cashFlowPropertyDraft, setCashFlowPropertyDraft] = useState({});
  const [cashFlowSaving, setCashFlowSaving] = useState(false);
  const [cashFlowError, setCashFlowError] = useState('');
  const [activeCashFlow, setActiveCashFlow] = useState(null);
  const [activeCrimeSummary, setActiveCrimeSummary] = useState(null);
  const [crimePromptCopied, setCrimePromptCopied] = useState(false);
  const [chartCollapsed, setChartCollapsed] = useState(false);
  const [chartView, setChartView] = useState('usage');
  const [regionChartMarketId, setRegionChartMarketId] = useState('all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const previewRequestIdRef = useRef(0);
  const [tableSearch, setTableSearch] = useState('');
  const [opportunityFilters, setOpportunityFilters] = useState(EMPTY_DASHBOARD_FILTERS);
  const [tableRows, setTableRows] = useState(25);
  const [tableFirst, setTableFirst] = useState(0);
  const [tableScope, setTableScope] = useState('all');
  const [reviewScope, setReviewScope] = useState('active');
  const [reviewBusyLeadId, setReviewBusyLeadId] = useState(null);
  const [passReviewRow, setPassReviewRow] = useState(null);
  const [passReviewNotes, setPassReviewNotes] = useState('');
  const [reviewSaveError, setReviewSaveError] = useState('');
  const [hoveredPassReview, setHoveredPassReview] = useState(null);
  const [hoverReviewEditing, setHoverReviewEditing] = useState(false);
  const [hoverReviewDraft, setHoverReviewDraft] = useState('');
  // Interested and passed share one note-capture and tooltip treatment. The status
  // being captured decides the wording, colour, and whether a note is required.
  const [reviewNoteStatus, setReviewNoteStatus] = useState('passed');
  const [optionalFiltersOpen, setOptionalFiltersOpen] = useState(false);
  const [realtyOptionsOpen, setRealtyOptionsOpen] = useState(false);
  const [realtyOptionsStatus, setRealtyOptionsStatus] = useState('For_Sale');
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [overageDialogOpen, setOverageDialogOpen] = useState(false);
  const [providerProtectionDialogOpen, setProviderProtectionDialogOpen] = useState(false);
  // RealtyAPI is the primary discovery provider, so its usage shows first on load.
  const [creditProviderKey, setCreditProviderKey] = useState('realtyapi_realtor');
  const [providerReserveDraft, setProviderReserveDraft] = useState(2);
  const [providerPaidDraft, setProviderPaidDraft] = useState(false);
  const [planDraft, setPlanDraft] = useState('developer');
  const [usageDraft, setUsageDraft] = useState(0);
  const [renewalDraft, setRenewalDraft] = useState('2026-08-24');
  const [overageLimitDraft, setOverageLimitDraft] = useState(0);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestTargetMode, setRequestTargetMode] = useState('region');
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [runInProgress, setRunInProgress] = useState(false);
  const [continuations, setContinuations] = useState([]);
  const [continuationPreview, setContinuationPreview] = useState(null);
  const [continuationConfirmOpen, setContinuationConfirmOpen] = useState(false);
  const [continuationPageCounts, setContinuationPageCounts] = useState({});
  const [researchPayload, setResearchPayload] = useState(getCachedResearchRegionsPayload());
  const [researchLoading, setResearchLoading] = useState(!getCachedResearchRegionsPayload());
  const [researchError, setResearchError] = useState('');
  const [schedulePanelOpen, setSchedulePanelOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [scheduleDraft, setScheduleDraft] = useState({ name: '', weeks: 1 });
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const bedroomSpaceInfo = useRef(null);
  const rentArvInfo = useRef(null);
  const maxRehabInfo = useRef(null);
  const cashFlowInfo = useRef(null);
  const rehabPerSqftInfo = useRef(null);
  const lowHeadacheInfo = useRef(null);
  const crimeInfo = useRef(null);
  const scenarioMenu = useRef(null);
  const compFailureInfo = useRef(null);
  const reviewToast = useRef(null);
  // Messages already announced by a more specific toast (e.g. a failed paid run).
  const announcedErrorRef = useRef('');
  const showErrorToast = (title, message, options = {}) => {
    reviewToast.current?.show({
      severity: 'error',
      life: 12000,
      ...options,
      content: () => <div className="rcl-review-toast__content rcl-api-completion-toast__content rcl-api-failure-toast__content" role="alert">
        <i className="pi pi-exclamation-triangle" aria-hidden="true" />
        <div><strong>{title}</strong><span>{message}</span></div>
      </div>,
    });
  };
  const passTooltipHideTimer = useRef(null);
  const crimeTooltipHideTimer = useRef(null);
  const historyCache = useRef(new Map());
  const loadSequence = useRef(0);

  useEffect(() => () => {
    if (passTooltipHideTimer.current) clearTimeout(passTooltipHideTimer.current);
  }, []);

  const cacheHistoryPayload = (payload, loadedChartDays, loadedTableDays) => {
    historyCache.current.set(`${loadedTableDays}:${loadedChartDays}`, payload);
    const loadedCount = loadedChartDays === 'all' ? Infinity : Number(loadedChartDays);
    rangeOptions.forEach(({ value: chartRange }) => {
      const rangeCount = chartRange === 'all' ? Infinity : Number(chartRange);
      if (rangeCount <= loadedCount) historyCache.current.set(`${loadedTableDays}:${chartRange}`, filterHistoryPayloadByDays(payload, chartRange));
    });
    const loadedTableCount = loadedTableDays === 'all' ? Infinity : Number(loadedTableDays);
    rangeOptions.forEach(({ value: tableRange }) => {
      const tableRangeCount = tableRange === 'all' ? Infinity : Number(tableRange);
      if (tableRangeCount > loadedTableCount || tableRange === loadedTableDays) return;
      const tablePayload = filterTablePayloadByDays(payload, tableRange);
      rangeOptions.forEach(({ value: chartRange }) => {
        const chartRangeCount = chartRange === 'all' ? Infinity : Number(chartRange);
        if (chartRangeCount <= loadedCount) historyCache.current.set(`${tableRange}:${chartRange}`, filterHistoryPayloadByDays(tablePayload, chartRange));
      });
    });
  };
  const load = async (loadedChartDays = chartDays, loadedTableDays = tableDays) => {
    const sequence = ++loadSequence.current;
    const params = new URLSearchParams({ chart_days: loadedChartDays, table_days: loadedTableDays });
    const payload = await apiJson(`/api-lab?${params}`);
    cacheHistoryPayload(payload, loadedChartDays, loadedTableDays);
    if (sequence === loadSequence.current) {
      const membersByLayout = {};
      const revisionsByLayout = {};
      (payload.observations || []).forEach((row) => {
        [['rent', 'zillow_rent'], ['sold', 'zillow_arv']].forEach(([evidenceType, scenarioType]) => {
          (row.selected_zillow_comp_sets?.[evidenceType] || []).forEach((selection) => {
            const key = `${row.lead_id}:${scenarioType}:${layoutKeyPart(selection.bedrooms)}:${layoutKeyPart(selection.bathrooms)}`;
            membersByLayout[key] = selection.members || [];
            revisionsByLayout[key] = Number(selection.revision || 0);
          });
        });
      });
      setSelectedZillowComps(membersByLayout);
      setSelectedZillowCompRevisions(revisionsByLayout);
      setData(payload);
    }
    return payload;
  };
  const refreshPropertyApiStatus = async (focusProviderKey) => {
    if (focusProviderKey) setCreditProviderKey(focusProviderKey);
    try {
      const status = await apiJson('/property-api/status');
      if (status?.providers) {
        setData((current) => current ? { ...current, property_api_status: status } : current);
      }
    } catch (_statusError) {
      // The full dashboard reload below already carries the same canonical status.
    }
  };

  useEffect(() => { load(chartDays, tableDays).catch((err) => setError(err.message)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadSchedules = async () => {
    const payload = await apiJson('/property-api/schedules');
    setSchedules((payload?.schedules || []).filter((item) => item.status !== 'disabled'));
  };
  useEffect(() => {
    apiJson('/underwriting-cases')
      .then((cases) => setUnderwritingCases(Object.fromEntries((cases || []).flatMap((item) => [
        [item.observation_id, item.id], [item.lead_id, item.id],
      ]))))
      .catch(() => {});
  }, []);
  useEffect(() => {
    apiJson('/property-promotions')
      .then((items) => setPortfolioProperties(Object.fromEntries((items || []).map((item) => [item.lead_id, item.property_id]))))
      .catch(() => {});
  }, []);
  const [providerLogosReady, setProviderLogosReady] = useState(false);
  useEffect(() => { loadProviderLogoImages(() => setProviderLogosReady(true)); }, []);
  useEffect(() => {
    let active = true;
    if (!researchPayload) {
      loadResearchRegionsPayload(apiJson)
        .then((payload) => active && setResearchPayload(payload))
        .catch((err) => active && setResearchError(err.message || 'Could not load U.S. market research.'))
        .finally(() => active && setResearchLoading(false));
    }
    return () => { active = false; };
  }, [researchPayload]);

  const changeChartDays = async (nextDays) => {
    setChartDaysState(nextDays);
    const cached = historyCache.current.get(`${tableDays}:${nextDays}`);
    if (cached) { setData(cached); return; }
    try { await load(nextDays, tableDays); }
    catch (err) { setError(err.message); }
  };
  const setChartDays = changeChartDays;
  const changeTableDays = async (nextDays) => {
    setTableDaysState(nextDays);
    resetTablePaging();
    const cached = historyCache.current.get(`${nextDays}:${chartDays}`);
    if (cached) { setData(cached); return; }
    try { await load(chartDays, nextDays); }
    catch (err) { setError(err.message); }
  };

  const update = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === 'market_ids' && next.request_cap < value.length) next.request_cap = value.length;
      return next;
    });
  };

  const updateProviderForm = (providerKey, name, value) => {
    setProviderForms((current) => ({
      ...current,
      [providerKey]: { ...current[providerKey], [name]: value },
    }));
  };
  const updateProviderSearch = (providerKey, statusKey, patch) => {
    setProviderForms((current) => {
      const form = current[providerKey];
      const searches = form.searches || defaultSearches();
      return {
        ...current,
        [providerKey]: {
          ...form,
          searches: {
            ...searches,
            [statusKey]: { ...searches[statusKey], ...patch },
          },
        },
      };
    });
  };
  // Options belong to one status, because the provider scopes many of them to a
  // single listing status and the three searches are independent requests.
  const updateRealtyAdvancedOption = (name, value) => {
    setProviderForms((current) => {
      const formState = current[realtyApiSource];
      const searches = formState.searches || defaultSearches();
      const search = searches[realtyOptionsStatus] || { advanced_parameters: {} };
      return {
        ...current,
        [realtyApiSource]: {
          ...formState,
          searches: {
            ...searches,
            [realtyOptionsStatus]: {
              ...search,
              advanced_parameters: { ...(search.advanced_parameters || {}), [name]: value },
            },
          },
        },
      };
    });
  };
  const selectedSearchTargets = [
    ...form.market_ids.map((id) => ({ type: 'research_market', id })),
    ...form.county_fips.map((fips) => ({ type: 'fema_county', fips })),
    ...(form.zcta_targets || []),
    ...(form.sourcing_zone_targets || []),
  ];
  const selectedPropertyTargets = selectedObservations.map((row) => ({ type: 'lead_observation', id: row.observation_id }));
  const activeRequestTargets = requestTargetMode === 'property' ? selectedPropertyTargets : selectedSearchTargets;
  const updateSelectedSearchTargets = (targets) => {
    setPreview(null);
    setForm((current) => {
      const marketIds = targets.filter((target) => target.type === 'research_market').map((target) => target.id);
      const countyFips = targets.filter((target) => target.type === 'fema_county').map((target) => target.fips);
      const zctaTargets = targets.filter((target) => target.type === 'zcta');
      const sourcingZoneTargets = targets.filter((target) => target.type === 'sourcing_zone');
      return {
        ...current,
        market_ids: marketIds,
        county_fips: countyFips,
        zcta_targets: zctaTargets,
        sourcing_zone_targets: sourcingZoneTargets,
        request_cap: Math.max(current.request_cap, targets.length),
      };
    });
  };
  const runFromMapTooltip = (target) => {
    const targetKey = propertySearchTargetKey(target);
    const alreadySelected = selectedSearchTargets.some((item) => propertySearchTargetKey(item) === targetKey);
    if (!alreadySelected) updateSelectedSearchTargets([...selectedSearchTargets, target]);
    setPreview(null);
    setRequestTargetMode('region');
    setComposerProvider('realtyapi_zillow');
    setComposerPurpose('For_Sale');
    setRealtyApiSource('realtyapi_zillow');
    setRealtyRequestVariants({ realtyapi_zillow: [], realtyapi_realtor: [] });
    setRentCastRequestOrder(['rent_comps']);
    setProviderExcludedTargetKeys({ rentcast: [], zillapi: [] });
    setSelectedProviders({ rentcast: false, zillapi: false, realtyapi_realtor: false, realtyapi_zillow: false });
    setRequestDialogOpen(true);
  };
  const deselectAllSearchTargets = () => {
    updateSelectedSearchTargets([]);
    if (continuations.length === 0) setRequestDialogOpen(false);
  };
  const deselectSearchRegion = (region) => {
    const regionKey = propertySearchTargetKey(region.target || region);
    const remaining = selectedSearchTargets.filter((target) => propertySearchTargetKey(target) !== regionKey);
    updateSelectedSearchTargets(remaining);
    if (remaining.length === 0 && continuations.length === 0) setRequestDialogOpen(false);
  };

  // Mirrors deselectSearchRegion: removing a chip also unselects the table row,
  // invalidates the previous preview, and closes the builder when none remain.
  const deselectPropertyTarget = (row) => {
    const remaining = selectedObservations.filter((item) => item.observation_id !== row.observation_id);
    setSelectedObservations(remaining);
    setPropertyApiDrafts((current) => {
      const { [row.observation_id]: _removed, ...rest } = current;
      return rest;
    });
    setPreview(null);
    if (remaining.length === 0) setRequestDialogOpen(false);
  };

  const toggleRequestProvider = (providerKey) => {
    setPreview(null);
    setSelectedProviders((current) => ({ ...current, [providerKey]: !current[providerKey] }));
  };

  const composerProviderOptions = requestTargetMode === 'property'
    ? [
      { label: 'RentCast', value: 'rentcast' },
      { label: 'RealtyAPI · Zillow', value: 'realtyapi_zillow' },
    ]
    : [
      { label: 'RentCast', value: 'rentcast' },
      { label: 'ZillAPI', value: 'zillapi' },
      { label: 'RealtyAPI · Zillow', value: 'realtyapi_zillow' },
      { label: 'RealtyAPI · Realtor', value: 'realtyapi_realtor' },
    ];
  const composerPurposeOptions = (() => {
    if (composerProvider === 'rentcast' && requestTargetMode === 'property') {
      return rentCastEndpointChoices.map((option) => ({ label: option.label, value: option.value }));
    }
    if (composerProvider === 'realtyapi_zillow' && requestTargetMode === 'property') {
      return [
        ...['search_byaddress', 'search_bycoordinates'].flatMap((endpoint) => SEARCH_STATUSES.map((status) => ({
          label: `GET /${endpoint.replace('_', '/')} · ${status.label}`,
          value: `${endpoint}|${status.key}`,
        }))),
        { label: 'GET /taxinfo', value: 'taxinfo|property_tax' },
      ];
    }
    if (composerProvider === 'realtyapi_zillow' || composerProvider === 'realtyapi_realtor') {
      const endpointLabel = composerProvider === 'realtyapi_realtor' ? 'GET /search/bycoordinates' : 'GET /search/byaddress';
      return SEARCH_STATUSES.map((status) => ({ label: `${endpointLabel} · ${status.label}`, value: status.key }));
    }
    return [{ label: composerProvider === 'zillapi' ? 'POST /v1/listings/for-sale' : 'GET /v1/listings/sale', value: 'For_Sale' }];
  })();
  const composerEndpointValue = composerProvider === 'realtyapi_zillow' && requestTargetMode === 'property'
    ? `${composerPurpose === 'property_tax' ? 'taxinfo' : (providerForms.realtyapi_zillow.zillow_endpoint === 'search_bycoordinates' ? 'search_bycoordinates' : 'search_byaddress')}|${composerPurpose}`
    : composerPurpose;
  const selectComposerEndpoint = (value) => {
    if (composerProvider === 'realtyapi_zillow' && requestTargetMode === 'property') {
      const [endpoint, purpose] = String(value).split('|');
      updateProviderForm('realtyapi_zillow', 'zillow_endpoint', endpoint);
      setComposerPurpose(purpose);
      return;
    }
    setComposerPurpose(value);
  };
  const composerEndpointDescription = (() => {
    if (composerProvider === 'rentcast' && requestTargetMode === 'region') return 'Search active sale listings around each selected region.';
    if (composerProvider === 'rentcast') return {
      rent_comps: 'Estimate market rent from nearby long-term rental comparables.',
      arv_comps: 'Estimate property value from nearby sale comparables.',
      property_tax: 'Retrieve the property record and available tax details.',
    }[composerPurpose];
    if (composerProvider === 'zillapi') return 'Search Zillow for-sale listings inside each selected region.';
    if (composerPurpose === 'property_tax') return 'Retrieve Zillow property and tax information for the selected address.';
    return {
      For_Sale: 'Search active for-sale listings.',
      Sold: 'Search sold listings for comparable-sale evidence.',
      For_Rent: 'Search active rentals for comparable-rent evidence.',
    }[composerPurpose];
  })();
  const selectComposerProvider = (providerKey) => {
    setComposerProvider(providerKey);
    if (providerKey.startsWith('realtyapi_')) setRealtyApiSource(providerKey);
    if (providerKey === 'rentcast' && requestTargetMode === 'property') setComposerPurpose('rent_comps');
    else {
      if (providerKey === 'realtyapi_zillow' && requestTargetMode === 'property') updateProviderForm('realtyapi_zillow', 'zillow_endpoint', 'search_byaddress');
      setComposerPurpose('For_Sale');
    }
  };
  const addComposerRequest = () => {
    const operationKey = {
      rent_comps: 'long_term_rent_estimate', arv_comps: 'property_value_estimate', property_tax: 'property_tax_lookup',
      For_Sale: 'sale_listing_search', Sold: 'sold_comp_search', For_Rent: 'rental_comp_search',
    }[composerPurpose] || 'sale_listing_search';
    const optimisticEndpoint = composerProvider === 'rentcast'
      ? (rentCastPropertyEndpoints.find((option) => option.key === composerPurpose)?.endpoint || 'https://api.rentcast.io/v1/listings/sale')
      : composerProvider === 'zillapi'
        ? 'https://api.zillapi.com/v1/listings/for-sale'
        : composerPurpose === 'property_tax'
          ? 'https://zillow.realtyapi.io/taxinfo'
          : providerForms.realtyapi_zillow.zillow_endpoint === 'search_bycoordinates' || composerProvider === 'realtyapi_realtor'
            ? `https://${composerProvider === 'realtyapi_realtor' ? 'realtor' : 'zillow'}.realtyapi.io/search/bycoordinates`
            : 'https://zillow.realtyapi.io/search/byaddress';
    const optimisticTargets = requestTargetMode === 'property' ? selectedObservations : selectedRegions;
    const optimisticRequests = optimisticTargets.map((entry) => {
      const target = entry.target || entry;
      const name = entry.formatted_address || entry.name || entry.display_label || propertySearchTargetLabel(target);
      const parameters = requestTargetMode === 'property'
        ? composerPurpose === 'property_tax'
          ? (composerProvider === 'rentcast' ? { address: name } : { byaddress: name })
          : composerProvider === 'rentcast'
            ? { address: name }
            : providerForms.realtyapi_zillow.zillow_endpoint === 'search_bycoordinates'
              ? { latitude: String(entry.latitude ?? ''), longitude: String(entry.longitude ?? ''), radius: String(providerForms.realtyapi_zillow.radius), page: '1', listingStatus: composerPurpose }
              : { location: String(entry.zip_code || name), page: '1', listingStatus: composerPurpose }
        : composerProvider === 'rentcast'
          ? { latitude: entry.latitude, longitude: entry.longitude, radius: form.radius, status: 'Active', price: form.price, limit: form.limit, offset: 0, includeTotalCount: 'true' }
          : composerProvider === 'zillapi'
            ? { filters: { status: 'for_sale' }, maxItems: providerForms.zillapi.result_limit, async: false }
            : composerProvider === 'realtyapi_realtor'
              ? { latitude: entry.latitude, longitude: entry.longitude, radius: providerForms.realtyapi_realtor.radius, page: 1, resultCount: 200, searchType: composerPurpose }
              : { location: name, page: '1', listingStatus: composerPurpose };
      const targetKey = requestTargetMode === 'property'
        ? `observation:${entry.observation_id}`
        : propertySearchTargetKey(target);
      return { market: { id: entry.observation_id || target.id || target.fips || name, name, target_key: targetKey }, method: composerProvider === 'zillapi' ? 'POST' : 'GET', endpoint: optimisticEndpoint, parameters, operation_key: operationKey };
    });
    setPreview((current) => {
      const providers = [...(current?.providers || [])];
      const index = providers.findIndex((item) => item.provider_key === composerProvider);
      const previousRequests = index >= 0 ? providers[index].requests || [] : [];
      const keepPrevious = composerProvider === 'zillapi'
        ? []
        : previousRequests.filter((item) => item.operation_key !== operationKey);
      const providerPreview = {
        provider_key: composerProvider, billing_unit_type: composerProvider === 'zillapi' ? 'result' : 'request',
        maximum_http_requests: optimisticRequests.length, maximum_provider_units: optimisticRequests.length,
        requests: [...optimisticRequests, ...keepPrevious], skipped_requests: [],
      };
      if (index >= 0) providers[index] = providerPreview;
      else providers.push(providerPreview);
      const selectedIndex = providers.findIndex((item) => item.provider_key === composerProvider);
      if (selectedIndex > 0) providers.unshift(...providers.splice(selectedIndex, 1));
      return { provider_count: providers.length, selected_region_count: activeRequestTargets.length, providers, execution_token: null };
    });
    if (composerProvider === 'rentcast') {
      setProviderExcludedTargetKeys((current) => ({ ...current, rentcast: [] }));
      setSelectedProviders((current) => ({ ...current, rentcast: true }));
      if (requestTargetMode === 'property') {
        const selection = rentCastEndpointChoices.find((option) => option.value === composerPurpose) || rentCastEndpointChoices[0];
        selectedObservations.forEach((row) => updatePropertyApiDraft(row.observation_id, {
          rentcast_endpoint: selection.value,
          ...Object.fromEntries(selection.keys.map((key) => [key, true])),
        }));
        setRentCastRequestOrder((current) => [...selection.keys, ...current.filter((key) => !selection.keys.includes(key))]);
      }
      setPreviewRevision((current) => current + 1);
      return;
    }
    if (composerProvider === 'zillapi') {
      setProviderExcludedTargetKeys((current) => ({ ...current, zillapi: [] }));
      setSelectedProviders((current) => ({ ...current, zillapi: true }));
      setPreviewRevision((current) => current + 1);
      return;
    }
    setRealtyApiSource(composerProvider);
    setSelectedProviders((current) => ({ ...current, [composerProvider]: true }));
    if (composerProvider.startsWith('realtyapi_')) {
      const selectedSearch = providerForms[composerProvider].searches?.[composerPurpose] || defaultSearches()[composerPurpose];
      const requestVariant = composerPurpose === 'property_tax'
        ? { zillow_endpoint: 'taxinfo' }
        : {
            ...(composerProvider === 'realtyapi_zillow' ? { zillow_endpoint: providerForms.realtyapi_zillow.zillow_endpoint } : {}),
            searches: Object.fromEntries(Object.entries(defaultSearches()).map(([key, value]) => [key, {
              ...value,
              ...(key === composerPurpose ? selectedSearch : {}),
              enabled: key === composerPurpose,
            }])),
          };
      setRealtyRequestVariants((current) => ({
        ...current,
        [composerProvider]: [
          { ...requestVariant, operation_key: operationKey, excluded_target_keys: [] },
          ...(current[composerProvider] || []).filter((variant) => variant.operation_key !== operationKey),
        ],
      }));
    }
    if (composerProvider === 'realtyapi_zillow' && composerPurpose === 'property_tax') {
      updateProviderForm('realtyapi_zillow', 'zillow_endpoint', 'taxinfo');
      setPreviewRevision((current) => current + 1);
      return;
    }
    if (composerProvider === 'realtyapi_zillow' && providerForms.realtyapi_zillow.zillow_endpoint === 'taxinfo') {
      updateProviderForm('realtyapi_zillow', 'zillow_endpoint', 'search_byaddress');
    }
    updateProviderSearch(composerProvider, composerPurpose, { enabled: true });
    setPreviewRevision((current) => current + 1);
  };
  const removeComposerRequest = (providerKey, purpose, selectedRequest) => {
    previewRequestIdRef.current += 1;
    const operationKey = {
      rent_comps: 'long_term_rent_estimate', arv_comps: 'property_value_estimate', property_tax: 'property_tax_lookup',
      For_Sale: 'sale_listing_search', Sold: 'sold_comp_search', For_Rent: 'rental_comp_search',
    }[purpose] || 'sale_listing_search';
    setPreview((current) => {
      if (!current) return current;
      const selectedTargetKey = selectedRequest?.market?.target_key || (selectedRequest?.market?.id ? `observation:${selectedRequest.market.id}` : '');
      const removeProvider = false;
      const providers = removeProvider
        ? current.providers.filter((item) => item.provider_key !== providerKey)
        : current.providers.map((item) => item.provider_key === providerKey
          ? { ...item, requests: (item.requests || []).filter((request) => {
              const requestTargetKey = request.market?.target_key || (request.market?.id ? `observation:${request.market.id}` : '');
              return !(request.operation_key === operationKey && request.endpoint === selectedRequest?.endpoint && requestTargetKey === selectedTargetKey);
            }) }
          : item).filter((item) => (item.requests || []).length || (item.skipped_requests || []).length);
      return providers.length ? { ...current, providers, provider_count: providers.length, execution_token: null } : null;
    });
    if (providerKey === 'rentcast' || providerKey === 'zillapi') {
      if (providerKey === 'rentcast' && requestTargetMode === 'property') {
        setPropertyApiDrafts((current) => {
          const selectedObservationId = String(selectedRequest?.market?.target_key || '').replace(/^observation:/, '') || String(selectedRequest?.market?.id || '');
          const next = Object.fromEntries(Object.entries(current).map(([observationId, draft]) => [observationId, observationId === selectedObservationId ? { ...draft, [purpose]: false } : draft]));
          const anotherEnabled = Object.values(next).some((draft) => rentCastPropertyEndpoints.some((option) => draft[option.key]));
          if (!anotherEnabled) setSelectedProviders((providers) => ({ ...providers, rentcast: false }));
          return next;
        });
      } else {
        const targetKey = selectedRequest?.market?.target_key || '';
        setProviderExcludedTargetKeys((current) => {
          const next = [...new Set([...(current[providerKey] || []), targetKey].filter(Boolean))];
          if (next.length >= activeRequestTargets.length) setSelectedProviders((providers) => ({ ...providers, [providerKey]: false }));
          return { ...current, [providerKey]: next };
        });
      }
      setPreviewRevision((current) => current + 1);
      return;
    }
    if (providerKey.startsWith('realtyapi_')) {
      setRealtyRequestVariants((current) => {
        const targetKey = selectedRequest?.market?.target_key || (selectedRequest?.market?.id ? `observation:${selectedRequest.market.id}` : '');
        const activeTargetKeys = new Set(activeRequestTargets.map((target) => propertySearchTargetKey(target)));
        const next = (current[providerKey] || []).flatMap((variant) => {
          if (variant.operation_key !== operationKey) return [variant];
          const excluded = [...new Set([...(variant.excluded_target_keys || []), targetKey].filter(Boolean))];
          return excluded.filter((key) => activeTargetKeys.has(key)).length >= activeTargetKeys.size ? [] : [{ ...variant, excluded_target_keys: excluded }];
        });
        if (!next.length) setSelectedProviders((providers) => ({ ...providers, [providerKey]: false }));
        return { ...current, [providerKey]: next };
      });
      setPreviewRevision((current) => current + 1);
      return;
    }
    updateProviderSearch(providerKey, purpose, { enabled: false });
    const searches = providerForms[providerKey]?.searches || defaultSearches();
    const anotherEnabled = Object.entries(searches).some(([key, value]) => key !== purpose && value.enabled);
    if (!anotherEnabled) setSelectedProviders((current) => ({ ...current, [providerKey]: false }));
    setPreviewRevision((current) => current + 1);
  };
  const previewPurpose = (operationKey) => ({
    long_term_rent_estimate: 'rent_comps',
    property_value_estimate: 'arv_comps',
    property_tax_lookup: 'property_tax',
    sale_listing_search: 'For_Sale',
    listing_search: 'For_Sale',
    sold_comp_search: 'Sold',
    rental_comp_search: 'For_Rent',
  }[operationKey] || 'For_Sale');

  const updatePropertyApiDraft = (observationId, changes) => {
    setPropertyApiDrafts((current) => ({
      ...current,
      [observationId]: { ...current[observationId], ...changes },
    }));
  };

  const openOptionalFilters = () => {
    setRequestDialogOpen(false);
    setOptionalFiltersOpen(true);
  };

  const closeOptionalFilters = () => {
    setOptionalFiltersOpen(false);
    if (selectedSearchTargets.length > 0) setRequestDialogOpen(true);
  };

  const previewRequest = async () => {
    const requestId = ++previewRequestIdRef.current;
    setBusy(true); setError('');
    const providers = {
      rentcast: {
        enabled: selectedProviders.rentcast,
        excluded_target_keys: providerExcludedTargetKeys.rentcast,
        radius: form.radius, price: form.price, property_type: 'single_family',
        result_limit: form.limit, request_cap: form.request_cap,
        bedrooms: form.bedrooms, bathrooms: form.bathrooms,
        square_footage: form.square_footage, year_built: form.year_built,
        days_old: form.days_old,
      },
      zillapi: { enabled: selectedProviders.zillapi, ...providerForms.zillapi, excluded_target_keys: providerExcludedTargetKeys.zillapi },
      realtyapi_zillow: { enabled: selectedProviders.realtyapi_zillow, ...realtyApiProviderConfig('realtyapi_zillow') },
      realtyapi_realtor: { enabled: selectedProviders.realtyapi_realtor, ...realtyApiProviderConfig('realtyapi_realtor') },
    };
    if (requestTargetMode === 'property') providers.zillapi.enabled = false;
    try {
      if (requestTargetMode !== 'property') {
        const nextPreview = await apiJson('/property-api/discovery/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targets: activeRequestTargets, providers }) });
        if (requestId === previewRequestIdRef.current) setPreview({ ...nextPreview, providers: [...(nextPreview.providers || [])].sort((left) => left.provider_key === composerProvider ? -1 : 0) });
        return;
      }
      const rentCastRequests = selectedProviders.rentcast
        ? selectedObservations.flatMap((row) => {
          const draft = propertyApiDrafts[row.observation_id] || {};
          const parameters = {
            address: row.formatted_address,
            propertyType: draft.property_type,
            bedrooms: draft.bedrooms,
            bathrooms: draft.bathrooms,
            squareFootage: draft.square_footage,
            lookupSubjectAttributes: true,
          };
          return [...rentCastPropertyEndpoints].sort((left, right) => {
            const leftIndex = rentCastRequestOrder.indexOf(left.key);
            const rightIndex = rentCastRequestOrder.indexOf(right.key);
            return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
          }).filter((option) => draft[option.key]).map((option) => ({
            market: { id: row.observation_id, name: row.formatted_address || 'Address unavailable' },
            method: option.method, endpoint: option.endpoint, parameters: Object.fromEntries(Object.entries(parameters).filter(([, value]) => value !== '' && value != null)), operation_key: option.operation_key,
          }));
        })
        : [];
      if (selectedProviders.rentcast && rentCastRequests.length === 0) throw new Error('Choose at least one RentCast endpoint.');
      const canonical = selectedProviders.realtyapi_zillow || selectedProviders.realtyapi_realtor
        ? await apiJson('/property-api/discovery/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targets: activeRequestTargets, providers: { realtyapi_zillow: providers.realtyapi_zillow, realtyapi_realtor: providers.realtyapi_realtor } }) })
        : { provider_count: 0, providers: [], execution_token: createExecutionToken() };
      const rentCastPreview = rentCastRequests.length ? {
        provider_key: 'rentcast', billing_unit_type: 'request', maximum_http_requests: rentCastRequests.length,
        maximum_provider_units: rentCastRequests.length, requests: rentCastRequests, skipped_requests: [],
      } : null;
      if (requestId !== previewRequestIdRef.current) return;
      setPreview({
        ...canonical,
        provider_count: Number(canonical.provider_count || 0) + (rentCastPreview ? 1 : 0),
        selected_region_count: activeRequestTargets.length,
        providers: [...(rentCastPreview ? [rentCastPreview] : []), ...(canonical.providers || [])].sort((left) => left.provider_key === composerProvider ? -1 : 0),
      });
    }
    catch (err) { if (requestId === previewRequestIdRef.current) setError(err.message); }
    finally { if (requestId === previewRequestIdRef.current) setBusy(false); }
  };

  // The URL preview is part of the composer, not a separate workflow step.
  // Refresh once per explicit batch mutation so typing remains instant and
  // does not repeatedly call the preview route.
  useEffect(() => {
    if (!previewRevision || !requestDialogOpen) return;
    if (!Object.values(selectedProviders).some(Boolean)) {
      setPreview(null);
      return;
    }
    previewRequest();
  }, [previewRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  const runRequest = async () => {
    if (!preview) return previewRequest();
    setConfirmationDialogOpen(true);
  };

  // Every error on this page is also announced as a toast, so a failure is
  // never visible only in a banner the operator may have scrolled past.
  useEffect(() => {
    if (!error) return;
    if (announcedErrorRef.current === error) {
      announcedErrorRef.current = '';
      return;
    }
    showErrorToast('Something went wrong', error);
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (researchError) showErrorToast('Market research did not load', researchError);
  }, [researchError]); // eslint-disable-line react-hooks/exhaustive-deps

  // A paid run can fail after some requests already completed and were saved.
  // Make the failure impossible to miss, then reload so saved work appears.
  const reportPaidRunFailure = async (err) => {
    const message = err?.message || 'The provider run did not finish.';
    const blockedBeforeProvider = err?.data?.code === 'duplicate_request_in_progress'
      && err?.data?.provider_request_started === false;
    announcedErrorRef.current = message;
    setError(message);
    showErrorToast(
      blockedBeforeProvider ? 'Request blocked before provider call' : 'Property API request did not finish',
      blockedBeforeProvider
        ? message
        : `${message} Any requests that completed are saved; the table and history were refreshed.`,
      { sticky: true },
    );
    historyCache.current.clear();
    try {
      await Promise.all([load(), refreshPropertyApiStatus()]);
    } catch {
      // The toast already explains the failure; a refresh error must not hide it.
    }
  };

  const executeConfirmedRequest = async () => {
    setConfirmationDialogOpen(false);
    setRunInProgress(true);
    setBusy(true); setError('');
    let lastExecutedProviderKey = null;
    let keepRequestDialogOpen = false;
    let ranForSaleSearch = false;
    try {
      if (selectedProviders.rentcast) {
        if (requestTargetMode === 'property') {
          const rentCastResult = await apiJson('/api-lab/observations/enrich', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ execution_token: preview.execution_token, items: selectedObservations.map((row) => {
              const draft = propertyApiDrafts[row.observation_id] || {};
              return {
                observation_id: row.observation_id,
                api_keys: rentCastPropertyEndpoints.filter((option) => draft[option.key]).map((option) => option.key),
                bedrooms: draft.bedrooms, bathrooms: draft.bathrooms,
                square_footage: draft.square_footage, property_type: draft.property_type,
              };
            }) }),
          });
          const failed = (rentCastResult.results || []).filter((item) => item.status && item.status !== 'completed');
          if (failed.length) throw new Error(failed[0].error_message || 'RentCast property request did not finish.');
          const taxResults = (rentCastResult.results || []).filter((item) => item.api_key === 'property_tax');
          const taxSaved = taxResults.reduce((total, item) => total + Number(item.normalized_record_count || 0), 0);
          reviewToast.current?.show({
            severity: 'success', life: 9000,
            content: () => <div className="rcl-review-toast__content rcl-api-completion-toast__content"><i className="pi pi-check-circle" aria-hidden="true" /><div><strong>RentCast property request complete</strong><span>{(rentCastResult.results || []).length.toLocaleString()} request{(rentCastResult.results || []).length === 1 ? '' : 's'} completed</span>{taxResults.length > 0 && <span>{taxSaved > 0 ? `Property taxes saved to ${taxSaved} cash-flow record${taxSaved === 1 ? '' : 's'}` : 'The response was preserved, but it did not contain an annual tax amount.'}</span>}</div></div>,
          });
        } else {
          await apiJson('/api-lab/runs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, targets: selectedSearchTargets, excluded_target_keys: providerExcludedTargetKeys.rentcast, execution_token: preview.execution_token, confirm_live_request: true }) });
          ranForSaleSearch = true;
        }
        lastExecutedProviderKey = 'rentcast';
      }
      if (selectedProviders.zillapi || selectedProviders.realtyapi_zillow || selectedProviders.realtyapi_realtor) {
        const result = await apiJson('/property-api/discovery/runs', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targets: activeRequestTargets,
            execution_token: preview.execution_token,
            confirm_live_request: true,
            providers: {
              zillapi: { enabled: selectedProviders.zillapi, ...providerForms.zillapi, excluded_target_keys: providerExcludedTargetKeys.zillapi },
              realtyapi_zillow: { enabled: selectedProviders.realtyapi_zillow, ...realtyApiProviderConfig('realtyapi_zillow') },
              realtyapi_realtor: { enabled: selectedProviders.realtyapi_realtor, ...realtyApiProviderConfig('realtyapi_realtor') },
            },
          }),
        });
        // Results without an operation key are listing searches (the backend default).
        if ((result.results || []).some((item) => (item.operation_key || 'sale_listing_search') === 'sale_listing_search')) ranForSaleSearch = true;
        const completedProviders = (result.results || []).map((item) => item.provider_key).filter(Boolean);
        if (completedProviders.length > 0) lastExecutedProviderKey = completedProviders[completedProviders.length - 1];
        const completion = propertyApiCompletionSummary(result.results);
        const operationLines = Object.entries(completion.byOperation)
          .filter(([operation]) => operation !== 'unknown')
          .map(([operation, counts]) => `${completionOperationLabel[operation] || operation}: ${counts.returned.toLocaleString()} returned · ${counts.saved.toLocaleString()} saved`);
        reviewToast.current?.show({
          severity: 'success',
          life: 9000,
          content: () => <div className="rcl-review-toast__content rcl-api-completion-toast__content">
            <i className="pi pi-check-circle" aria-hidden="true" />
            <div><strong>Property API request complete</strong>{operationLines.map((line) => <span key={line}>{line}</span>)}<span>Total: {completion.returned.toLocaleString()} returned · {completion.saved.toLocaleString()} saved · {completion.additionalPages.toLocaleString()} additional page{completion.additionalPages === 1 ? '' : 's'}</span></div>
          </div>,
        });
        const nextContinuations = (result.results || []).filter((item) => item.provider_key?.startsWith('realtyapi_') && item.pagination_evidence && item.has_next_page);
        // Pagination belongs only to the request the operator just ran in this
        // browser workflow. Historical unresolved decisions remain preserved in
        // evidence, but must never reappear as anonymous cards in a new form.
        setContinuations(nextContinuations);
        keepRequestDialogOpen = nextContinuations.length > 0;
      }
      setRequestDialogOpen(keepRequestDialogOpen);
      setPreview(null);
      // A completed property batch is no longer a draft. The next click on
      // Run APIs must open a clean builder instead of replaying what just ran.
      if (requestTargetMode === 'property') propertyRequestDraftKeyRef.current = '';
      // Only a For Sale pull creates leads, so only it moves the table to
      // Latest pull. Sold/For Rent comp runs keep the operator's current view.
      if (ranForSaleSearch) setTableScope('latest');
      setTableFirst(0);
      historyCache.current.clear();
      await Promise.all([load(), refreshPropertyApiStatus(lastExecutedProviderKey)]);
    } catch (err) { await reportPaidRunFailure(err); }
    finally { setBusy(false); setRunInProgress(false); }
  };

  const previewContinuation = async (item, requestedCount) => {
    setBusy(true); setError('');
    try {
      const remaining = Number(item.pagination_evidence?.estimated_remaining_pages || 1);
      const pageCount = Math.max(1, Math.min(remaining, Number(requestedCount || continuationPageCounts[item.request_id] || 1)));
      const nextPreview = await apiJson(`/property-api/discovery/continuations/${item.request_id}/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ page_count: pageCount }),
      });
      setContinuationPreview(nextPreview);
      setContinuationConfirmOpen(true);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const executeContinuation = async () => {
    if (!continuationPreview) return;
    setContinuationConfirmOpen(false); setRunInProgress(true); setBusy(true); setError('');
    try {
      const result = await apiJson(`/property-api/discovery/continuations/${continuationPreview.parent_request_id}/runs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ execution_token: continuationPreview.execution_token, confirm_live_request: true,
          page_count: continuationPreview.maximum_http_requests }),
      });
      setContinuations((current) => {
        const remaining = current.filter((item) => item.request_id !== continuationPreview.parent_request_id);
        return result.has_next_page ? [...remaining, result] : remaining;
      });
      setContinuationPreview(null);
      if ((result.operation_key || continuationPreview.operation_key || 'sale_listing_search') === 'sale_listing_search') setTableScope('latest');
      setTableFirst(0); historyCache.current.clear();
      await load();
      await refreshPropertyApiStatus(result.provider_key || continuationPreview.provider_key);
    } catch (err) { await reportPaidRunFailure(err); }
    finally { setBusy(false); setRunInProgress(false); }
  };

  const stopContinuation = async (item) => {
    setBusy(true); setError('');
    try {
      await apiJson(`/property-api/discovery/continuations/${item.request_id}/stop`, { method: 'POST' });
      setContinuations((current) => current.filter((entry) => entry.request_id !== item.request_id));
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const openPlanDialog = () => {
    setPlanDraft(data?.subscription?.plan_code || 'developer');
    setUsageDraft(rentCastQuotaUsage);
    setPlanDialogOpen(true);
  };

  const openRenewalDialog = () => {
    setRenewalDraft(data?.subscription?.next_renewal_date || data?.subscription?.renewal_anchor_date || '2026-08-24');
    setRenewalDialogOpen(true);
  };

  const openOverageDialog = () => {
    setOverageLimitDraft(Number(data?.subscription?.overage_limit || 0));
    setOverageDialogOpen(true);
  };

  const saveSubscription = async (changes, close) => {
    setSettingsBusy(true); setError('');
    try {
      await apiJson('/api-lab/subscription', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes) });
      close();
      historyCache.current.clear();
      await load();
    } catch (err) { setError(err.message); }
    finally { setSettingsBusy(false); }
  };

  const openProviderProtectionDialog = (provider) => {
    setProviderReserveDraft(Number(provider?.budget?.minimum_remaining_unit_reserve || 0));
    setProviderPaidDraft(Boolean(provider?.budget?.provider_overage_permitted));
    setProviderProtectionDialogOpen(true);
  };

  const saveProviderProtection = async () => {
    setSettingsBusy(true); setError('');
    try {
      const budgetProviderKey = creditProviderKey === 'realtyapi_realtor'
        ? realtyApiSource
        : creditProviderKey;
      await apiJson(`/property-api/providers/${budgetProviderKey}/budget`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          minimum_remaining_unit_reserve: providerReserveDraft,
          provider_overage_permitted: providerPaidDraft,
        }),
      });
      setProviderProtectionDialogOpen(false);
      historyCache.current.clear();
      await load();
    } catch (err) { setError(err.message); }
    finally { setSettingsBusy(false); }
  };

  const chartData = useMemo(() => {
    const legacy = data?.timeline || [];
    const canonical = data?.provider_timeline || [];
    const referenceDate = data?.history_today ? new Date(`${data.history_today}T12:00:00`) : new Date();
    const days = chartDayKeys(chartDays, [...legacy.map((row) => row.day), ...canonical.map((row) => row.day)], referenceDate);
    const values = (...providerKeys) => canonical
      .filter((row) => providerKeys.includes(row.provider_key))
      .reduce((points, row) => points.set(row.day, (points.get(row.day) || 0) + Number(row.http_requests || 0)), new Map());
    const rentcast = new Map(legacy.map((row) => [row.day, Number(row.requests || 0)]));
    const series = [
      ['RentCast', 'rentcast', rentcast, '#6ee7b7'],
      ['ZillAPI', 'zillapi', values('zillapi'), '#60a5fa'],
      // Realtor and Zillow searches share one RealtyAPI account and credit pool.
      ['RealtyAPI', 'realtyapi_realtor', values('realtyapi_realtor', 'realtyapi_zillow'), '#f59e0b'],
    ];
    return {
      labels: days.map((day) => new Date(`${day}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
      datasets: series.map(([label, providerKey, points, color]) => ({
        label, providerKey, data: days.map((day) => points.get(day) || 0), borderColor: color,
        backgroundColor: color, yAxisID: 'y', tension: 0, borderWidth: 3,
        pointRadius: 3, pointHoverRadius: 5, borderCapStyle: 'butt', borderJoinStyle: 'miter',
      })),
    };
  }, [chartDays, data]);
  const usageChartLegend = useMemo(() => chartData.datasets.map((dataset) => ({
    label: dataset.label, providerKey: dataset.providerKey, color: dataset.borderColor,
  })), [chartData]);
  const chartRequestTotal = useMemo(() => (data?.timeline || []).reduce((total, row) => total + Number(row.requests || 0), 0)
    + (data?.provider_timeline || []).reduce((total, row) => total + Number(row.http_requests || 0), 0), [data?.timeline, data?.provider_timeline]);

  const regionHistory = useMemo(() => data?.region_history || [], [data?.region_history]);
  const regionChartOptions = useMemo(() => {
    const byId = new Map();
    regionHistory.forEach((row) => {
      const regionKey = regionHistoryKey(row);
      if (byId.has(regionKey)) return;
      byId.set(regionKey, row.market_name || 'Selected region');
    });
    return [{ label: 'All regions', value: 'all' }, ...Array.from(byId, ([value, label]) => ({ label, value }))];
  }, [regionHistory]);
  const regionResultsChartData = useMemo(() => {
    const filtered = regionChartMarketId === 'all'
      ? regionHistory
      : regionHistory.filter((row) => regionHistoryKey(row) === String(regionChartMarketId));
    const allMarketIds = Array.from(new Set(regionHistory.map((row) => `${row.provider_key || 'rentcast'}:${regionHistoryKey(row)}`)));
    const usesCalendarDays = chartDays !== 'all';
    const referenceDate = data?.history_today ? new Date(`${data.history_today}T12:00:00`) : new Date();
    const categoryKeys = usesCalendarDays
      ? chartDayKeys(chartDays, [], referenceDate)
      : Array.from(new Set(filtered.map((row) => String(row.run_id))));
    const categoryLabels = usesCalendarDays
      ? categoryKeys.map((day) => new Date(`${day}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))
      : categoryKeys.map((runId) => {
        const row = filtered.find((item) => String(item.run_id) === runId);
        return formatRunDateTime(row?.requested_at);
      });
    const marketIds = Array.from(new Set(filtered.map((row) => `${row.provider_key || 'rentcast'}:${regionHistoryKey(row)}`)));
    return {
      labels: categoryLabels,
      datasets: marketIds.flatMap((marketId) => {
        const marketRows = filtered.filter((row) => `${row.provider_key || 'rentcast'}:${regionHistoryKey(row)}` === marketId);
        const metricsByCategory = usesCalendarDays
          ? new Map(Array.from(new Set(marketRows.map((row) => payloadDayKey(row.requested_at)).filter(Boolean))).map((day) => [day, aggregateRegionMetrics(marketRows.filter((row) => payloadDayKey(row.requested_at) === day))]))
          : new Map(marketRows.map((row) => [String(row.run_id), row]));
        const color = regionChartPalette[Math.max(0, allMarketIds.indexOf(marketId)) % regionChartPalette.length];
        const providerKey = marketRows[0]?.provider_key || 'rentcast';
        const logoReady = providerLogosReady && Boolean(providerLogoImages[providerKey]);
        const providerName = providerLabels[providerKey];
        const marketName = marketRows[0]?.market_name || `Region ${marketId}`;
        const regionName = `${providerName} · ${marketName}`;
        const metrics = categoryKeys.map((key) => metricsByCategory.get(key) || null);
        return [{
          label: `${regionName} · New leads`,
          regionName,
          providerKey,
          marketName,
          logoReady,
          segmentType: 'new',
          stack: marketId,
          data: metrics.map((metric) => metric ? Math.min(Number(metric.new_leads || 0), Number(metric.result_count || 0)) : null),
          regionMetrics: metrics,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 2,
          borderRadius: 0,
          minBarLength: 4,
          maxBarThickness: 86,
          categoryPercentage: .86,
          barPercentage: .94,
          skipNull: true,
        }, {
          label: `${regionName} · Repeat observations`,
          regionName,
          segmentType: 'duplicate',
          stack: marketId,
          data: metrics.map((metric) => metric ? Number(metric.duplicate_records ?? Math.max(Number(metric.result_count || 0) - Number(metric.new_leads || 0), 0)) : null),
          regionMetrics: metrics,
          backgroundColor: '#ef4444',
          borderColor: '#fca5a5',
          borderWidth: 2,
          borderRadius: 0,
          maxBarThickness: 86,
          categoryPercentage: .86,
          barPercentage: .94,
          skipNull: true,
        }];
      }),
    };
  }, [chartDays, data?.history_today, regionChartMarketId, regionHistory, providerLogosReady]);
  const regionResultsLegend = useMemo(() => regionResultsChartData.datasets
    .filter((dataset) => dataset.segmentType === 'new')
    .map((dataset) => ({
      label: dataset.regionName,
      marketName: dataset.marketName,
      providerKey: dataset.providerKey,
      color: dataset.backgroundColor,
    })), [regionResultsChartData]);
  const regionResultsChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: true },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => formatRunDateTime(items?.[0]?.dataset?.regionMetrics?.[items[0].dataIndex]?.requested_at),
          label: (context) => `${context.dataset.regionName}: ${Number(context.parsed.y || 0).toLocaleString()} ${context.dataset.segmentType === 'new' ? 'new leads' : 'repeat observations'}`,
          afterLabel: (context) => {
            const metric = context.dataset.regionMetrics?.[context.dataIndex];
            if (!metric) return [];
            const total = Number(metric.result_count || 0);
            const duplicates = Number(metric.duplicate_records ?? Math.max(total - Number(metric.new_leads || 0), 0));
            const duplicatePercent = total > 0 ? ((duplicates / total) * 100).toFixed(1) : '0.0';
            return [
              // Request/page count and HTTP status are deliberately omitted. The
              // builder reports page counts before a pull, and a failed request is
              // announced by the header pill and the alert panel above the chart,
              // so both were noise on every successful bar.
              `Total returned: ${total.toLocaleString()}`,
              `Repeat-observation share: ${duplicatePercent}%`,
              `Unique leads: ${Number(metric.unique_leads || 0).toLocaleString()}`,
              `Inside region: ${Number(metric.inside_rows || 0).toLocaleString()}`,
              `Outside region: ${Number(metric.outside_rows || 0).toLocaleString()}`,
            ];
          },
        },
      },
    },
    scales: {
      x: { stacked: true, ticks: { color: '#94a3b8', maxRotation: 0, autoSkip: true }, grid: { color: 'rgba(148,163,184,.12)' } },
      y: { stacked: true, beginAtZero: true, grace: '18%', ticks: { color: '#60a5fa', precision: 0 }, title: { display: true, text: 'Records returned', color: '#94a3b8' }, grid: { color: 'rgba(148,163,184,.12)' } },
    },
    layout: { padding: { top: 28 } },
  }), []);

  const visibleObservations = useMemo(() => {
    const scoped = tableScope === 'latest' && data?.latest_request?.comparison_batch_id
      ? filterNewObservationsByBatch(data?.observations || [], data.latest_request.comparison_batch_id)
      : (data?.observations || []);
    const reviewFiltered = reviewScope === 'passed' || reviewScope === 'interested'
      ? sortObservationsByReviewedAt(filterObservationsByReview(scoped, reviewScope))
      : filterObservationsByReview(scoped, reviewScope);
    const opportunityFiltered = filterDashboardRows(reviewFiltered, opportunityFilters, {
      getPotentialBedrooms: (row) => potentialExtraBedroomRange(row.square_footage, row.bedrooms)?.high,
    });
    const query = tableSearch.trim().toLowerCase();
    if (!query) return opportunityFiltered;
    return opportunityFiltered.filter((row) => [row.searched_region_name || row.market_name, row.formatted_address, row.property_type, row.listing_type, row.geography_relationship]
      .some((value) => String(value || '').toLowerCase().includes(query)));
  }, [data, opportunityFilters, reviewScope, tableScope, tableSearch]);
  const pricing = data?.usage_pricing || {};
  const includedCredits = Number(pricing.included_credits || 0);
  const basePrice = Number(data?.period_usage?.base_price || 0);
  const overageLimit = Number(data?.subscription?.overage_limit || 0);
  const planOptions = (data?.available_plans || []).map((plan) => ({
    label: `${plan.display_name} · ${plan.included_requests.toLocaleString()} requests · ${dollars(plan.monthly_price)}/mo`,
    value: plan.plan_code,
  }));
  const saleTypeOptions = useMemo(() => listingTypeOptions(data?.observations || []), [data?.observations]);
  const tableFiltersActive = tableSearch.trim() !== '' || dashboardFilterCount(opportunityFilters) > 0;
  const latestRequestFailed = data?.latest_request?.billing_status === 'released' || data?.latest_request?.status === 'failed';
  const latestRequestSucceeded = data?.latest_request?.status_code === 200 && data?.latest_request?.status === 'completed';
  const latestProviderError = data?.latest_request?.provider_error || {};
  // The latest pull can come from any provider, so the status banner must name the
  // provider that actually ran instead of always saying RentCast.
  const latestRequestProvider = providerLabels[data?.latest_request?.provider_key] || 'Provider';
  const rentcastMarketIdByRegionId = useMemo(() => new Map((data?.markets || [])
    .filter((market) => market.zillow_region_id != null)
    .map((market) => [String(market.zillow_region_id), market.id])), [data]);
  const rentcastReadinessByRegionId = useMemo(() => new Map((data?.markets || [])
    .filter((market) => market.zillow_region_id != null)
    .map((market) => [String(market.zillow_region_id), market.rentcast_ready === true])), [data]);
  const getRentcastMarketSelectionId = (market) => rentcastMarketIdByRegionId.get(String(market.region_id ?? market.zillow_region_id)) ?? market.id;
  const isRentcastMarketSelectable = (market) => rentcastReadinessByRegionId.get(String(market.region_id ?? market.zillow_region_id)) === true;
  const resetTablePaging = () => setTableFirst(0);
  const clearTableFilters = () => { setTableSearch(''); setOpportunityFilters(EMPTY_DASHBOARD_FILTERS); resetTablePaging(); };
  const showAllLeads = () => {
    setTableScope('all');
    resetTablePaging();
  };
  const selectedMarkets = (data?.markets || []).filter((market) => form.market_ids.some((id) => String(id) === String(market.id)));
  const selectedCounties = (researchPayload?.counties || [])
    .filter((county) => form.county_fips.includes(county.geo_id))
    .map((county) => ({ type: 'fema_county', fips: county.geo_id, name: `${county.name}, ${county.state_abbr}`, state_abbr: county.state_abbr }));
  const selectedRegions = [
    ...selectedMarkets.map((market) => ({ ...market, target: { type: 'research_market', id: market.id, name: market.name || market.display_label } })),
    ...selectedCounties.map((county) => ({ ...county, target: county })),
    ...(form.zcta_targets || []).map((target) => ({ ...target, target })),
    ...(form.sourcing_zone_targets || []).map((target) => ({ ...target, target })),
  ];
  const lastPullByMarketId = new Map();
  (data?.last_pulls || []).forEach((pull) => {
    const key = String(pull.market_id);
    const current = lastPullByMarketId.get(key);
    if (!current || new Date(pull.requested_at).getTime() > new Date(current.requested_at).getTime()) {
      lastPullByMarketId.set(key, pull);
    }
  });
  const selectedMarketLastPulls = selectedMarkets
    .map((market) => ({ market, pull: lastPullByMarketId.get(String(market.id)) }))
    .filter(({ pull }) => Boolean(pull));

  const saveReviewStatus = async (row, reviewStatus, { rememberUndo = true, reviewNotes = null } = {}) => {
    if (!row?.lead_id) {
      const identityError = 'This property is missing its saved lead identity. Refresh the dashboard and try again.';
      setReviewSaveError(identityError);
      setError(identityError);
      return null;
    }
    const previousStatus = row.review_status || 'unreviewed';
    setReviewBusyLeadId(row.lead_id);
    setReviewSaveError('');
    setError('');
    try {
      const saved = await apiJson(`/api-lab/leads/${row.lead_id}/review`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ review_status: reviewStatus, review_notes: reviewNotes }),
      });
      setData((current) => ({ ...current, observations: (current?.observations || []).map((item) => item.lead_id === row.lead_id ? { ...item, ...saved } : item) }));
      historyCache.current.clear();
      setSelectedObservations((current) => current.filter((item) => item.lead_id !== row.lead_id));
      if (rememberUndo) {
        const changedRow = { ...row, ...saved };
        reviewToast.current?.show({
          // Passing is a deliberate decision, not a failure, so it uses the neutral
          // slate treatment rather than the error red. Green stays reserved for
          // interested so the two decisions remain distinguishable at a glance.
          severity: reviewStatus === 'interested' ? 'success' : 'info',
          life: 8000,
          content: () => <div className="rcl-review-toast__content">
            <i className={`pi ${reviewStatus === 'interested' ? 'pi-check-circle' : 'pi-info-circle'}`} aria-hidden="true" />
            <div><strong>{reviewStatus === 'passed' ? 'Property passed' : reviewStatus === 'interested' ? 'Property marked interested' : 'Property returned to active review'}</strong><span>{row.formatted_address}</span></div>
            <button type="button" onClick={() => { reviewToast.current?.clear(); saveReviewStatus(changedRow, previousStatus, { rememberUndo: false, reviewNotes: row.review_notes || null }); }}>Undo</button>
          </div>,
        });
      } else reviewToast.current?.clear();
      return saved;
    } catch (reviewError) {
      const message = reviewError.message || 'Could not save this property review.';
      setReviewSaveError(message);
      setError(message);
      return null;
    } finally {
      setReviewBusyLeadId(null);
    }
  };

  const openPassReview = (row, status = 'passed') => {
    setReviewSaveError('');
    setReviewNoteStatus(status);
    setPassReviewRow(row);
    setPassReviewNotes(row.review_notes || '');
  };

  const closeReviewNoteDialog = () => {
    setReviewSaveError('');
    setPassReviewRow(null);
    setPassReviewNotes('');
  };

  const submitPassReview = async () => {
    if (!passReviewRow) return;
    const notes = passReviewNotes.trim();
    // Passing still requires a written reason; an interested note is optional so a
    // quick positive decision is never blocked by a required field.
    if (reviewNoteStatus === 'passed' && !notes) return;
    const saved = await saveReviewStatus(passReviewRow, reviewNoteStatus, { reviewNotes: notes });
    if (saved) closeReviewNoteDialog();
  };

  const keepPassTooltipOpen = () => {
    if (passTooltipHideTimer.current) clearTimeout(passTooltipHideTimer.current);
  };

  const hidePassTooltip = () => {
    if (passTooltipHideTimer.current) clearTimeout(passTooltipHideTimer.current);
    passTooltipHideTimer.current = setTimeout(() => {
      setHoveredPassReview(null);
      setHoverReviewEditing(false);
      setHoverReviewDraft('');
    }, 140);
  };

  // Both saved decisions remain editable from their badge, including older
  // interested records that do not have a note yet.
  const hasReviewNote = (row) => row?.review_status === 'passed' || row?.review_status === 'interested';

  const showPassTooltipFromElement = (row, element) => {
    if (!hasReviewNote(row)) return;
    keepPassTooltipOpen();
    const bounds = element.getBoundingClientRect();
    if (hoveredPassReview?.row?.lead_id !== row.lead_id) {
      setHoverReviewEditing(false);
      setHoverReviewDraft('');
    }
    setHoveredPassReview({ row, x: bounds.left, y: bounds.bottom });
  };

  const startHoverReviewEdit = () => {
    if (!hoveredPassReview) return;
    keepPassTooltipOpen();
    setHoverReviewDraft(hoveredPassReview.row.review_notes || '');
    setHoverReviewEditing(true);
  };

  const cancelHoverReviewEdit = () => {
    setHoverReviewEditing(false);
    setHoverReviewDraft('');
  };

  const saveHoverReviewNote = async () => {
    if (!hoveredPassReview) return;
    const row = hoveredPassReview.row;
    const notes = hoverReviewDraft.trim();
    if (row.review_status === 'passed' && !notes) return;
    const saved = await saveReviewStatus(row, row.review_status, { rememberUndo: false, reviewNotes: notes });
    if (!saved) return;
    setHoveredPassReview((current) => current?.row?.lead_id === row.lead_id
      ? { ...current, row: { ...current.row, ...saved } }
      : current);
    setHoverReviewEditing(false);
    setHoverReviewDraft('');
  };

  const reviewStatusBody = (row) => <div className="rcl-review-actions">
    {row.review_status !== 'unreviewed' && row.review_status && <span className={`rcl-review-badge rcl-review-badge--${row.review_status}`} tabIndex={hasReviewNote(row) ? 0 : undefined} aria-label={hasReviewNote(row) ? `${row.review_status === 'passed' ? 'Passed' : 'Interested'} property notes for ${row.formatted_address || 'property'}` : undefined} title={hasReviewNote(row) ? 'Hover to preview and scroll notes' : undefined} onMouseEnter={hasReviewNote(row) ? (event) => showPassTooltipFromElement(row, event.currentTarget) : undefined} onMouseLeave={hasReviewNote(row) ? hidePassTooltip : undefined} onFocus={hasReviewNote(row) ? (event) => showPassTooltipFromElement(row, event.currentTarget) : undefined} onBlur={hasReviewNote(row) ? hidePassTooltip : undefined}>{row.review_status === 'passed' ? 'Passed' : 'Interested'}</span>}
    <div>
      <button type="button" className="rcl-review-action rcl-review-action--interested" aria-label={`Mark ${row.formatted_address || 'property'} interested`} title="Mark interested" disabled={reviewBusyLeadId === row.lead_id} onClick={() => openPassReview(row, 'interested')}><i className="pi pi-check" aria-hidden="true" /></button>
      <button type="button" className="rcl-review-action rcl-review-action--passed" aria-label={`Pass on ${row.formatted_address || 'property'}`} title="Pass on property" disabled={reviewBusyLeadId === row.lead_id} onClick={() => openPassReview(row)}><i className="pi pi-times" aria-hidden="true" /></button>
      {(row.review_status === 'passed' || row.review_status === 'interested') && <button type="button" className="rcl-review-action" aria-label={`Reset review for ${row.formatted_address || 'property'}`} title="Reset to unreviewed" disabled={reviewBusyLeadId === row.lead_id} onClick={() => saveReviewStatus(row, 'unreviewed')}><i className="pi pi-undo" aria-hidden="true" /></button>}
    </div>
  </div>;

  const loadSourceEvidence = async (observationId) => {
    if (sourceEvidenceRef.current[observationId]?.loading || sourceEvidenceRef.current[observationId]?.data) return;
    sourceEvidenceRef.current[observationId] = { loading: true };
    setSourceEvidence((current) => ({ ...current, [observationId]: sourceEvidenceRef.current[observationId] }));
    try {
      const evidence = await apiJson(`/api-lab/observations/${observationId}/source`);
      sourceEvidenceRef.current[observationId] = { data: evidence };
      setSourceEvidence((current) => ({ ...current, [observationId]: sourceEvidenceRef.current[observationId] }));
    } catch (err) {
      sourceEvidenceRef.current[observationId] = { error: err.message || 'Could not load source evidence.' };
      setSourceEvidence((current) => ({ ...current, [observationId]: sourceEvidenceRef.current[observationId] }));
    }
  };
  const loadCompRequestPayload = async (requestId) => {
    if (compRequestPayloadsRef.current[requestId]?.loading || compRequestPayloadsRef.current[requestId]?.data) return;
    compRequestPayloadsRef.current[requestId] = { loading: true };
    setCompRequestPayloads((current) => ({ ...current, [requestId]: compRequestPayloadsRef.current[requestId] }));
    try {
      const payload = await apiJson(`/api-lab/comp-requests/${requestId}/source`);
      compRequestPayloadsRef.current[requestId] = { data: payload };
    } catch (err) {
      compRequestPayloadsRef.current[requestId] = { error: err.message || 'Could not load comp request evidence.' };
    }
    setCompRequestPayloads((current) => ({ ...current, [requestId]: compRequestPayloadsRef.current[requestId] }));
  };
  const zillowEndpoint = requestTargetMode === 'property'
    ? (providerForms.realtyapi_zillow.zillow_endpoint || 'search_byaddress')
    : 'search_byaddress';
  const realtyApiProviderConfig = (source = realtyApiSource) => {
    const variants = realtyRequestVariants[source] || [];
    return source === 'realtyapi_zillow'
      ? { ...providerForms.realtyapi_zillow, zillow_endpoint: zillowEndpoint, ...(variants.length ? { request_variants: variants } : {}) }
      : { ...providerForms[source], ...(variants.length ? { request_variants: variants } : {}) };
  };
  const scheduleProviderConfig = (providerKey) => {
    const base = realtyApiProviderConfig(providerKey);
    const searches = base.searches || defaultSearches();
    const { request_variants: _variants, ...withoutVariants } = base;
    return {
      ...withoutVariants,
      enabled: true,
      ...(providerKey === 'realtyapi_zillow' ? { zillow_endpoint: 'search_byaddress' } : {}),
      searches: Object.fromEntries(Object.entries(searches).map(([key, value]) => [key, {
        ...value,
        enabled: key === 'For_Sale',
      }])),
    };
  };
  const schedulePayloadFromPreview = (schedulePreview, providerKey) => {
    const provider = (schedulePreview.providers || []).find((item) => item.provider_key === providerKey);
    const saleRequests = (provider?.requests || []).filter((item) => item.operation_key === 'sale_listing_search');
    if (!saleRequests.length) throw new Error('No schedulable For Sale requests were produced.');
    const targets = Array.from(new Map(saleRequests.map((item) => [item.market.search_target_id, item.market])).values());
    if (targets.some((target) => !target.search_target_id)) throw new Error('One selected region is not available for scheduling.');
    const first = saleRequests[0];
    const requestParameters = Object.fromEntries(Object.entries(first.parameters || {}).filter(([key]) => (
      !['location', 'latitude', 'longitude', 'bbox', 'polygon', 'page'].includes(key)
    )));
    const maximumRecords = Number(first.parameters?.resultCount || 200);
    return {
      name: scheduleDraft.name.trim() || `${providerLabels[providerKey]} · ${targets.length} region${targets.length === 1 ? '' : 's'}`,
      schedule_kind: 'area_discovery',
      timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Denver',
      cadence_kind: 'interval',
      cadence_config: { weeks: Number(scheduleDraft.weeks) },
      targets: targets.map((target) => ({
        search_target_id: target.search_target_id,
        pagination_mode: 'first_page',
        maximum_pages: 1,
      })),
      requests: [{
        provider_key: providerKey,
        operation_key: 'sale_listing_search',
        method: first.method,
        endpoint: first.endpoint,
        request_parameters: requestParameters,
        maximum_returned_records: maximumRecords,
      }],
    };
  };
  const activateNewSchedule = async (payload) => {
    const created = await apiJson('/property-api/schedules', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    await apiJson(`/property-api/schedules/${created.schedule_id}/approve`, { method: 'POST' });
    return apiJson(`/property-api/schedules/${created.schedule_id}/activate`, { method: 'POST' });
  };
  const saveSchedule = async () => {
    setScheduleBusy(true); setScheduleError('');
    try {
      let payload;
      if (editingSchedule) {
        const detail = await apiJson(`/property-api/schedules/${editingSchedule.schedule_id}`);
        const snapshot = detail.configuration_snapshot || {};
        payload = {
          name: scheduleDraft.name.trim() || detail.name,
          schedule_kind: 'area_discovery', timezone_name: detail.timezone_name,
          cadence_kind: 'interval', cadence_config: { weeks: Number(scheduleDraft.weeks) },
          targets: snapshot.targets || [], requests: snapshot.requests || [],
          approved_request_cap: detail.approved_request_cap,
          approved_unit_cap: detail.approved_unit_cap,
          approved_incremental_cost_cap: detail.approved_incremental_cost_cap,
          plan_evidence: { replaced_schedule_id: detail.schedule_id },
        };
      } else {
        if (requestTargetMode !== 'region' || selectedSearchTargets.length === 0) {
          throw new Error('Select at least one region before saving a schedule.');
        }
        if (!['realtyapi_zillow', 'realtyapi_realtor'].includes(composerProvider) || composerPurpose !== 'For_Sale') {
          throw new Error('Initial scheduling supports the RealtyAPI For Sale endpoint.');
        }
        const providerKey = composerProvider;
        const schedulePreview = await apiJson('/property-api/discovery/preview', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targets: selectedSearchTargets,
            providers: { [providerKey]: scheduleProviderConfig(providerKey) },
          }),
        });
        payload = schedulePayloadFromPreview(schedulePreview, providerKey);
      }
      await activateNewSchedule(payload);
      if (editingSchedule) {
        await apiJson(`/property-api/schedules/${editingSchedule.schedule_id}/disable`, { method: 'POST' });
      }
      await loadSchedules();
      setEditingSchedule(null);
      setScheduleDraft({ name: '', weeks: 1 });
    } catch (err) {
      setScheduleError(err.message || 'Could not save schedule.');
    } finally {
      setScheduleBusy(false);
    }
  };
  const toggleSchedule = async (schedule) => {
    setScheduleBusy(true); setScheduleError('');
    try {
      await apiJson(`/property-api/schedules/${schedule.schedule_id}/${schedule.status === 'active' ? 'pause' : 'activate'}`, { method: 'POST' });
      await loadSchedules();
    } catch (err) { setScheduleError(err.message || 'Could not update schedule.'); }
    finally { setScheduleBusy(false); }
  };
  const editSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleDraft({ name: schedule.name, weeks: Number(schedule.cadence_config?.weeks || 1) });
    setSchedulePanelOpen(true);
  };
  const deleteSchedule = async (schedule) => {
    setScheduleBusy(true); setScheduleError('');
    try {
      await apiJson(`/property-api/schedules/${schedule.schedule_id}/disable`, { method: 'POST' });
      await loadSchedules();
      if (editingSchedule?.schedule_id === schedule.schedule_id) setEditingSchedule(null);
    } catch (err) { setScheduleError(err.message || 'Could not delete schedule.'); }
    finally { setScheduleBusy(false); }
  };
  const toggleSchedulePanel = () => {
    setScheduleError('');
    const opening = !schedulePanelOpen;
    setSchedulePanelOpen(opening);
    if (opening) loadSchedules().catch((err) => setScheduleError(err.message || 'Could not load schedules.'));
  };
  const openApiDialog = () => {
    setPreview(null);
    setRequestTargetMode('property');
    const draftKey = selectedObservations.map((row) => String(row.observation_id)).sort().join('|');
    const initializeDraft = propertyRequestDraftKeyRef.current !== draftKey;
    if (initializeDraft) {
      propertyRequestDraftKeyRef.current = draftKey;
      setComposerProvider('realtyapi_zillow');
      setComposerPurpose('Sold');
      setRealtyApiSource('realtyapi_zillow');
      setRealtyRequestVariants({
        realtyapi_zillow: [],
        realtyapi_realtor: [],
      });
      setRentCastRequestOrder(['rent_comps']);
      setProviderExcludedTargetKeys({ rentcast: [], zillapi: [] });
      setSelectedProviders({ rentcast: false, zillapi: false, realtyapi_realtor: false, realtyapi_zillow: false });
      setPropertyApiDrafts(Object.fromEntries(selectedObservations.map((row) => [row.observation_id, {
        rentcast_endpoint: 'rent_comps', rent_comps: false, arv_comps: false, property_tax: false,
        bedrooms: row.bedrooms ?? '', bathrooms: row.bathrooms ?? '', square_footage: row.square_footage ?? '',
        property_type: rentCastPropertyTypes.includes(row.property_type) ? row.property_type : 'Single Family',
      }])));
      setProviderForms((current) => ({
        ...current,
        realtyapi_zillow: {
          ...current.realtyapi_zillow,
          zillow_endpoint: 'search_byaddress',
          searches: {
            ...defaultSearches(),
            For_Sale: { ...defaultSearches().For_Sale, enabled: false },
            Sold: { ...defaultSearches().Sold, enabled: false },
            For_Rent: { ...defaultSearches().For_Rent, enabled: false },
          },
        },
      }));
    }
    setExpandedProvider('realtyapi_realtor');
    setRequestDialogOpen(true);
    setPreviewRevision((current) => current + 1);
  };
  const toggleExpandedRows = (event) => {
    setExpandedRows(event.data);
    // PrimeReact keys expandedRows by dataKey (lead_id), but source evidence is
    // fetched by observation_id, so resolve each expanded key back to its row.
    const expandedRowObjects = Array.isArray(event.data)
      ? event.data
      : Object.keys(event.data || {}).map((leadId) => visibleObservations.find((row) => String(row.lead_id) === String(leadId))).filter(Boolean);
    expandedRowObjects.forEach((row) => {
      const observationId = row.observation_id;
      if (observationId && !sourceEvidenceRef.current[observationId]) loadSourceEvidence(observationId);
    });
  };
  const rawExpansion = (row) => {
    const evidence = sourceEvidence[row.observation_id];
    if (!evidence || evidence.loading) return <div className="rcl-raw rcl-raw--loading" role="status">Loading preserved source evidence…</div>;
    if (evidence.error) return <div className="rcl-raw rcl-raw--error" role="alert">{evidence.error} <Button label="Retry" text onClick={() => loadSourceEvidence(row.observation_id)} /></div>;
    const apiResults = apiEvidenceRows(evidence.data.api_results);
    const listingObservations = evidence.data.listing_observations || [{
      observation_id: row.observation_id,
      endpoint: evidence.data.endpoint,
      request_parameters: evidence.data.request_parameters,
      raw_source_record: evidence.data.raw_source_record,
      searched_region_name: evidence.data.searched_region_name || row.searched_region_name || row.market_name,
      requested_at: row.requested_at,
    }];
    // One timeline of every endpoint whose evidence touches this lead, newest
    // first. Listing observations are this lead's own record and stay open;
    // everything else is collapsed so the row opens at a readable size.
    const compEvidence = Array.isArray(evidence.data.comp_calculation_evidence) ? evidence.data.comp_calculation_evidence : [];
    const datasetEvidence = Array.isArray(evidence.data.property_api_dataset_evidence) ? evidence.data.property_api_dataset_evidence : [];
    const relevantRequestIds = new Set([
      ...listingObservations.map((listing) => listing.property_api_request_id),
      ...compEvidence.map((comp) => comp.property_api_request_id),
    ].filter(Boolean).map(String));
    // Dataset batches can contain several regions. A lead should see only the
    // logical request groups that produced that lead or contributed one of its
    // comps—not every unrelated request that happened to share the batch.
    const groupedDatasetEvidence = groupDatasetEvidencePages(datasetEvidence)
      .filter((group) => group.operation_key !== 'sale_listing_search'
        && group.pages.some((page) => relevantRequestIds.has(String(page.request_id))));
    const timelineEntries = [
      ...listingObservations.map((listing) => ({
        entryKey: `listing:${listing.observation_id || listing.requested_at}`,
        requestedAt: listing.requested_at,
        // The call that discovered this lead always leads the timeline, even
        // when a later market-wide comp pull is chronologically newer.
        pinnedFirst: true,
        defaultOpen: true,
        scopeNote: 'This property',
        requestTitle: `${evidenceOperationTitle(null, listing.request_parameters)} · ${listing.searched_region_name || 'Region unavailable'}`,
        endpoint: listing.endpoint,
        requestParameters: listing.request_parameters,
        responseTitle: 'Complete property record',
        responseBody: listing.raw_source_record,
      })),
      ...apiResults.map((result) => {
        const label = result.api_key === 'rent_comps' ? 'Rent – GET' : result.api_key === 'arv_comps' ? 'Sold – GET' : `${result.api_key} – GET`;
        return {
          entryKey: `api:${result.evidence_key}`,
          requestedAt: result.requested_at,
          defaultOpen: false,
          scopeNote: 'This property',
          requestTitle: label,
          endpoint: result.endpoint,
          requestParameters: result.request_parameters,
          responseTitle: `${label} output`,
          responseNote: result.status_code ? `HTTP ${result.status_code}` : result.status,
          responseBody: result.raw_response,
        };
      }),
      ...groupedDatasetEvidence.map((entry) => {
        const location = entry.request_parameters?.location || entry.request_parameters?.region || entry.request_parameters?.address;
        const returned = entry.pages.reduce((total, page) => total + Number(page.result_count || 0), 0);
        const saved = entry.pages.reduce((total, page) => total + Number(page.normalized_record_count || 0), 0);
        return {
          entryKey: `dataset:${entry.group_key}`,
          requestedAt: entry.pages.at(-1)?.requested_at || entry.requested_at,
          defaultOpen: false,
          scopeNote: `${entry.pages.length} preserved page${entry.pages.length === 1 ? '' : 's'} · ${returned.toLocaleString()} returned · ${saved.toLocaleString()} saved`,
          requestTitle: `${evidenceOperationTitle(entry.operation_key, entry.request_parameters)}${location ? ` · ${location}` : ''}`,
          endpoint: entry.endpoint,
          responseTitle: 'Complete preserved provider response',
          pages: entry.pages,
        };
      }),
    ];
    const sortedTimeline = sortEvidenceTimeline(timelineEntries);

    const crimeKey = `crime:${row.lead_id}`;
    const crimeOpen = !!openEvidenceEntries[crimeKey];
    return <div className="rcl-raw">
      {sortedTimeline.map((entry) => {
        const stateKey = `${row.observation_id}:${entry.entryKey}`;
        const isOpen = openEvidenceEntries[stateKey] ?? entry.defaultOpen;
        const selectedPageNumber = Number(evidencePageSelections[stateKey] || entry.pages?.[0]?.request_parameters?.page || 1);
        const selectedPage = entry.pages?.find((page) => Number(page.request_parameters?.page || 1) === selectedPageNumber) || entry.pages?.[0];
        const requestParameters = selectedPage?.request_parameters || entry.requestParameters;
        const responsePayload = selectedPage ? compRequestPayloads[selectedPage.request_id] : null;
        const selectEvidencePage = (requestedPage) => {
          const pageNumbers = (entry.pages || []).map((page) => Number(page.request_parameters?.page || 1));
          const boundedPage = Math.max(Math.min(...pageNumbers), Math.min(Math.max(...pageNumbers), Number(requestedPage) || 1));
          const page = entry.pages?.find((candidate) => Number(candidate.request_parameters?.page || 1) === boundedPage);
          if (!page) return;
          setEvidencePageSelections((current) => ({ ...current, [stateKey]: boundedPage }));
          loadCompRequestPayload(page.request_id);
        };
        return <section className={`rcl-evidence-entry${isOpen ? ' rcl-evidence-entry--open' : ''}`} key={entry.entryKey}>
          <button
            type="button"
            className="rcl-evidence-entry__toggle"
            aria-expanded={isOpen}
            onClick={() => {
              const nextOpen = !(openEvidenceEntries[stateKey] ?? entry.defaultOpen);
              if (nextOpen && selectedPage) loadCompRequestPayload(selectedPage.request_id);
              setOpenEvidenceEntries((current) => ({ ...current, [stateKey]: nextOpen }));
            }}
          >
            <i className={`pi ${isOpen ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
            <strong>{entry.requestTitle}</strong>
            <time dateTime={entry.requestedAt || undefined}>{formatPulledDateTime(entry.requestedAt)}</time>
            <span>{entry.scopeNote}</span>
          </button>
          {isOpen && <div className="rcl-api-evidence-row">
            {entry.pages?.length > 1 && <div className="rcl-evidence-page-picker" aria-label={`Response pagination for ${entry.requestTitle}`}>
              <span>Page</span>
              <button type="button" onClick={() => selectEvidencePage(selectedPageNumber - 1)} disabled={selectedPageNumber <= Number(entry.pages[0].request_parameters?.page || 1)} aria-label="Previous response page"><i className="pi pi-chevron-left" aria-hidden="true" /></button>
              <InputNumber value={selectedPageNumber} min={Number(entry.pages[0].request_parameters?.page || 1)} max={Number(entry.pages.at(-1).request_parameters?.page || entry.pages.length)} useGrouping={false} onValueChange={(event) => selectEvidencePage(event.value)} aria-label={`Response page for ${entry.requestTitle}`} />
              <span>of {entry.pages.length}</span>
              <button type="button" onClick={() => selectEvidencePage(selectedPageNumber + 1)} disabled={selectedPageNumber >= Number(entry.pages.at(-1).request_parameters?.page || entry.pages.length)} aria-label="Next response page"><i className="pi pi-chevron-right" aria-hidden="true" /></button>
            </div>}
            <div className="rcl-api-request">
              <div className="rcl-api-json__heading"><strong>Request</strong></div>
              <code>{entry.endpoint}</code>
              <pre>{JSON.stringify(requestParameters, null, 2)}</pre>
            </div>
            <div className="rcl-api-response">
              <strong>{entry.responseTitle}</strong>
              {(selectedPage?.http_status || entry.responseNote) && <small>{selectedPage?.http_status ? `HTTP ${selectedPage.http_status}` : entry.responseNote}</small>}
              <code className="rcl-api-endpoint-spacer" aria-hidden="true">&nbsp;</code>
              {selectedPage
                ? responsePayload?.loading
                  ? <pre role="status">Loading preserved provider response…</pre>
                  : responsePayload?.error
                    ? <pre role="alert">{responsePayload.error}</pre>
                    : <pre>{JSON.stringify(responsePayload?.data?.parsed_json, null, 2)}</pre>
                : <pre>{JSON.stringify(entry.responseBody, null, 2)}</pre>}
            </div>
          </div>}
        </section>;
      })}
      <section className={`rcl-crime-evidence${crimeOpen ? ' rcl-crime-evidence--open' : ''}`} aria-label="Community crime evidence JSON">
        <button
          type="button"
          className="rcl-evidence-entry__toggle rcl-evidence-entry__toggle--crime"
          aria-expanded={crimeOpen}
          onClick={() => setOpenEvidenceEntries((current) => ({ ...current, [crimeKey]: !current[crimeKey] }))}
        >
          <i className={`pi ${crimeOpen ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
          <strong>Community crime evidence</strong>
          <span>Address jurisdiction, selected local agency, monthly FBI UCR evidence, and compact county context</span>
          <small>{evidence.data.community_crime_evidence?.publication_status === 'experimental_not_filter_ready' ? 'Experimental · not filter ready' : 'Evidence unavailable'}</small>
        </button>
        {crimeOpen && <pre>{JSON.stringify(evidence.data.community_crime_evidence || {
          status: 'unavailable',
          explanation: 'No community crime evidence was returned for this address.',
        }, null, 2)}</pre>}
      </section>
    </div>;
  };

  const summary = data?.summary || {};
  const propertyApiProviders = data?.property_api_status?.providers || [];
  const providerByKey = new Map(propertyApiProviders.map((provider) => [provider.provider_key, provider]));
  const rentCastProvider = providerByKey.get('rentcast');
  const rentCastQuotaUsage = Number(rentCastProvider?.quota?.counted_units ?? data?.month_usage ?? 0);
  const selectedProviderKeys = Object.keys(selectedProviders).filter((key) => selectedProviders[key]);
  const missingProviderCredentials = selectedProviderKeys.filter((key) => !(data?.provider_credentials?.[key] ?? (key === 'rentcast' && data?.credential_configured)));
  const creditProviderKeys = ['realtyapi_realtor', 'zillapi', 'rentcast'];
  const activeCreditIndex = Math.max(0, creditProviderKeys.indexOf(creditProviderKey));
  const effectiveCreditProviderKey = creditProviderKey === 'realtyapi_realtor'
    ? realtyApiSource
    : creditProviderKey;
  const effectiveCreditProvider = providerByKey.get(effectiveCreditProviderKey)
    || providerByKey.get(creditProviderKey);
  const moveCreditProvider = (direction) => {
    const nextIndex = (activeCreditIndex + direction + creditProviderKeys.length) % creditProviderKeys.length;
    setCreditProviderKey(creditProviderKeys[nextIndex]);
  };
  const renderCreditSummary = (variant = '') => {
    const isResearchPrototype = variant === 'rcl-credit-summary--research';
    const planLabel = `${data?.subscription?.plan_name || pricing.plan_name || 'Developer'} plan`;
    const renewalLabel = formatRenewalDate(data?.subscription?.next_renewal_date);
    const provider = effectiveCreditProvider;
    const isRentCast = creditProviderKey === 'rentcast';
    const providerName = creditProviderKey === 'zillapi' ? 'ZILLAPI' : creditProviderKey === 'realtyapi_realtor' ? 'REALTYAPI' : 'RENTCAST';
    // Every provider card must display the same canonical quota state that the
    // request builder enforces. The legacy RentCast usage fields remain only as
    // a compatibility fallback for databases that predate the shared ledger.
    const providerUsed = Number(provider?.quota?.counted_units ?? (isRentCast ? rentCastQuotaUsage : 0));
    const providerIncluded = Number(provider?.plan?.included_units ?? (isRentCast ? includedCredits : 0));
    const providerBase = isRentCast ? basePrice : Number(provider?.plan?.base_price || 0);
    const providerOverage = isRentCast
      ? Math.max(0, providerUsed - providerIncluded) * Number(pricing.overage_cost_per_credit || 0)
      : 0;
    const projectionTargetCount = requestTargetMode === 'property'
      ? selectedPropertyTargets.length
      : selectedSearchTargets.length;
    const providerConfig = isRentCast
      ? requestTargetMode === 'property'
        ? { fixed_requests_per_target: 1 }
        : { request_cap: form.request_cap, result_limit: form.limit }
      : requestTargetMode === 'property'
        && effectiveCreditProviderKey === 'realtyapi_zillow'
        && zillowEndpoint === 'taxinfo'
        ? { ...providerForms[effectiveCreditProviderKey], fixed_requests_per_target: 1 }
        : providerForms[effectiveCreditProviderKey];
    const projection = providerSearchProjection(
      effectiveCreditProviderKey,
      selectedProviders[creditProviderKey] ? projectionTargetCount : 0,
      providerConfig,
      provider,
    );
    const nextUnits = projection.label;
    const nextSearchDetail = projection.exceedsSafeBalance
      ? 'Exceeds safe balance'
      : `${projection.safeAfter} safely available`;
    return <div className={`rcl-credit-carousel${isResearchPrototype ? ' rcl-credit-carousel--research' : ''}`} aria-label={`${providerName} credit summary`}>
      <button type="button" className="rcl-credit-carousel__control rcl-credit-carousel__control--previous" aria-label="Show previous API usage" onClick={() => moveCreditProvider(-1)}><i className="pi pi-chevron-left" aria-hidden="true" /></button>
      <div className={`rcl-credit-summary${variant ? ` ${variant}` : ''}`} aria-live="polite">
        <div className="rcl-credit-summary__current"><span>{providerName} USAGE</span><strong>{providerUsed}<small> / {providerIncluded || '—'}</small></strong>{isRentCast ? <button type="button" className={isResearchPrototype ? 'rcl-billing-text' : 'rcl-billing-edit'} onClick={openPlanDialog}>{planLabel}</button> : <em>{provider?.plan?.display_name || 'Plan unavailable'}</em>}</div>
        <div className="rcl-credit-summary__base"><span>{providerName} BASE {!isResearchPrototype && isRentCast && <a href={pricing.source_url || 'https://www.rentcast.io/api'} target="_blank" rel="noopener noreferrer" aria-label="View RentCast API pricing"><i className="pi pi-info-circle" /></a>}</span><strong>{dollars(providerBase)}</strong>{isRentCast ? <button type="button" className={isResearchPrototype ? 'rcl-billing-text' : 'rcl-billing-edit'} onClick={openRenewalDialog}>{renewalLabel}</button> : <em>{providerIncluded ? `${providerIncluded} included` : 'Allowance unavailable'}</em>}</div>
        <div className="rcl-credit-summary__overage"><span>{isRentCast ? 'OVERAGES' : 'PAID CREDITS'}</span><strong>{dollars(providerOverage)}</strong>{isRentCast ? <button type="button" className={isResearchPrototype ? 'rcl-billing-text' : 'rcl-billing-edit'} onClick={openOverageDialog}>Limit {dollars(overageLimit)}</button> : <button type="button" className="rcl-billing-text" onClick={() => openProviderProtectionDialog(provider)}>{provider?.budget?.provider_overage_permitted ? 'Allowed' : 'Protected'}</button>}</div>
        <div className="rcl-credit-summary__next"><span>NEXT SEARCH</span><strong>{nextUnits}</strong><em>{nextSearchDetail}</em></div>
      </div>
      <button type="button" className="rcl-credit-carousel__control rcl-credit-carousel__control--next" aria-label="Show next API usage" onClick={() => moveCreditProvider(1)}><i className="pi pi-chevron-right" aria-hidden="true" /></button>
    </div>;
  };
  const researchHeaderSupplement = <div className="rcl-research-header-supplement">
    {renderCreditSummary('rcl-credit-summary--research')}
    {runInProgress ? <div className="rcl-research-build rcl-research-build--loading" role="status" aria-label="Property API pull in progress"><i className="pi pi-spinner pi-spin" aria-hidden="true" /><span>Pulling leads…</span></div> : (selectedSearchTargets.length > 0 || continuations.length > 0) ? <button type="button" className="rcl-research-build" aria-label={continuations.length > 0 ? `Build request · ${continuations.length} pagination decision${continuations.length === 1 ? '' : 's'} available` : `Build Property API request for ${selectedSearchTargets.length} selected region${selectedSearchTargets.length === 1 ? '' : 's'}`} onClick={() => { setPreview(null); setRequestTargetMode('region'); setComposerProvider('realtyapi_zillow'); setComposerPurpose('For_Sale'); setRealtyApiSource('realtyapi_zillow'); setRealtyRequestVariants({ realtyapi_zillow: [], realtyapi_realtor: [] }); setProviderExcludedTargetKeys({ rentcast: [], zillapi: [] }); setSelectedProviders({ rentcast: false, zillapi: false, realtyapi_realtor: false, realtyapi_zillow: false }); setRequestDialogOpen(true); }}><i className="pi pi-sliders-h" aria-hidden="true" /><span>Build request</span><b>{continuations.length || selectedSearchTargets.length}</b></button> : null}
  </div>;
  const compactNumber = (value) => value == null ? '—' : Number(value).toLocaleString('en-US', { maximumFractionDigits: 1 });
  const crimeStatusLabel = (status) => status === 'elevated' ? 'Region Risk' : status === 'not_elevated' ? 'Region Low Risk' : 'Unknown';
  const crimeRate = (value) => value == null ? '—' : `${Math.round(Number(value)).toLocaleString('en-US')} / 100K`;
  const crimeRatio = (value) => value == null ? '—' : `${Number(value).toFixed(2)}×`;
  const crimeIssueLabels = {
    local_agency_unresolved: 'No unique local city police department could be resolved.',
    incomplete_reporting: 'A comparable rate requires all 12 usable reporting months.',
    missing_population: 'The FBI represented population is unavailable or zero.',
    insufficient_national_peers: 'Fewer than 20 same-type, same-population-group national peers qualify.',
    small_population_volatility: 'Small populations can produce volatile per-100K rates; review raw counts.',
    insufficient_state_peers: 'Fewer than five state peers qualify; use the national comparison.',
    crime_evidence_unavailable: 'No FBI department-level comparison is available.',
  };
  const crimeMetricDetail = (metric) => metric
    ? `${crimeRate(metric.rate_per_100k)} · ${crimeRatio(metric.national_peers?.ratio)} US peers`
    : 'Comparable rate unavailable';
  const showCrimeInfo = async (event, row) => {
    const leadId = String(row.lead_id);
    const cached = crimeSummaryDetailsRef.current[leadId];
    const address = row.formatted_address || '';
    const initial = cached?.data
      ? { ...cached.data, formatted_address: address }
      : {
        ...(row.community_crime_summary || {}), lead_id: leadId, loading: !cached?.error,
        formatted_address: address,
        issues: cached?.error ? ['crime_evidence_unavailable'] : [],
      };
    keepCrimeInfoOpen();
    setCrimePromptCopied(false);
    setActiveCrimeSummary(initial);
    crimeInfo.current?.show(event);
    if (cached?.data || cached?.loading) return;
    crimeSummaryDetailsRef.current[leadId] = { loading: true };
    try {
      const detail = await apiJson(`/api-lab/leads/${leadId}/crime-summary`);
      crimeSummaryDetailsRef.current[leadId] = { data: detail };
      setActiveCrimeSummary((current) => current?.lead_id === leadId
        ? { ...detail, lead_id: leadId, formatted_address: address }
        : current);
    } catch (_err) {
      crimeSummaryDetailsRef.current[leadId] = { error: true };
      setActiveCrimeSummary((current) => current?.lead_id === leadId
        ? { ...(row.community_crime_summary || {}), lead_id: leadId, formatted_address: address, issues: ['crime_evidence_unavailable'] }
        : current);
    }
  };
  const keepCrimeInfoOpen = () => {
    if (crimeTooltipHideTimer.current) clearTimeout(crimeTooltipHideTimer.current);
  };
  const hideCrimeInfo = () => {
    if (crimeTooltipHideTimer.current) clearTimeout(crimeTooltipHideTimer.current);
    crimeTooltipHideTimer.current = setTimeout(() => crimeInfo.current?.hide(), 140);
  };
  const crimeOrdinal = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);
    const remainderTen = number % 10;
    const remainderHundred = number % 100;
    if (remainderTen === 1 && remainderHundred !== 11) return `${number}st`;
    if (remainderTen === 2 && remainderHundred !== 12) return `${number}nd`;
    if (remainderTen === 3 && remainderHundred !== 13) return `${number}rd`;
    return `${number}th`;
  };
  const crimeResearchPrompt = (summary) => {
    if (!summary) return '';
    const address = summary.formatted_address || 'this property';
    const lines = [
      `Research neighborhood-level safety for ${address}.`,
      '',
      'I am evaluating this property for a long-distance BRRRR rental purchase. Please research the specific neighborhood and streets around this address, and tell me what a local would know that city-wide statistics cannot show. Cite your sources.',
      '',
      'For context, here is the city-wide FBI evidence I already have. It describes the entire police jurisdiction, NOT this neighborhood:',
    ];
    if (summary.agency_name) {
      const year = summary.source_year ? ` (FBI Return A, ${summary.source_year})` : '';
      lines.push(`- Department: ${summary.agency_name}${year}`);
    } else {
      lines.push('- Department: no local police department could be matched to this address.');
    }
    const metric = (label, data) => {
      if (!data) return null;
      const parts = [`${crimeOrdinal(data.national_peer_percentile)} percentile vs US peers`];
      if (data.rate_per_100k != null) parts.push(`${crimeRate(data.rate_per_100k)}`);
      if (data.national_peers?.ratio != null) parts.push(`${crimeRatio(data.national_peers.ratio)} US peer rate`);
      if (data.state_peers?.ratio != null) parts.push(`${crimeRatio(data.state_peers.ratio)} state peer rate`);
      return `- ${label}: ${parts.join(' · ')}`;
    };
    [metric('Violent crime', summary.violent_crime), metric('Property crime', summary.property_crime)]
      .filter(Boolean).forEach((line) => lines.push(line));
    if (summary.represented_population != null) {
      lines.push(`- Jurisdiction population: ${Number(summary.represented_population).toLocaleString('en-US')}`);
    }
    lines.push(`- Screen result: ${crimeStatusLabel(summary.screen_status)}`);
    lines.push(`- Reporting completeness: ${summary.reporting_months == null ? 'unavailable' : `${summary.reporting_months} of 12 months`}`);
    (summary.issues || []).forEach((issue) => {
      lines.push(`- Caveat: ${crimeIssueLabels[issue] || issue}`);
    });
    lines.push('');
    lines.push('Important: the figures above are department-jurisdiction evidence for an entire municipality. They are not neighborhood, street-radius, or parcel crime data, and they should not be described as such. Where the city-wide numbers and the neighborhood differ, say so explicitly.');
    return lines.join('\n');
  };
  const copyCrimeResearchPrompt = async () => {
    const text = crimeResearchPrompt(activeCrimeSummary);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCrimePromptCopied(true);
      window.setTimeout(() => setCrimePromptCopied(false), 2000);
    } catch (_err) {
      setCrimePromptCopied(false);
    }
  };
  const crimeBody = (row) => {
    const status = row.crime_screen_status || 'unknown';
    return <strong className={`rcl-crime-screen rcl-crime-screen--${status}`} tabIndex="0" aria-label={`${crimeStatusLabel(status)} department-level crime evidence for ${row.formatted_address || 'property'}`} onMouseEnter={(event) => showCrimeInfo(event, row)} onMouseLeave={hideCrimeInfo} onFocus={(event) => showCrimeInfo(event, row)} onBlur={hideCrimeInfo}>
      {crimeStatusLabel(status)}
    </strong>;
  };
  const showBedroomSpaceInfo = (event) => bedroomSpaceInfo.current?.show(event);
  const hideBedroomSpaceInfo = () => bedroomSpaceInfo.current?.hide();
  const bedroomSpaceHeader = <span>Sq. ft. / bed</span>;
  const bedroomSpaceBody = (row) => {
    const signal = row.bedroom_space_signal || 'insufficient_data';
    return <span className={`rcl-sfbed rcl-sfbed--${signal}`} tabIndex="0" aria-label={`${compactNumber(row.square_feet_per_bedroom)} square feet per bedroom, ${bedroomSpaceLabels[signal] || bedroomSpaceLabels.insufficient_data}`} onMouseEnter={showBedroomSpaceInfo} onMouseLeave={hideBedroomSpaceInfo} onFocus={showBedroomSpaceInfo} onBlur={hideBedroomSpaceInfo}>
      <strong>{compactNumber(row.square_feet_per_bedroom)}</strong>
    </span>;
  };
  const potentialBedroomBody = (row) => {
    const range = potentialExtraBedroomRange(row.square_footage, row.bedrooms);
    return <strong className="rcl-potential-beds" aria-label={range ? `${range.label} potential extra bedrooms` : 'Potential extra bedrooms unavailable'}>{range?.label ?? '—'}</strong>;
  };
  const rawScenariosFor = (row, type) => type.startsWith('zillow_')
    ? row.zillow_comp_scenarios?.[type.replace('zillow_', '')] || []
    : row.comp_scenarios?.[type] || [];
  const sameScenarioLayout = (left, right) => Number(left?.bedrooms) === Number(right?.bedrooms)
    && (left?.bathrooms == null || right?.bathrooms == null || Number(left.bathrooms) === Number(right.bathrooms));
  const scenariosFor = (row, type) => {
    const scenarios = rawScenariosFor(row, type);
    const pairedType = type.startsWith('zillow_')
      ? (type === 'zillow_rent' ? 'zillow_arv' : 'zillow_rent')
      : (type === 'rent' ? 'arv' : 'rent');
    const pairedScenarios = rawScenariosFor(row, pairedType);
    const pairable = scenarios.filter((scenario) => pairedScenarios.some((paired) => sameScenarioLayout(scenario, paired)));
    return pairable.length ? pairable : scenarios;
  };
  const scenarioSelectionKey = (row) => row.lead_id || row.observation_id;
  const selectedScenario = (row, type) => {
    const scenarios = scenariosFor(row, type);
    const selectedId = scenarioSelections[scenarioSelectionKey(row)]?.[type];
    const explicitlySelected = scenarios.find((scenario) => scenario.id === selectedId);
    if (explicitlySelected) return explicitlySelected;
    // Saved Zillow picks are durable evidence. Restore their layout before
    // falling back to the provider's first scenario, whose ordering can change
    // after a new pull or timeframe refresh.
    if (type.startsWith('zillow_')) {
      const prefix = `${row.lead_id}:${type}:`;
      const savedLayout = scenarios.find((scenario) => (
        (selectedZillowComps[`${prefix}${layoutKeyPart(scenario.bedrooms)}:${layoutKeyPart(scenario.bathrooms)}`] || []).length > 0
      ));
      if (savedLayout) return savedLayout;
    }
    return scenarios[0] || null;
  };
  const reviewedCompKey = (row, type, scenario = selectedScenario(row, type)) => `${row.lead_id}:${type}:${layoutKeyPart(scenario?.bedrooms)}:${layoutKeyPart(scenario?.bathrooms)}`;
  const reviewedMembers = (row, type) => selectedZillowComps[reviewedCompKey(row, type)] || [];
  const reviewedScenario = (row, type) => {
    const members = reviewedMembers(row, type);
    if (!members.length) return null;
    const amountKey = type === 'zillow_rent' ? 'asking_rent_monthly' : 'sold_price';
    const values = members.map((member) => Number(member[amountKey])).filter(Number.isFinite);
    const source = selectedScenario(row, type);
    return values.length ? { id: `reviewed-${reviewedCompKey(row, type)}`, bedrooms: source?.bedrooms, bathrooms: source?.bathrooms, low: Math.min(...values), high: Math.max(...values), comparable_count: values.length, member_ids: members.map((member) => member.comp_rental_id || member.comp_sale_id) } : null;
  };
  const toggleReviewedComp = async (row, type, comp) => {
    const key = reviewedCompKey(row, type);
    const id = comp.comp_rental_id || comp.comp_sale_id;
    const existing = selectedZillowComps[key] || [];
    const nextMembers = existing.some((item) => (item.comp_rental_id || item.comp_sale_id) === id)
      ? existing.filter((item) => (item.comp_rental_id || item.comp_sale_id) !== id)
      : [...existing, comp];
    const scenario = selectedScenario(row, type);
    try {
      const result = await apiJson(`/api-lab/leads/${row.lead_id}/selected-comps/${type === 'zillow_rent' ? 'rent' : 'sold'}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bedrooms: scenario?.bedrooms,
          bathrooms: scenario?.bathrooms,
          target_living_area_sqft: row.square_footage,
          member_ids: nextMembers.map((item) => item.comp_rental_id || item.comp_sale_id),
          expected_revision: selectedZillowCompRevisions[key] || 0,
        }),
      });
      setSelectedZillowComps((current) => ({ ...current, [key]: result.selection?.members || [] }));
      setSelectedZillowCompRevisions((current) => ({ ...current, [key]: Number(result.selection?.revision || 0) }));
    } catch (err) {
      reviewToast.current?.show({ severity: 'error', summary: 'Selection not saved', detail: err.message || 'Reload and try again.', life: 5000 });
    }
  };
  const reviewedScenarioBody = (row, type) => {
    const scenario = reviewedScenario(row, type);
    if (!scenario) return <span className="rcl-comp-empty">—</span>;
    const isRent = type === 'zillow_rent';
    return <div className="rcl-zillow-sold-cell"><div className="rcl-scenario-chip"><span>{scenarioLabel(scenario)}</span><strong>{moneyRange(scenario, !isRent)}</strong><small>{scenario.comparable_count} selected</small></div><button type="button" className="rcl-sold-comp-viewer-button" onClick={() => { const selectedLead = { ...row, selected_comp_scenario: scenario }; if (isRent) setRentalCompLead(selectedLead); else setSoldCompLead(selectedLead); }} aria-label={`Review selected Zillow ${isRent ? 'rent' : 'sold'} comps for ${row.formatted_address}`}><i className="pi pi-images" aria-hidden="true" /></button></div>;
  };
  const createOrOpenUnderwriting = async () => {
    const row = selectedObservations[0];
    if (!row || selectedObservations.length !== 1) return;
    const existingId = underwritingCases[row.lead_id] || underwritingCases[row.observation_id];
    if (existingId) { navigate(`/lead-underwriting/${existingId}`); return; }
    setUnderwritingBusy(true); setError('');
    try {
      const created = await apiJson('/underwriting-cases', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          observation_id: row.observation_id,
          rent_api_request_id: selectedScenario(row, 'rent')?.id || null,
          arv_api_request_id: selectedScenario(row, 'arv')?.id || null,
        }),
      });
      setUnderwritingCases((current) => ({ ...current, [row.observation_id]: created.id, [row.lead_id]: created.id }));
      navigate(`/lead-underwriting/${created.id}`);
    } catch (err) { setError(err.message || 'Could not create underwriting case.'); }
    finally { setUnderwritingBusy(false); }
  };
  const openPortfolioDialog = () => {
    const row = selectedObservations[0];
    if (!row) return;
    const propertyId = portfolioProperties[row.lead_id];
    if (propertyId) { navigate(`/properties/${propertyId}`); return; }
    setPortfolioLead(row); setPortfolioPurchaseDate(''); setPortfolioNickname(''); setPortfolioClosed(false); setPortfolioError('');
  };
  const addToPortfolio = async () => {
    if (!portfolioLead) return;
    setPortfolioBusy(true); setPortfolioError('');
    try {
      const result = await apiJson('/property-promotions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ observation_id: portfolioLead.observation_id, purchase_date: portfolioPurchaseDate, acquisition_closed: portfolioClosed, nickname: portfolioNickname.trim() || null }),
      });
      setPortfolioProperties((current) => ({ ...current, [portfolioLead.lead_id]: result.property_id }));
      setPortfolioLead(null);
      navigate(`/properties/${result.property_id}`);
    } catch (err) { setPortfolioError(err.message || 'Could not add this property to the portfolio.'); }
    finally { setPortfolioBusy(false); }
  };
  const openScenarioMenu = (event, row, type) => {
    setActiveScenarioMenu({ row, type });
    scenarioMenu.current?.show(event);
  };
  const chooseScenario = (row, type, scenarioId) => {
    const selected = scenariosFor(row, type).find((scenario) => scenario.id === scenarioId);
    const isZillow = type.startsWith('zillow_');
    const pairedType = isZillow
      ? (type === 'zillow_rent' ? 'zillow_arv' : 'zillow_rent')
      : (type === 'rent' ? 'arv' : 'rent');
    const paired = selected && scenariosFor(row, pairedType).find((scenario) => sameScenarioLayout(scenario, selected));
    const selectionKey = scenarioSelectionKey(row);
    setScenarioSelections((current) => ({
      ...current,
      [selectionKey]: {
        ...current[selectionKey], [type]: scenarioId,
        ...(paired ? { [pairedType]: paired.id } : {}),
      },
    }));
    scenarioMenu.current?.hide();
  };
  const scenarioBody = (row, type) => {
    const scenario = selectedScenario(row, type);
    const baseType = type.replace('zillow_', '');
    const failure = type.startsWith('zillow_') ? null : row.comp_failures?.[type];
    if (type === 'zillow_arv' && zillowSoldNonDisclosureStates.has(row.state_abbr)) return <span className="rcl-sold-comp-status" title="RealtyAPI Zillow closing prices are not a reliable comp source in this state.">Non-disclosure</span>;
    if (!scenario && failure) return <button type="button" className="rcl-comp-failure" aria-label={compFailureAriaLabel(type, failure)} onMouseEnter={(event) => { setActiveCompFailure({ ...failure, type }); compFailureInfo.current?.show(event); }} onMouseLeave={() => compFailureInfo.current?.hide()} onFocus={(event) => { setActiveCompFailure({ ...failure, type }); compFailureInfo.current?.show(event); }} onBlur={() => compFailureInfo.current?.hide()}>
      <i className="pi pi-exclamation-circle" aria-hidden="true" />No comps
    </button>;
    if (!scenario) return type.startsWith('zillow_') ? <span className="rcl-sold-comp-status">{type === 'zillow_rent' ? 'No rent data' : 'No comps'}</span> : <span className="rcl-comp-empty">—</span>;
    const scenarios = scenariosFor(row, type);
    const scenarioButton = <button type="button" className="rcl-scenario-chip" onClick={(event) => openScenarioMenu(event, row, type)} aria-label={`Choose ${type.startsWith('zillow_') ? 'Zillow ' : 'RentCast '}${baseType === 'rent' ? 'rent' : 'comp'} scenario for ${row.formatted_address}`}>
      <span>{scenarioLabel(scenario)}</span>
      <strong>{moneyRange(scenario, baseType === 'arv')}</strong>
      {scenarios.length > 1 && <i className="pi pi-chevron-down" aria-hidden="true" />}
      <small>{scenario.comparable_count == null ? 'Comps' : `${scenario.comparable_count} comp${scenario.comparable_count === 1 ? '' : 's'}`}</small>
    </button>;
    if (!type.startsWith('zillow_')) return scenarioButton;
    const isRentalComp = type === 'zillow_rent';
    return <div className="rcl-zillow-sold-cell">
      {scenarioButton}
      <button type="button" className="rcl-sold-comp-viewer-button" onClick={() => {
        const selectedLead = { ...row, selected_comp_scenario: scenario };
        if (isRentalComp) setRentalCompLead(selectedLead); else setSoldCompLead(selectedLead);
      }} title={`View Zillow ${isRentalComp ? 'rent' : 'sold'} comps`} aria-label={`View Zillow ${isRentalComp ? 'rent' : 'sold'} comps for ${row.formatted_address}`}>
        <i className="pi pi-images" aria-hidden="true" />
      </button>
    </div>;
  };
  const ratioBody = (row, source) => {
    const rentScenario = source === 'zillow' ? reviewedScenario(row, 'zillow_rent') || selectedScenario(row, 'zillow_rent') : selectedScenario(row, 'rent');
    const arvScenario = source === 'zillow' ? reviewedScenario(row, 'zillow_arv') || selectedScenario(row, 'zillow_arv') : selectedScenario(row, 'arv');
    const range = rentArvRatioRange(rentScenario, arvScenario);
    if (!range) return <span className="rcl-comp-empty rcl-ratio-trigger" tabIndex="0" aria-label={`${source === 'zillow' ? 'Zillow' : 'RentCast'} rent to ARV formula`} onMouseEnter={(event) => showRentArvInfo(event, row, source)} onMouseLeave={hideRentArvInfo} onFocus={(event) => showRentArvInfo(event, row, source)} onBlur={hideRentArvInfo}>—</span>;
    const results = pairedRentArvResults(rentScenario, arvScenario);
    const mixed = results[0].tone !== results[1].tone;
    return <strong className={`rcl-ratio-range rcl-ratio-trigger${mixed ? ' is-crossing' : results[0].tone === 'positive' ? ' is-one-percent' : ''}`} tabIndex="0" aria-label={`${source === 'zillow' ? 'Zillow' : 'RentCast'}: ${results[0].label} ${results[0].formatted}; ${results[1].label} ${results[1].formatted}`} onMouseEnter={(event) => showRentArvInfo(event, row, source)} onMouseLeave={hideRentArvInfo} onFocus={(event) => showRentArvInfo(event, row, source)} onBlur={hideRentArvInfo}>
      <span className={`rcl-ratio-result rcl-ratio-result--${results[0].tone}`}>{results[0].formatted}</span><span className="rcl-ratio-separator">–</span><span className={`rcl-ratio-result rcl-ratio-result--${results[1].tone}`}>{results[1].formatted}</span>
    </strong>;
  };
  const showRentArvInfo = (event, row, source) => {
    const rentScenario = source === 'zillow' ? reviewedScenario(row, 'zillow_rent') || selectedScenario(row, 'zillow_rent') : selectedScenario(row, 'rent');
    const arvScenario = source === 'zillow' ? reviewedScenario(row, 'zillow_arv') || selectedScenario(row, 'zillow_arv') : selectedScenario(row, 'arv');
    setActiveRentArv({ source, rentScenario, arvScenario, range: rentArvRatioRange(rentScenario, arvScenario) });
    rentArvInfo.current?.show(event);
  };
  const hideRentArvInfo = () => rentArvInfo.current?.hide();
  const rentArvHeader = (source) => <span>{source} 1% Rule</span>;
  const rehabArvScenario = (row) => reviewedScenario(row, 'zillow_arv')
    || selectedScenario(row, 'zillow_arv')
    || selectedScenario(row, 'arv');
  const rehabOfferPrice = (row) => effectiveOfferPrice(row);
  const rowFixedCosts = (row) => row.fixed_costs || data?.fixed_costs || {};
  const showMaxRehabInfo = (event, row) => {
    const arvScenario = rehabArvScenario(row);
    const purchasePrice = rehabOfferPrice(row);
    const holdingCosts = holdingCostTotal(row.holding_costs);
    const fixedCosts = fixedCostTotal(rowFixedCosts(row));
    setActiveMaxRehab({
      range: maxRehabRange(arvScenario, purchasePrice, holdingCosts + fixedCosts),
      purchasePrice, arvScenario,
      holdingCosts, fixedCosts,
    });
    maxRehabInfo.current?.show(event);
  };
  const hideMaxRehabInfo = () => maxRehabInfo.current?.hide();
  const maxRehabBody = (row) => {
    const range = maxRehabRange(rehabArvScenario(row), rehabOfferPrice(row), holdingCostTotal(row.holding_costs) + fixedCostTotal(rowFixedCosts(row)));
    const label = range == null ? 'Max rehab formula; selected sold or RentCast ARV comps required' : `Max rehab range ${moneyRange(range)}`;
    return <strong className={`rcl-max-rehab${range != null && range.high < 0 ? ' is-negative' : ''}`} tabIndex="0" aria-label={label} onMouseEnter={(event) => showMaxRehabInfo(event, row)} onMouseLeave={hideMaxRehabInfo} onFocus={(event) => showMaxRehabInfo(event, row)} onBlur={hideMaxRehabInfo}>
      {range == null ? '—' : moneyRange(range, true)}
    </strong>;
  };
  const showRehabPerSqftInfo = (event, row) => {
    const allowance = maxRehabRange(rehabArvScenario(row), rehabOfferPrice(row), holdingCostTotal(row.holding_costs) + fixedCostTotal(rowFixedCosts(row)))?.low ?? null;
    setActiveRehabPerSqft({
      allowance,
      squareFootage: Number(row.square_footage),
      perSqft: maxRehabPerSqft(allowance, row.square_footage),
    });
    rehabPerSqftInfo.current?.show(event);
  };
  const hideRehabPerSqftInfo = () => rehabPerSqftInfo.current?.hide();
  const rehabPerSqftBody = (row) => {
    const allowance = maxRehabRange(rehabArvScenario(row), rehabOfferPrice(row), holdingCostTotal(row.holding_costs) + fixedCostTotal(rowFixedCosts(row)))?.low ?? null;
    const perSqft = maxRehabPerSqft(allowance, row.square_footage);
    const tier = rehabPerSqftTier(perSqft);
    const label = perSqft == null ? 'Rehab per square foot formula; ARV comps and square footage required' : `Rehab per square foot ${moneyPerSquareFoot(perSqft)}`;
    return <strong className={`rcl-rehab-sqft${tier ? ` rcl-rehab-sqft--${tier}` : ''}`} tabIndex="0" aria-label={label} onMouseEnter={(event) => showRehabPerSqftInfo(event, row)} onMouseLeave={hideRehabPerSqftInfo} onFocus={(event) => showRehabPerSqftInfo(event, row)} onBlur={hideRehabPerSqftInfo}>
      {perSqft == null ? '—' : `${moneyPerSquareFoot(perSqft)}/sq ft`}
    </strong>;
  };
  const openHoldingCosts = (row) => {
    const current = row.holding_costs || {};
    setHoldingCostsRow(row);
    setHoldingCostsDraft(Object.fromEntries([
      ...HOLDING_COST_OPTIONS.map(([key]) => [key, Number(current[key]) || 0]),
      ['notes', current.notes || ''],
    ]));
    setHoldingCostsError('');
  };
  const saveHoldingCosts = async () => {
    if (!holdingCostsRow) return;
    setHoldingCostsSaving(true); setHoldingCostsError('');
    try {
      const saved = await apiJson(`/api-lab/leads/${holdingCostsRow.lead_id}/holding-costs`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(holdingCostsDraft),
      });
      setData((current) => ({ ...current, observations: (current?.observations || []).map((row) => (
        row.lead_id === holdingCostsRow.lead_id ? { ...row, holding_costs: saved } : row
      )) }));
      setHoldingCostsRow(null);
    } catch (err) { setHoldingCostsError(err.message); }
    finally { setHoldingCostsSaving(false); }
  };
  const holdingCostsBody = (row) => {
    const total = holdingCostTotal(row.holding_costs);
    return <button type="button" className="rcl-cost-value" onClick={(event) => { event.stopPropagation(); openHoldingCosts(row); }} aria-label={`Edit holding costs for ${row.formatted_address || 'property'}; total ${money(total)}`}><span>{money(total)}</span><i className="pi pi-pencil" aria-hidden="true" /></button>;
  };
  const saveOfferPrice = async (offerRow, offerPrice) => {
    const url = `/api-lab/leads/${offerRow.lead_id}/purchase-price`;
    const saved = offerPrice == null
      ? (await apiJson(url, { method: 'DELETE' }), null)
      : await apiJson(url, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ offer_price: offerPrice }),
      });
    setData((currentData) => ({ ...currentData, observations: (currentData?.observations || []).map((row) => (
      row.lead_id === offerRow.lead_id ? { ...row, offer: saved } : row
    )) }));
    return saved;
  };
  const offerPriceBody = (row) => <InlineOfferPrice row={row} value={rehabOfferPrice(row)} askingPrice={row.asking_price} hasOffer={row.offer?.offer_price != null} onSave={saveOfferPrice} />;
  const openFixedCosts = (row) => {
    const current = rowFixedCosts(row);
    setFixedCostsDraft(Object.fromEntries([...FIXED_COST_OPTIONS.map(([key]) => [key, Number(current[key]) || 0]), ['notes', current.notes || '']]));
    setFixedCostsError(''); setFixedCostsOpen(true);
  };
  const saveFixedCosts = async () => {
    setFixedCostsSaving(true); setFixedCostsError('');
    try {
      const saved = await apiJson('/api-lab/fixed-costs', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fixedCostsDraft) });
      setData((current) => ({ ...current, fixed_costs: saved }));
      setFixedCostsOpen(false);
    } catch (err) { setFixedCostsError(err.message); }
    finally { setFixedCostsSaving(false); }
  };
  const fixedCostsBody = (row) => {
    const total = fixedCostTotal(rowFixedCosts(row));
    return <button type="button" className="rcl-cost-value" onClick={(event) => { event.stopPropagation(); openFixedCosts(row); }} aria-label={`Edit shared fixed costs; total ${money(total)}`}><span>{money(total)}</span><i className="pi pi-pencil" aria-hidden="true" /></button>;
  };
  // Cash flow is an underwriting result, so broad provider result ranges must
  // never become its inputs. Require the operator-curated Selected Rent and
  // Selected Sold sets for the active bed/bath layout.
  const cashFlowCompRent = (row) => reviewedScenario(row, 'zillow_rent');
  const cashFlowCompSold = (row) => reviewedScenario(row, 'zillow_arv');
  const cashFlowSelectedScenarios = (row) => {
    const rent = cashFlowCompRent(row);
    const sold = cashFlowCompSold(row);
    return rent && sold && sameScenarioLayout(rent, sold) ? { rent, sold } : null;
  };
  const cashFlowCompRentSource = (row) => cashFlowCompRent(row) ? 'realtyapi_zillow' : null;
  const cashFlowRentScenario = (row, assumptions = row.cash_flow_assumptions) => cashFlowRentAdjustment(assumptions) || cashFlowCompRent(row);
  const cashFlowCalculation = (row) => {
    const selected = cashFlowSelectedScenarios(row);
    if (!selected) return null;
    return projectedCashFlowRange(
      cashFlowRentAdjustment(row.cash_flow_assumptions) || selected.rent,
      selected.sold,
      data?.cash_flow_defaults,
      row.cash_flow_assumptions,
    );
  };
  const openCashFlow = (row) => {
    const defaults = data?.cash_flow_defaults || {};
    const property = row.cash_flow_assumptions || {};
    setCashFlowRow(row);
    setCashFlowDefaultsDraft(Object.fromEntries(CASH_FLOW_DEFAULT_OPTIONS.map(([key]) => (
      [key, defaults[key] == null ? CASH_FLOW_DEFAULT_VALUES[key] : Number(defaults[key])]
    ))));
    setCashFlowPropertyDraft(Object.fromEntries([
      ['expected_loan_amount', property.expected_loan_amount ?? null],
      ['rent_override_low', property.rent_override_low ?? null],
      ['rent_override_high', property.rent_override_high ?? null],
      ...CASH_FLOW_PROPERTY_OPTIONS.map(([key]) => [key, Number(property[key]) || 0]),
      ['property_tax_source', property.property_tax_source || null],
      ['property_tax_year', property.property_tax_year || null],
      ['provider_property_tax_amount', property.provider_property_tax_amount ?? (property.property_tax_source === 'rentcast' ? property.annual_property_taxes : null)],
      ['provider_property_tax_source', property.provider_property_tax_source || (property.property_tax_source === 'rentcast' ? 'rentcast' : null)],
      ['provider_property_tax_year', property.provider_property_tax_year || property.property_tax_year || null],
      ['restore_provider_property_tax', false],
      ['notes', property.notes || ''],
    ]));
    setCashFlowError('');
  };
  const saveCashFlow = async () => {
    if (!cashFlowRow) return;
    setCashFlowSaving(true); setCashFlowError('');
    try {
      const [savedDefaults, savedProperty] = await Promise.all([
        apiJson('/api-lab/cash-flow-defaults', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cashFlowDefaultsDraft) }),
        apiJson(`/api-lab/leads/${cashFlowRow.lead_id}/cash-flow-assumptions`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cashFlowPropertyDraft) }),
      ]);
      setData((current) => ({
        ...current,
        cash_flow_defaults: savedDefaults,
        observations: (current?.observations || []).map((row) => row.lead_id === cashFlowRow.lead_id ? { ...row, cash_flow_assumptions: savedProperty } : row),
      }));
      setCashFlowRow(null);
    } catch (err) { setCashFlowError(err.message); }
    finally { setCashFlowSaving(false); }
  };
  // Store only which row is open; every number is derived at render time from
  // current state so the tooltip follows ARV, comp, and assumption changes.
  const showCashFlowInfo = (event, row) => {
    setActiveCashFlow({ leadId: row.lead_id, observationId: row.observation_id, row });
    cashFlowInfo.current?.show(event);
  };
  const cashFlowInfoView = (() => {
    if (!activeCashFlow) return null;
    const observations = data?.observations || [];
    const row = observations.find((item) => activeCashFlow.observationId != null && item.observation_id === activeCashFlow.observationId)
      || observations.find((item) => item.lead_id === activeCashFlow.leadId)
      || activeCashFlow.row;
    const arvScenario = cashFlowCompSold(row);
    const defaults = data?.cash_flow_defaults || {};
    const assumptions = row.cash_flow_assumptions || {};
    return {
      calculation: cashFlowCalculation(row),
      rentScenario: cashFlowRentScenario(row),
      rentAdjusted: Boolean(cashFlowRentAdjustment(assumptions)),
      rentSource: cashFlowCompRentSource(row),
      propertyTaxSource: assumptions.property_tax_source || null,
      arvScenario, defaults,
      loan: loanSizingSteps(arvScenario, defaults, assumptions),
    };
  })();
  const cashFlowBody = (row) => {
    const calculation = cashFlowCalculation(row);
    const label = calculation ? `Projected monthly cash flow ${moneyRange(calculation)}` : 'Projected cash flow; Selected Rent and Selected Sold required';
    return <button type="button" className={`rcl-cash-flow${calculation?.high < 0 ? ' is-negative' : calculation?.low >= 0 ? ' is-positive' : ' is-mixed'}`} onClick={(event) => { event.stopPropagation(); openCashFlow(row); }} onMouseEnter={(event) => showCashFlowInfo(event, row)} onMouseLeave={() => cashFlowInfo.current?.hide()} onFocus={(event) => showCashFlowInfo(event, row)} onBlur={() => cashFlowInfo.current?.hide()} aria-label={`${label}; edit assumptions`}>
      <span>{calculation ? `${moneyRange(calculation, true)}/mo` : '—'}</span><i className="pi pi-pencil" aria-hidden="true" />
    </button>;
  };
  // Link the searched region straight to its research page when one exists; the
  // link has always pointed at the region, not a county.
  const regionBody = (row) => {
    const name = row.searched_regions?.[0] || row.searched_region_name || row.market_name;
    return <div className="rcl-searched-regions">
      {name && row.market_slug
        ? <Link to={researchMarketPath(row.market_slug)} aria-label={`View research for ${name}`} title={row.searched_regions?.join(', ') || name}>{name}</Link>
        : <span>{name || 'Region unavailable'}</span>}
    </div>;
  };
  const loadPhotoAlbum = async (row) => {
    const leadId = String(row.lead_id);
    if (photoAlbums[leadId]) return photoAlbums[leadId];
    if (photoAlbumRequests.current[leadId]) return photoAlbumRequests.current[leadId];
    const previewUrls = Array.isArray(row.preview_photo_urls) && row.preview_photo_urls.length
      ? row.preview_photo_urls
      : row.primary_photo_url ? [row.primary_photo_url] : [];
    const initial = previewUrls.map((sourceUrl) => ({ source_url: sourceUrl }));
    const observationId = row.photo_observation_id;
    if (!observationId) return initial;
    const pending = apiJson(`/api-lab/observations/${observationId}/photos`)
      .then((response) => {
        const photos = Array.isArray(response.photos) ? response.photos : initial;
        setPhotoAlbums((current) => ({ ...current, [leadId]: photos }));
        return photos;
      })
      .catch(() => {
        setPhotoAlbums((current) => ({ ...current, [leadId]: initial }));
        return initial;
      })
      .finally(() => { delete photoAlbumRequests.current[leadId]; });
    photoAlbumRequests.current[leadId] = pending;
    return pending;
  };
  const movePhoto = async (event, row, direction) => {
    event.stopPropagation();
    const leadId = String(row.lead_id);
    const previewUrls = Array.isArray(row.preview_photo_urls) && row.preview_photo_urls.length
      ? row.preview_photo_urls
      : row.primary_photo_url ? [row.primary_photo_url] : [];
    const previewPhotos = previewUrls.map((sourceUrl) => ({ source_url: sourceUrl }));
    const currentIndex = photoIndexes[leadId] || 0;
    const previewTarget = currentIndex + direction;
    const canUsePreview = !photoAlbums[leadId]
      && previewTarget >= 0
      && previewTarget < previewPhotos.length;
    if (canUsePreview) {
      setPhotoIndexes((current) => ({ ...current, [leadId]: previewTarget }));
      return;
    }
    const photos = await loadPhotoAlbum(row);
    if (!photos.length) return;
    setPhotoIndexes((current) => {
      const next = ((current[leadId] || 0) + direction + photos.length) % photos.length;
      return { ...current, [leadId]: next };
    });
  };
  const openPhotoGallery = async (event, row) => {
    event.stopPropagation();
    const photos = await loadPhotoAlbum(row);
    if (!photos.length) return;
    const leadId = String(row.lead_id);
    setPhotoGallery({ row, photos, index: Math.min(photoIndexes[leadId] || 0, photos.length - 1) });
  };
  const photoBody = (row) => {
    const leadId = String(row.lead_id);
    const previewUrls = Array.isArray(row.preview_photo_urls) && row.preview_photo_urls.length
      ? row.preview_photo_urls
      : row.primary_photo_url ? [row.primary_photo_url] : [];
    const photos = photoAlbums[leadId] || previewUrls.map((sourceUrl) => ({ source_url: sourceUrl }));
    const count = Number(row.photo_count || photos.length || 0);
    if (!photos.length) return <span className="rcl-photo-empty">No photos</span>;
    const index = Math.min(photoIndexes[leadId] || 0, photos.length - 1);
    const photo = photos[index];
    const photoFailed = failedPhotoUrls[photo.source_url];
    return <div className="rcl-photo-cell">
      <button type="button" className="rcl-photo-cell__arrow" onClick={(event) => movePhoto(event, row, -1)} aria-label={`Previous photo of ${row.formatted_address}`} disabled={count < 2}><i className="pi pi-chevron-left" aria-hidden="true" /></button>
      <button type="button" className={`rcl-photo-cell__image${photoFailed ? ' is-broken' : ''}`} onClick={(event) => openPhotoGallery(event, row)} aria-label={`Open photos of ${row.formatted_address}`}>
        {photoFailed ? <span className="rcl-photo-cell__unavailable"><i className="pi pi-image" aria-hidden="true" />Unavailable</span> : <img src={photo.source_url} alt={`${row.formatted_address || 'Property'} view ${index + 1}`} loading="lazy" onLoad={() => { const nextUrl = photos[index + 1]?.source_url; if (nextUrl) { const nextImage = new Image(); nextImage.src = nextUrl; } }} onError={() => setFailedPhotoUrls((current) => ({ ...current, [photo.source_url]: true }))} />}
        {count > 1 && <span>{index + 1} / {count}</span>}
      </button>
      <button type="button" className="rcl-photo-cell__arrow" onClick={(event) => movePhoto(event, row, 1)} aria-label={`Next photo of ${row.formatted_address}`} disabled={count < 2}><i className="pi pi-chevron-right" aria-hidden="true" /></button>
    </div>;
  };
  // Miles from the searched market's centroid. Zillow's rent-to-price signal
  // describes the metro, so a lead far from it inherits less of that evidence.
  const distanceHeader = <span title="Miles from the searched market centre">Distance</span>;
  const distanceBody = (row) => {
    const miles = Number(row.distance_from_market_miles);
    if (row.distance_from_market_miles == null || !Number.isFinite(miles)) return <span className="rcl-comp-empty">—</span>;
    return <span className="rcl-distance">{miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi</span>;
  };
  const addressBody = (row) => <div className="rcl-address">
    {row.formatted_address ? <a href={googlePropertySearchUrl(row.formatted_address)} target="_blank" rel="noopener noreferrer" aria-label={`Search Google for ${row.formatted_address}`}>{row.formatted_address}</a> : <strong>Address unavailable</strong>}
    <small>
      <time dateTime={row.requested_at || undefined} title={row.requested_at || 'Pull date unavailable'}>Pulled {formatPulledDate(row.requested_at)}</time>
      {Number(row.observation_count || 1) > 1 && <span className="rcl-repeat-meta" title={`Found in ${row.observation_count} saved search results across ${row.searched_region_count || row.searched_regions?.length || 1} regions`}>{row.observation_count} Dupes</span>}
      {row.listing_type && <span className="rcl-listing-type-meta">{displayLabel(row.listing_type)}</span>}
    </small>
  </div>;
  const sortableObservations = useMemo(() => visibleObservations.map((row) => {
    const rentScenario = selectedScenario(row, 'rent');
    const arvScenario = selectedScenario(row, 'arv');
    const zillowRentScenario = selectedScenario(row, 'zillow_rent');
    const zillowArvScenario = selectedScenario(row, 'zillow_arv');
    const selectedZillowRent = reviewedScenario(row, 'zillow_rent');
    const selectedZillowSold = reviewedScenario(row, 'zillow_arv');
    const ratio = rentArvRatioRange(rentScenario, arvScenario);
    const holdingCosts = holdingCostTotal(row.holding_costs);
    const fixedCosts = fixedCostTotal(rowFixedCosts(row));
    const selectedSoldOrRentCastArv = selectedZillowSold || arvScenario;
    const purchasePrice = rehabOfferPrice(row);
    const maxRehab = maxRehabRange(selectedSoldOrRentCastArv, purchasePrice, holdingCosts + fixedCosts);
    const cashFlow = selectedZillowRent && selectedZillowSold && sameScenarioLayout(selectedZillowRent, selectedZillowSold)
      ? projectedCashFlowRange(cashFlowRentAdjustment(row.cash_flow_assumptions) || selectedZillowRent, selectedZillowSold, data?.cash_flow_defaults, row.cash_flow_assumptions)
      : null;
    const potential = potentialExtraBedroomRange(row.square_footage, row.bedrooms);
    return { ...row,
      selected_rent_low: rentScenario?.low == null ? null : Number(rentScenario.low),
      selected_arv_low: arvScenario?.low == null ? null : Number(arvScenario.low),
      selected_rent_arv: ratio?.lowRentLowArv ?? null,
      selected_zillow_rent_low: zillowRentScenario?.low == null ? null : Number(zillowRentScenario.low),
      selected_zillow_arv_low: zillowArvScenario?.low == null ? null : Number(zillowArvScenario.low),
      reviewed_zillow_rent_low: selectedZillowRent?.low == null ? null : Number(selectedZillowRent.low),
      reviewed_zillow_sold_low: selectedZillowSold?.low == null ? null : Number(selectedZillowSold.low),
      selected_zillow_rent_arv: rentArvRatioRange(selectedZillowRent || zillowRentScenario, selectedZillowSold || zillowArvScenario)?.lowRentLowArv ?? null,
      projected_cash_flow: cashFlow?.low ?? null,
      offer_price_assumption: purchasePrice,
      holding_cost_total: holdingCosts,
      fixed_cost_total: fixedCosts,
      selected_max_rehab: maxRehab?.low ?? null,
      selected_max_rehab_high: maxRehab?.high ?? null,
      selected_rehab_per_sqft: maxRehabPerSqft(maxRehab?.low, row.square_footage),
      potential_extra_bedrooms: potential?.high ?? null,
    };
  }), [visibleObservations, scenarioSelections, selectedZillowComps, data?.fixed_costs, data?.cash_flow_defaults]); // eslint-disable-line react-hooks/exhaustive-deps
  const resultsDataTable = useMemo(() => (
    <DataTable className="rcl-data-table" value={sortableObservations} dataKey="lead_id" cellMemo={false} selection={selectedObservations} onSelectionChange={(event) => setSelectedObservations(event.value)} expandedRows={expandedRows} onRowToggle={toggleExpandedRows} rowExpansionTemplate={rawExpansion} rowClassName={(row) => ({ 'rcl-row--passed': row.review_status === 'passed', 'rcl-row--interested': row.review_status === 'interested' })} paginator first={tableFirst} rows={tableRows} onPage={(event) => { setTableFirst(event.first); setTableRows(event.rows); }} rowsPerPageOptions={[10,25,50,100]} paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink" currentPageReportTemplate="{first}–{last} of {totalRecords}" stripedRows removableSort sortMode="single" size="small" emptyMessage="No saved property API listings match these filters." tableStyle={{ minWidth: '2180px' }}>
      <Column selectionMode="multiple" headerStyle={{ width: '34px' }} headerClassName="rcl-select-column" className="rcl-select-column" />
      <Column expander className="rcl-expand-column" />
      <Column field="review_status" header="Review" body={reviewStatusBody} sortable className="rcl-review-column" />
      <Column header="API" className="rcl-api-icon-column" body={(row) => {
        const sources = aggregateProviderPulls(row);
        return <div className="rcl-api-icons" aria-label={`Saved source history: ${sources.map(({ label, count }) => `${label}, ${count} pull${count === 1 ? '' : 's'}`).join('; ')}`}>{sources.map(({ sourceKey, iconKey, label, count }) => {
          const pullLabel = `${label}: Pulled ${count} time${count === 1 ? '' : 's'}`;
          return <span key={sourceKey} className={`rcl-api-icon rcl-api-icon--${iconKey}`} role="img" aria-label={pullLabel} title={pullLabel}><img src={providerLogoUrls[iconKey]} alt="" />{count > 1 && <b aria-hidden="true">×{count}</b>}</span>;
        })}</div>;
      }} />
      <Column field="searched_region_name" header="Searched" sortable className="rcl-region-column" body={regionBody} />
      <Column header="Photos" className="rcl-photo-column" body={photoBody} />
      <Column field="formatted_address" header="Property address" sortable className="rcl-address-column" body={addressBody} />
      <Column field="property_type" header="Type" sortable className="rcl-type-column" body={(row) => <span className="rcl-type-pill">{displayLabel(row.property_type) || 'Unknown'}</span>} />
      <Column field="asking_price" header="Price" body={(row) => <strong className="rcl-price">{money(row.asking_price)}</strong>} sortable className="rcl-number-column" />
      <Column field="days_on_market" header={<span title="Days on market">DOM</span>} body={(row) => compactNumber(row.days_on_market)} sortable className="rcl-number-column rcl-dom-column" />
      <Column field="year_built" header={<span title="Year built">Built</span>} body={(row) => compactNumber(row.year_built)} sortable className="rcl-number-column rcl-year-column" />
      <Column field="bedrooms" header="Beds" body={(row) => compactNumber(row.bedrooms)} sortable className="rcl-number-column rcl-small-column" />
      <Column field="bathrooms" header="Baths" body={(row) => compactNumber(row.bathrooms)} sortable className="rcl-number-column rcl-small-column" />
      <Column field="square_footage" header="Sq. ft." body={(row) => compactNumber(row.square_footage)} sortable className="rcl-number-column" />
      <Column field="price_per_square_foot" header="$ / Sq. ft." body={(row) => <strong className="rcl-price-per-sqft">{moneyPerSquareFoot(row.price_per_square_foot)}</strong>} sortable className="rcl-number-column rcl-price-sqft-column" />
      <Column field="square_feet_per_bedroom" header={bedroomSpaceHeader} body={bedroomSpaceBody} sortable className="rcl-sfbed-column" />
      <Column field="potential_extra_bedrooms" header="Extra Beds" body={potentialBedroomBody} sortable className="rcl-potential-beds-column" />
      <Column field="distance_from_market_miles" header={distanceHeader} body={distanceBody} sortable className="rcl-number-column rcl-distance-column" />
      <Column field="selected_rent_low" header="RentCast Rent" body={(row) => scenarioBody(row, 'rent')} sortable className="rcl-comp-column" />
      <Column field="selected_arv_low" header="RentCast ARV" body={(row) => scenarioBody(row, 'arv')} sortable className="rcl-comp-column" />
      <Column field="selected_rent_arv" header={rentArvHeader('RentCast')} body={(row) => ratioBody(row, 'rentcast')} sortable className="rcl-ratio-column" />
      <Column field="selected_zillow_rent_low" header="Zillow Rent" body={(row) => scenarioBody(row, 'zillow_rent')} sortable className="rcl-comp-column" />
      <Column field="reviewed_zillow_rent_low" header="Selected Rent" body={(row) => reviewedScenarioBody(row, 'zillow_rent')} sortable className="rcl-comp-column" />
      <Column field="selected_zillow_arv_low" header="Zillow Sold" body={(row) => scenarioBody(row, 'zillow_arv')} sortable className="rcl-comp-column" />
      <Column field="reviewed_zillow_sold_low" header="Selected Sold" body={(row) => reviewedScenarioBody(row, 'zillow_arv')} sortable className="rcl-comp-column" />
      <Column field="selected_zillow_rent_arv" header={rentArvHeader('Zillow')} body={(row) => ratioBody(row, 'zillow')} sortable className="rcl-ratio-column" />
      <Column field="projected_cash_flow" header="Projected Cash Flow" body={cashFlowBody} sortable className="rcl-cash-flow-column" />
      <Column field="offer_price_assumption" header="Offer Price" body={offerPriceBody} sortable className="rcl-fixed-costs-column" />
      <Column field="fixed_cost_total" header="Fixed Costs" body={fixedCostsBody} sortable className="rcl-fixed-costs-column" />
      <Column field="holding_cost_total" header="Holding Costs" body={holdingCostsBody} sortable className="rcl-fixed-costs-column" />
      <Column field="selected_max_rehab" header="Max Rehab" body={maxRehabBody} sortable className="rcl-max-rehab-column" />
      <Column field="selected_rehab_per_sqft" header="Rehab / Sq Ft" body={rehabPerSqftBody} sortable className="rcl-rehab-sqft-column" />
      <Column field="crime_screen_rank" header="Crime" body={crimeBody} sortable className="rcl-crime-screen-column" />
    </DataTable>
  ), [sortableObservations, selectedObservations, expandedRows, tableFirst, tableRows, sourceEvidence, openEvidenceEntries, compRequestPayloads, evidencePageSelections, scenarioSelections, selectedZillowComps, reviewBusyLeadId, photoAlbums, photoIndexes, failedPhotoUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared cash-flow default inputs; Virtual Assistant and Internet render under operating costs.
  const renderCashFlowDefaultField = ([key, label, type]) => {
    // onChange fires per keystroke so the preview below updates while typing.
    const setDefault = (event) => setCashFlowDefaultsDraft((current) => ({ ...current, [key]: event.value ?? 0 }));
    if (type === 'term') {
      const months = Number(cashFlowDefaultsDraft.loan_term_months) || 0;
      const setMonths = (value) => setCashFlowDefaultsDraft((current) => ({ ...current, loan_term_months: value }));
      return <label key={key}><span>{label}</span><span className="rcl-loan-term-inputs">
        <InputNumber value={loanTermYears(months)} min={0} max={50} mode="decimal" locale="en-US" suffix=" yrs" maxFractionDigits={2} aria-label="Loan term in years" onChange={(event) => setMonths(loanTermFromYears(event.value))} onValueChange={(event) => setMonths(loanTermFromYears(event.value))} />
        <InputNumber value={months} min={0} max={600} mode="decimal" locale="en-US" suffix=" mo" useGrouping={false} maxFractionDigits={0} aria-label="Loan term in months" onChange={(event) => setMonths(event.value ?? 0)} onValueChange={(event) => setMonths(event.value ?? 0)} />
      </span></label>;
    }
    return <label key={key}><span>{label}</span><InputNumber value={Number(cashFlowDefaultsDraft[key]) || 0} min={0} max={type === 'percent' ? 100 : 9999999999.99} mode={type === 'currency' ? 'currency' : 'decimal'} currency={type === 'currency' ? 'USD' : undefined} locale="en-US" suffix={type === 'percent' ? '%' : undefined} minFractionDigits={type === 'currency' ? 2 : 0} maxFractionDigits={type === 'percent' ? 3 : 2} onChange={setDefault} onValueChange={setDefault} /></label>;
  };

  if (!data) return <PageLoader />;

  return <main className="rcl-page">
    <Toast ref={reviewToast} position="top-right" className="rcl-review-toast" />
    <Dialog visible={Boolean(photoGallery)} onHide={() => setPhotoGallery(null)} modal dismissableMask className="rcl-photo-gallery" header={photoGallery?.row?.formatted_address || 'Property photos'}>
      {photoGallery && <div className="rcl-photo-gallery__content">
        <button type="button" onClick={() => setPhotoGallery((current) => ({ ...current, index: (current.index - 1 + current.photos.length) % current.photos.length }))} disabled={photoGallery.photos.length < 2} aria-label="Previous large property photo"><i className="pi pi-chevron-left" aria-hidden="true" /></button>
        <figure>
          <img src={photoGallery.photos[photoGallery.index].source_url} alt={`${photoGallery.row.formatted_address || 'Property'} enlarged view ${photoGallery.index + 1}`} />
          <figcaption>{photoGallery.index + 1} of {photoGallery.photos.length}</figcaption>
        </figure>
        <button type="button" onClick={() => setPhotoGallery((current) => ({ ...current, index: (current.index + 1) % current.photos.length }))} disabled={photoGallery.photos.length < 2} aria-label="Next large property photo"><i className="pi pi-chevron-right" aria-hidden="true" /></button>
      </div>}
    </Dialog>
    <Dialog header="Holding Costs" visible={Boolean(holdingCostsRow)} onHide={() => !holdingCostsSaving && setHoldingCostsRow(null)} className="rcl-fixed-costs-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask={!holdingCostsSaving}>
      <p className="rcl-fixed-costs-dialog__intro">Enter the property-specific costs expected before refinance for <strong>{holdingCostsRow?.formatted_address}</strong>. The total is deducted from Max Rehab.</p>
      <div className="rcl-fixed-costs-grid">
        {HOLDING_COST_OPTIONS.map(([key, label]) => <label key={key}><span>{label}</span><InputNumber value={Number(holdingCostsDraft[key]) || 0} min={0} max={9999999999.99} mode="currency" currency="USD" locale="en-US" minFractionDigits={2} maxFractionDigits={2} onValueChange={(event) => setHoldingCostsDraft((current) => ({ ...current, [key]: event.value ?? 0 }))} /></label>)}
      </div>
      <label className="rcl-fixed-costs-notes"><span>Notes</span><InputTextarea value={holdingCostsDraft.notes || ''} maxLength={2000} rows={3} onChange={(event) => setHoldingCostsDraft((current) => ({ ...current, notes: event.target.value }))} /></label>
      <div className="rcl-fixed-costs-total"><span>Total holding costs</span><strong>{money(holdingCostTotal(holdingCostsDraft))}</strong></div>
      {holdingCostsError && <Message severity="error" text={holdingCostsError} />}
      <AppActionGroup className="rcl-billing-actions"><AppButton variant="text" label="Cancel" disabled={holdingCostsSaving} onClick={() => setHoldingCostsRow(null)} /><AppButton variant="primary" label="Save holding costs" icon="pi pi-check" loading={holdingCostsSaving} onClick={saveHoldingCosts} /></AppActionGroup>
    </Dialog>
    <Dialog header="Fixed Costs" visible={fixedCostsOpen} onHide={() => !fixedCostsSaving && setFixedCostsOpen(false)} className="rcl-fixed-costs-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask={!fixedCostsSaving}>
      <p className="rcl-fixed-costs-dialog__intro">These account-level costs apply to every property. Saving them updates the Fixed Costs value for every lead in your table.</p>
      <div className="rcl-fixed-costs-grid">
        {FIXED_COST_OPTIONS.map(([key, label]) => <label key={key}><span>{label}</span><InputNumber value={Number(fixedCostsDraft[key]) || 0} min={0} max={9999999999.99} mode="currency" currency="USD" locale="en-US" minFractionDigits={2} maxFractionDigits={2} onValueChange={(event) => setFixedCostsDraft((current) => ({ ...current, [key]: event.value ?? 0 }))} /></label>)}
      </div>
      <label className="rcl-fixed-costs-notes"><span>Notes</span><InputTextarea value={fixedCostsDraft.notes || ''} maxLength={2000} rows={3} onChange={(event) => setFixedCostsDraft((current) => ({ ...current, notes: event.target.value }))} /></label>
      <div className="rcl-fixed-costs-total"><span>Total fixed costs for every lead</span><strong>{money(fixedCostTotal(fixedCostsDraft))}</strong></div>
      {fixedCostsError && <Message severity="error" text={fixedCostsError} />}
      <AppActionGroup className="rcl-billing-actions"><AppButton variant="text" label="Cancel" disabled={fixedCostsSaving} onClick={() => setFixedCostsOpen(false)} /><AppButton variant="primary" label="Save for every lead" icon="pi pi-check" loading={fixedCostsSaving} onClick={saveFixedCosts} /></AppActionGroup>
    </Dialog>
    <Dialog header="Projected Cash Flow" visible={Boolean(cashFlowRow)} onHide={() => !cashFlowSaving && setCashFlowRow(null)} className="rcl-cash-flow-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask={!cashFlowSaving}>
      <p className="rcl-fixed-costs-dialog__intro rcl-cash-flow-dialog__intro">Estimate stabilized monthly cash flow after refinance for <strong>{cashFlowRow?.formatted_address}</strong>. Shared defaults update every lead; property expenses apply only here.</p>
      <section className="rcl-cash-flow-section"><h3>Shared refinance and reserve defaults</h3><div className="rcl-fixed-costs-grid">
        {CASH_FLOW_DEFAULT_OPTIONS.filter(([key]) => !CASH_FLOW_SHARED_OPERATING_KEYS.includes(key)).map(renderCashFlowDefaultField)}
      </div></section>
      <section className="rcl-cash-flow-section"><h3 className="rcl-source-label">Rent for this projection{cashFlowRow && !cashFlowRentAdjustment(cashFlowPropertyDraft) && cashFlowCompRentSource(cashFlowRow) === 'rentcast' ? <ProviderSourceMark providerKey="rentcast" /> : null}</h3><div className="rcl-fixed-costs-grid">
        {(() => {
          const comps = cashFlowRow ? cashFlowCompRent(cashFlowRow) : null;
          const compsText = comps ? `Comps: ${moneyRange(comps)}` : 'No comp rent yet';
          const setRent = (key) => (event) => setCashFlowPropertyDraft((current) => ({ ...current, [key]: event.value || null }));
          return <>
            <label><span>Rent (single value or low)</span><InputNumber value={cashFlowPropertyDraft.rent_override_low ?? null} min={0} max={9999999999.99} mode="currency" currency="USD" locale="en-US" maxFractionDigits={2} placeholder={compsText} onChange={setRent('rent_override_low')} onValueChange={setRent('rent_override_low')} /></label>
            <label><span>Rent high (optional)</span><InputNumber value={cashFlowPropertyDraft.rent_override_high ?? null} min={0} max={9999999999.99} mode="currency" currency="USD" locale="en-US" maxFractionDigits={2} placeholder="Leave blank for one value" onChange={setRent('rent_override_high')} onValueChange={setRent('rent_override_high')} /></label>
          </>;
        })()}
      </div></section>
      <section className="rcl-cash-flow-section"><h3>Operating costs</h3><div className="rcl-fixed-costs-grid">
        {CASH_FLOW_DEFAULT_OPTIONS.filter(([key]) => CASH_FLOW_SHARED_OPERATING_KEYS.includes(key)).map(([key, label, type]) => renderCashFlowDefaultField([key, `${label} · all properties`, type]))}
        <label><span>Expected loan amount override</span><InputNumber value={cashFlowPropertyDraft.expected_loan_amount} min={0} max={9999999999.99} mode="currency" currency="USD" locale="en-US" minFractionDigits={2} maxFractionDigits={2} placeholder="Use ARV × LTV" onChange={(event) => setCashFlowPropertyDraft((current) => ({ ...current, expected_loan_amount: event.value }))} onValueChange={(event) => setCashFlowPropertyDraft((current) => ({ ...current, expected_loan_amount: event.value }))} /></label>
        {CASH_FLOW_PROPERTY_OPTIONS.map(([key, label]) => {
          const rentCastTax = key === 'annual_property_taxes' && cashFlowPropertyDraft.provider_property_tax_source === 'rentcast';
          const taxTitle = rentCastTax ? `From RentCast${cashFlowPropertyDraft.provider_property_tax_year ? ` · tax year ${cashFlowPropertyDraft.provider_property_tax_year}` : ''}. Editing marks it manual; click to restore ${money(cashFlowPropertyDraft.provider_property_tax_amount)}.` : '';
          const setAmount = (event) => setCashFlowPropertyDraft((current) => ({
            ...current, [key]: event.value ?? 0,
            ...(key === 'annual_property_taxes' ? { property_tax_source: 'manual', property_tax_year: null, restore_provider_property_tax: false } : {}),
          }));
          return <label key={key}><span className="rcl-source-label">{label}{rentCastTax ? <button type="button" className="rcl-source-mark-button" title={taxTitle} aria-label={taxTitle} onClick={() => setCashFlowPropertyDraft((current) => ({ ...current, annual_property_taxes: Number(current.provider_property_tax_amount) || 0, property_tax_source: 'rentcast', property_tax_year: current.provider_property_tax_year, restore_provider_property_tax: true }))}><ProviderSourceMark providerKey="rentcast" title={taxTitle} /></button> : null}</span><InputNumber value={Number(cashFlowPropertyDraft[key]) || 0} min={0} max={9999999999.99} mode="currency" currency="USD" locale="en-US" minFractionDigits={2} maxFractionDigits={2} onChange={setAmount} onValueChange={setAmount} /></label>;
        })}
      </div></section>
      <label className="rcl-fixed-costs-notes"><span>Notes</span><InputTextarea value={cashFlowPropertyDraft.notes || ''} maxLength={2000} rows={3} onChange={(event) => setCashFlowPropertyDraft((current) => ({ ...current, notes: event.target.value }))} /></label>
      {(() => {
        const selected = cashFlowRow ? cashFlowSelectedScenarios(cashFlowRow) : null;
        const preview = selected
          ? projectedCashFlowRange(cashFlowRentAdjustment(cashFlowPropertyDraft) || selected.rent, selected.sold, cashFlowDefaultsDraft, cashFlowPropertyDraft)
          : null;
        const missingTerm = !(Number(cashFlowDefaultsDraft.loan_term_months) > 0);
        return <>
          <div className="rcl-fixed-costs-total"><span>Projected monthly cash flow</span><strong>{preview ? moneyRange(preview) : missingTerm ? 'Enter a loan term' : 'Needs rent and ARV evidence'}</strong></div>
          <CashFlowMathTable calculation={preview} ltvPct={cashFlowDefaultsDraft.refi_ltv_pct} rentAdjusted={Boolean(cashFlowRentAdjustment(cashFlowPropertyDraft))} rentSource={cashFlowRow ? cashFlowCompRentSource(cashFlowRow) : null} propertyTaxSource={cashFlowPropertyDraft.property_tax_source} />
        </>;
      })()}
      {cashFlowError && <Message severity="error" text={cashFlowError} />}
      <AppActionGroup className="rcl-billing-actions rcl-cash-flow-dialog__actions"><AppButton variant="text" label="Cancel" disabled={cashFlowSaving} onClick={() => setCashFlowRow(null)} /><AppButton variant="primary" label="Save" loading={cashFlowSaving} onClick={saveCashFlow} /></AppActionGroup>
    </Dialog>
    <aside className="rcl-deal-criteria" aria-label="BRRRR criteria reminder">
      <span className="rcl-deal-criteria__title">BRRRR Criteria</span>
      <span
        className="rcl-deal-criteria__help"
        tabIndex="0"
        aria-label="Low headache area guidance"
        aria-describedby="rcl-low-headache-tooltip"
        onMouseEnter={(event) => lowHeadacheInfo.current?.show(event)}
        onMouseLeave={() => lowHeadacheInfo.current?.hide()}
        onFocus={(event) => lowHeadacheInfo.current?.show(event)}
        onBlur={() => lowHeadacheInfo.current?.hide()}
      ><strong>Low</strong> Headache Area</span>
      <span>Cash Flow <strong>+</strong></span>
      <span><strong>75%</strong> All In</span>
    </aside>
    <ResearchRegionsWorkspace payload={researchPayload} loading={researchLoading} error={researchError} showSourceAudit sourceAuditKeys={API_LAB_SOURCE_AUDIT_KEYS} showTitle={false} headerSupplement={researchHeaderSupplement} selectedMarketIds={form.market_ids} onSelectedMarketIdsChange={(marketIds) => update('market_ids', marketIds)} selectedSearchTargets={selectedSearchTargets} onSelectedSearchTargetsChange={updateSelectedSearchTargets} onRunSearchTarget={runFromMapTooltip} getMarketSelectionId={getRentcastMarketSelectionId} isMarketSelectable={isRentcastMarketSelectable} getMarketDisabledReason={() => 'Geography review required before this market can be used in API Lab.'} selectionPurpose="API Lab" selectionLimit={data.max_requests_per_run} collapsible />

    {error && <Message severity="error" text={error} />}
    {runInProgress && <div className="rcl-run-progress" role="status" aria-live="polite"><PageLoader compact label="Property API pull in progress" /><div><strong>Property API pull in progress</strong><span>Requesting listings, preserving every full source response, and preparing the leads and charts…</span></div></div>}
    <div className={`rcl-top-layout rcl-top-layout--chart-only${chartCollapsed ? ' rcl-top-layout--chart-collapsed' : ''}`}>
    <section className={`rcl-chart${chartCollapsed ? ' rcl-chart--collapsed' : ''}`}><div className="rcl-chart-head"><div className="rcl-chart-heading-line"><span>{chartView === 'usage' ? 'API REQUEST HISTORY' : 'REGION LEAD RESULTS'}</span>{chartView === 'usage' ? <small className="rcl-request-period-total">API REQUESTS · <b>{chartRequestTotal.toLocaleString()} total</b></small> : null}{latestRequestFailed && <small className="rcl-request-failure" title={data.latest_request.error_message || 'Latest request failed'}>Latest failed · {data.latest_request.status_code || 'error'}</small>}{latestRequestSucceeded && <small className="rcl-request-success" title={`Latest ${latestRequestProvider} request succeeded`}><i className="pi pi-check-circle" aria-hidden="true" />200</small>}
    {/* <h2>Requests used and records returned</h2>
    <p>Counts and credits come from the internal ledger—not a provider account-balance endpoint.</p> */}
    </div>
    <div className="rcl-chart-tools">{!chartCollapsed ? <><div className="rcl-chart-view-toggle" role="group" aria-label="Request history chart view"><button type="button" className={chartView === 'usage' ? 'is-active' : ''} onClick={() => setChartView('usage')}>API usage</button><button type="button" className={chartView === 'regions' ? 'is-active' : ''} onClick={() => setChartView('regions')}>Region results</button></div>{chartView === 'regions' ? <Dropdown className="rcl-region-chart-filter" panelClassName="rcl-period-panel" value={regionChartMarketId} options={regionChartOptions} onChange={(event) => setRegionChartMarketId(event.value)} aria-label="Filter region results chart" /> : null}<Dropdown className="rcl-chart-period" panelClassName="rcl-period-panel" value={chartDays} options={rangeOptions} onChange={(event) => setChartDays(event.value)} aria-label="Graph timeline" /></> : null}<button type="button" className="rcl-chart-collapse" onClick={() => setChartCollapsed((value) => !value)} aria-label={`${chartCollapsed ? 'Expand' : 'Collapse'} request history`}><i className={`pi pi-chevron-${chartCollapsed ? 'down' : 'up'}`} aria-hidden="true" /></button></div></div>{!chartCollapsed ? <>{latestRequestFailed && <div className="rcl-provider-error" role="alert"><i className="pi pi-exclamation-triangle" aria-hidden="true" /><strong>{latestRequestProvider} request failed</strong><code>{latestProviderError.error || `HTTP ${data.latest_request.status_code || 'error'}`}</code><span>{latestProviderError.message || data.latest_request.error_message || `${latestRequestProvider} did not return an error message.`}</span></div>}{chartView === 'regions' && regionHistory.length === 0 ? <div className="rcl-region-chart-empty">No region-level pulls are available in this period.</div> : <><div className="rcl-chart-canvas">{chartView === 'usage' ? <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#94a3b8' }, grid: { color: 'rgba(148,163,184,.12)' } }, y: { beginAtZero: true, ticks: { color: '#6ee7b7', precision: 0 }, grid: { color: 'rgba(148,163,184,.12)' } } } }} /> : <Bar data={regionResultsChartData} options={regionResultsChartOptions} plugins={[regionBarLabelsPlugin]} />}</div>{chartView === 'usage' ? <div className="rcl-chart-legend rcl-chart-legend--regions" aria-label="Chart legend">{usageChartLegend.map((item) => <span key={item.label}><i style={{ backgroundColor: item.color }} />{providerLogoUrls[item.providerKey] ? <img className="rcl-legend-logo" src={providerLogoUrls[item.providerKey]} alt="" title={item.label} /> : null}{item.label}</span>)}</div> : null}{chartView === 'regions' ? <div className="rcl-chart-legend rcl-chart-legend--regions" aria-label="Chart legend">{regionResultsLegend.map((item) => <span key={item.label}><i style={{ backgroundColor: item.color }} />{providerLogoUrls[item.providerKey] ? <img className="rcl-legend-logo" src={providerLogoUrls[item.providerKey]} alt="" title={providerLabels[item.providerKey] || item.providerKey} /> : null}{item.marketName || item.label}</span>)}<span><i className="is-duplicates" />Repeat observations</span></div> : null}</>}</> : null}</section>
    </div>

    <Dialog header={<span className="rcl-request-dialog__title"><span>{`Build Property API request · ${activeRequestTargets.length} selected`}</span>{requestTargetMode === 'region' && selectedSearchTargets.length > 0 ? <button type="button" onClick={deselectAllSearchTargets}>Deselect all</button> : null}</span>} aria-label={`Build Property API request · ${activeRequestTargets.length} selected`} visible={requestDialogOpen} onHide={() => setRequestDialogOpen(false)} className="rcl-request-dialog rcl-request-dialog--providers" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask>
      <div className="rcl-request-dialog__markets" aria-label="Selected Property API targets">{requestTargetMode === 'property' ? selectedObservations.map((row) => { const label = row.formatted_address || 'Address unavailable'; return <span key={row.observation_id}><span>{label}</span><button type="button" aria-label={`Remove ${label}`} title={`Remove ${label}`} onClick={() => deselectPropertyTarget(row)}>&times;</button></span>; }) : selectedRegions.map((region) => { const target = region.target || region; const label = propertySearchTargetLabel(target); return <span key={propertySearchTargetKey(target)}><span>{label}</span><button type="button" aria-label={`Remove ${label}`} title={`Remove ${label}`} onClick={() => deselectSearchRegion(region)}>&times;</button></span>; })}</div>
      {requestTargetMode === 'region' && selectedMarketLastPulls.length > 0 ? <div className="rcl-repeat-pull-warnings" aria-label="Recent pulls for selected regions">{selectedMarketLastPulls.map(({ market, pull }) => { const source = recentApiSourceLabel(pull); return <div key={market.id} title={`${market.name || market.display_label} | Most recent API: ${source} | Run ${formatPullRecency(pull.requested_at)} | ${Number(pull.result_count || 0).toLocaleString()} returned | ${Number(pull.request_count || 0).toLocaleString()} request${Number(pull.request_count || 0) === 1 ? '' : 's'}`}><i className="pi pi-history" aria-hidden="true" /><span><strong>{market.name || market.display_label}</strong> | Most recent API: <b>{source}</b> | Run {formatPullRecency(pull.requested_at)} | <b>{Number(pull.result_count || 0).toLocaleString()}</b> returned | {Number(pull.request_count || 0).toLocaleString()} request{Number(pull.request_count || 0) === 1 ? '' : 's'}</span></div>; })}</div> : null}
      {false && continuations.length > 0 && <div className="rcl-continuation-workflow" aria-label="Response pagination evidence">
        <h2 className="rcl-continuations__title">Continue RealtyAPI results?</h2>
        <div className="rcl-continuations__grid">{continuations.map((item) => {
          const evidence = item.pagination_evidence || {};
          const market = selectedMarkets.find((entry) => Number(entry.id) === Number(item.market_id));
          const inefficientLegacyPage = Number(evidence.request?.resultCount || 0) < 200;
          const completedPage = item.page || evidence.request?.page || 1;
          const returnedCount = Number(item.result_count || 0);
          const savedCount = Number(item.normalized_record_count ?? item.result_count ?? 0);
          const remainingPages = Number(evidence.estimated_remaining_pages || 1);
          const selectedPageCount = Math.max(1, Math.min(remainingPages, Number(continuationPageCounts[item.request_id] || 1)));
          const pageCountOptions = Array.from({ length: remainingPages }, (_, index) => {
            const pageCount = index + 1;
            if (pageCount === 1) return { label: 'Next page', value: pageCount };
            if (pageCount === remainingPages) return { label: `All remaining (${pageCount} pages)`, value: pageCount };
            return { label: `Next ${pageCount} pages`, value: pageCount };
          });
          return <article key={item.request_id} className="rcl-continuation-card">
            <div className="rcl-continuation-card__title">
              <strong>{item.market_name || market?.name || market?.display_label || 'Selected region'}</strong>
              <div className="rcl-continuation-card__stats" aria-label="Completed page results">
                <span>Page {Number(completedPage).toLocaleString()}</span>
                <span>{returnedCount.toLocaleString()} returned</span>
                <span>{savedCount.toLocaleString()} saved</span>
              </div>
            </div>
            <div className="rcl-api-evidence-row rcl-continuation-evidence">
              <div className="rcl-api-request">
                <div className="rcl-api-json__heading"><strong>Listing GET request · {item.market_name || market?.name || market?.display_label || 'Selected region'}</strong></div>
                <code>{item.endpoint}</code>
                <pre>{JSON.stringify(evidence.request || {}, null, 2)}</pre>
              </div>
              <div className="rcl-api-response">
                <strong>Listing response fields</strong>
                <code className="rcl-api-endpoint-spacer" aria-hidden="true">&nbsp;</code>
                <pre>{JSON.stringify(evidence.provider_response || {}, null, 2)}</pre>
              </div>
            </div>
            <div className="rcl-continuation-card__footer"><span>{evidence.estimated_remaining_pages == null ? 'Another page is available.' : `${remainingPages} page${remainingPages === 1 ? '' : 's'} remain.`}</span><div className="rcl-continuation-card__actions">{inefficientLegacyPage && <small className="rcl-continuation-legacy-status">1-record legacy request</small>}{!inefficientLegacyPage && <><label className="rcl-continuation-page-count"><span>Pages to load</span><Dropdown value={selectedPageCount} options={pageCountOptions} panelClassName="rcl-request-select-panel" onChange={(event) => setContinuationPageCounts((current) => ({ ...current, [item.request_id]: event.value }))} aria-label={`Pages to load for ${item.market_name || market?.name || market?.display_label || 'selected region'}`} /></label><Button className="rcl-continuation-action rcl-continuation-action--preview" label="Review request" icon="pi pi-eye" onClick={() => previewContinuation(item, selectedPageCount)} disabled={busy} /></>}<Button className="rcl-continuation-action rcl-continuation-action--stop" label="Stop" text onClick={() => stopContinuation(item)} disabled={busy} /></div></div>
          </article>;
        })}</div>
      </div>}
      <section className="rcl-composer" aria-label="API request composer">
        <div className="rcl-composer__selectors">
          <div className="rcl-field"><label>Provider</label><FastSelect value={composerProvider} options={composerProviderOptions} onChange={selectComposerProvider} ariaLabel="Request provider" /></div>
          <div className="rcl-field"><label className="rcl-composer__endpoint-label"><span>Endpoint</span><a href={composerProvider === 'realtyapi_zillow' && zillowEndpoint === 'search_bycoordinates' ? zillowCoordinatesDocumentationUrl : composerProvider === 'realtyapi_zillow' && composerPurpose === 'property_tax' ? zillowTaxDocumentationUrl : providerDocumentationUrls[composerProvider] || providerDocumentationUrls.realtyapi_zillow} target="_blank" rel="noopener noreferrer">API docs <i className="pi pi-external-link" aria-hidden="true" /></a></label><FastSelect value={composerPurposeOptions.some((option) => option.value === composerEndpointValue) ? composerEndpointValue : composerPurposeOptions[0]?.value} options={composerPurposeOptions} onChange={selectComposerEndpoint} ariaLabel="Request endpoint" />{composerEndpointDescription && <small className="rcl-composer__endpoint-help">{composerEndpointDescription}</small>}</div>
        </div>

        {composerProvider === 'rentcast' && requestTargetMode === 'region' && <div className="rcl-composer__fields rcl-builder-grid">
          <div className="rcl-field"><label>Radius (miles)</label><InputNumber value={form.radius} min={1} max={100} onValueChange={(event) => update('radius', event.value)} /></div>
          <div className="rcl-field"><label>Maximum requests</label><InputNumber value={form.request_cap} min={Math.max(1, selectedSearchTargets.length)} max={data.max_requests_per_run} onValueChange={(event) => update('request_cap', event.value)} /></div>
          <div className="rcl-field"><label>Asking price</label><InputText value={form.price} onChange={(event) => update('price', event.target.value)} /></div>
          <div className="rcl-field"><label>Results per request</label><InputNumber value={form.limit} min={1} max={500} onValueChange={(event) => update('limit', event.value)} /></div>
        </div>}

        {composerProvider === 'rentcast' && requestTargetMode === 'property' && selectedObservations.map((row) => {
          const draft = propertyApiDrafts[row.observation_id] || {};
          if (composerPurpose === 'property_tax') return null;
          return <div className="rcl-composer__fields rcl-builder-grid" key={row.observation_id} aria-label={`RentCast subject details for ${row.formatted_address}`}>
            <div className="rcl-field"><label>Bedrooms</label><InputNumber value={draft.bedrooms} min={0} max={20} onValueChange={(event) => updatePropertyApiDraft(row.observation_id, { bedrooms: event.value })} /></div>
            <div className="rcl-field"><label>Bathrooms</label><InputNumber value={draft.bathrooms} min={0} max={20} onValueChange={(event) => updatePropertyApiDraft(row.observation_id, { bathrooms: event.value })} /></div>
            <div className="rcl-field"><label>Square footage</label><InputNumber value={draft.square_footage} min={1} max={1000000} onValueChange={(event) => updatePropertyApiDraft(row.observation_id, { square_footage: event.value })} /></div>
            <div className="rcl-field"><label>Property type</label><FastSelect value={draft.property_type} options={rentCastPropertyTypes.map((value) => ({ label: value, value }))} onChange={(value) => updatePropertyApiDraft(row.observation_id, { property_type: value })} ariaLabel={`RentCast property type for ${row.formatted_address}`} /></div>
          </div>;
        })}

        {(composerProvider === 'realtyapi_zillow' || composerProvider === 'realtyapi_realtor') && composerPurpose !== 'property_tax' && <div className="rcl-composer__fields rcl-builder-grid">
          {composerProvider === 'realtyapi_zillow' && zillowEndpoint === 'search_bycoordinates' && requestTargetMode === 'property' && <div className="rcl-field"><label>Radius (miles)</label><InputNumber value={providerForms.realtyapi_zillow.radius} min={1} max={25} onValueChange={(event) => updateProviderForm('realtyapi_zillow', 'radius', event.value)} aria-label="Zillow search radius in miles" /></div>}
          {composerProvider === 'realtyapi_realtor' && <div className="rcl-field"><label>Radius (miles)</label><InputNumber value={providerForms.realtyapi_realtor.radius} min={1} max={25} onValueChange={(event) => updateProviderForm('realtyapi_realtor', 'radius', event.value)} /></div>}
          <div className="rcl-field"><label>Property type</label><FastSelect value={providerForms[composerProvider].property_type} options={(realtyApiPropertyTypes[composerProvider] || []).map(([label, value]) => ({ label, value }))} onChange={(value) => updateProviderForm(composerProvider, 'property_type', value)} ariaLabel="Property type" /></div>
          <div className="rcl-field"><label>{SEARCH_STATUSES.find((status) => status.key === composerPurpose)?.priceLabel || 'Price range'}</label><InputText value={providerForms[composerProvider].searches?.[composerPurpose]?.price || ''} placeholder="any" onChange={(event) => updateProviderSearch(composerProvider, composerPurpose, { price: event.target.value })} /></div>
        </div>}

        {composerProvider === 'zillapi' && <div className="rcl-composer__fields rcl-builder-grid">
          <div className="rcl-field"><label>Radius (miles)</label><InputNumber value={providerForms.zillapi.radius} min={1} max={25} onValueChange={(event) => updateProviderForm('zillapi', 'radius', event.value)} /></div>
          <div className="rcl-field"><label>Maximum properties</label><InputNumber value={providerForms.zillapi.result_limit} min={1} max={5} onValueChange={(event) => updateProviderForm('zillapi', 'result_limit', event.value)} /></div>
          <div className="rcl-field"><label>Asking price</label><InputText value={providerForms.zillapi.price} onChange={(event) => updateProviderForm('zillapi', 'price', event.target.value)} /></div>
          <div className="rcl-field"><label>Property type</label><FastSelect value={providerForms.zillapi.property_type} options={(realtyApiPropertyTypes.realtyapi_zillow || []).map(([label, value]) => ({ label, value }))} onChange={(value) => updateProviderForm('zillapi', 'property_type', value)} ariaLabel="Property type" /></div>
        </div>}

        <div className="rcl-composer__action-row">
          {composerProvider === 'rentcast' && requestTargetMode === 'region' && <Button className="rcl-optional-button" label="More filters" icon="pi pi-sliders-h" text onClick={openOptionalFilters} />}
          {(composerProvider === 'realtyapi_zillow' || composerProvider === 'realtyapi_realtor') && composerPurpose !== 'property_tax' && <Button className="rcl-optional-button" label="More options" icon="pi pi-sliders-h" text onClick={() => { setRealtyApiSource(composerProvider); setRequestDialogOpen(false); setRealtyOptionsStatus(composerPurpose); setRealtyOptionsOpen(true); }} />}
          {(composerProvider === 'realtyapi_zillow' || composerProvider === 'realtyapi_realtor') && composerPurpose === 'For_Sale' && requestTargetMode === 'region' && <Button className="rcl-optional-button rcl-schedule-button" label="Schedule" icon="pi pi-calendar" text onClick={toggleSchedulePanel} />}
          <Button className="rcl-composer__add" label="Add" icon="pi pi-plus" onClick={addComposerRequest} />
        </div>
        {schedulePanelOpen && <section className="rcl-schedule-panel" aria-label="Property API schedules">
          <div className="rcl-schedule-panel__form">
            <div className="rcl-field"><label>Schedule name</label><InputText value={scheduleDraft.name} placeholder={`${providerLabels[composerProvider]} regions`} onChange={(event) => setScheduleDraft((current) => ({ ...current, name: event.target.value }))} /></div>
            <div className="rcl-field"><label>Run every</label><FastSelect value={scheduleDraft.weeks} options={Array.from({ length: 8 }, (_, index) => ({ label: `${index + 1} week${index ? 's' : ''}`, value: index + 1 }))} onChange={(weeks) => setScheduleDraft((current) => ({ ...current, weeks }))} ariaLabel="Schedule interval" /></div>
            <AppButton variant="primary" label={editingSchedule ? 'Save changes' : 'Save & turn on'} icon="pi pi-check" loading={scheduleBusy} disabled={scheduleBusy} onClick={saveSchedule} />
            {editingSchedule && <AppButton variant="text" label="Cancel" onClick={() => { setEditingSchedule(null); setScheduleDraft({ name: '', weeks: 1 }); }} />}
          </div>
          <small className="rcl-schedule-panel__note">Uses the selected regions and this RealtyAPI For Sale search. Starts with page one and keeps 5 credits in reserve.</small>
          {scheduleError && <small className="rcl-schedule-panel__error" role="alert">{scheduleError}</small>}
          {schedules.length > 0 && <div className="rcl-schedule-list">{schedules.map((schedule) => <article className="rcl-schedule-card" key={schedule.schedule_id}>
            <button type="button" className={`rcl-schedule-switch${schedule.status === 'active' ? ' is-on' : ''}`} role="switch" aria-checked={schedule.status === 'active'} aria-label={`${schedule.status === 'active' ? 'Pause' : 'Turn on'} ${schedule.name}`} disabled={scheduleBusy} onClick={() => toggleSchedule(schedule)}><span /></button>
            <div><strong>{schedule.name}</strong><small>Every {Number(schedule.cadence_config?.weeks || 1)} week{Number(schedule.cadence_config?.weeks || 1) === 1 ? '' : 's'} · {schedule.status === 'active' ? 'On' : 'Paused'}</small></div>
            <button type="button" onClick={() => editSchedule(schedule)} disabled={scheduleBusy}>Edit</button>
            <button type="button" className="is-delete" onClick={() => deleteSchedule(schedule)} disabled={scheduleBusy}>Delete</button>
          </article>)}</div>}
        </section>}
      </section>

      {false && <div className="rcl-provider-builder rcl-provider-builder--legacy" aria-hidden="true" hidden>
        {(requestTargetMode === 'property' ? ['rentcast', 'realtyapi_realtor'] : ['rentcast', 'zillapi', 'realtyapi_realtor']).map((providerKey) => {
          const effectiveProviderKey = providerKey === 'realtyapi_realtor' ? realtyApiSource : providerKey;
          const provider = providerByKey.get(effectiveProviderKey);
          const propertyRentCastRequestCount = requestTargetMode === 'property'
            ? selectedObservations.reduce((total, row) => total + rentCastPropertyEndpoints.filter((option) => propertyApiDrafts[row.observation_id]?.[option.key]).length, 0)
            : 0;
          const providerConfig = providerKey === 'rentcast'
            ? { request_cap: requestTargetMode === 'property' ? propertyRentCastRequestCount : form.request_cap, result_limit: form.limit }
            : providerForms[effectiveProviderKey];
          const enabled = selectedProviders[providerKey];
          const projection = providerSearchProjection(effectiveProviderKey, enabled ? activeRequestTargets.length : 0, providerConfig, provider);
          const expanded = expandedProvider === providerKey;
          const providerContinuations = continuations.filter((item) => item.provider_key === effectiveProviderKey);
          return <section className={`rcl-provider-option${enabled ? ' is-selected' : ''}`} key={providerKey}>
            <div className="rcl-provider-option__head">
              <label><input type="checkbox" aria-label={`${providerLabels[providerKey]} API provider`} checked={enabled} onChange={() => toggleRequestProvider(providerKey)} /><span><strong>{providerLabels[providerKey]}</strong><a href={effectiveProviderKey === 'realtyapi_zillow' && zillowEndpoint === 'search_bycoordinates' ? zillowCoordinatesDocumentationUrl : effectiveProviderKey === 'realtyapi_zillow' && zillowEndpoint === 'taxinfo' ? zillowTaxDocumentationUrl : providerDocumentationUrls[effectiveProviderKey]} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} aria-label={`${providerLabels[effectiveProviderKey] || providerLabels[providerKey]} API Docs`}>API Docs <i className="pi pi-external-link" aria-hidden="true" /></a></span></label>
              <span className={`rcl-provider-option__estimate${projection.exceedsSafeBalance ? ' is-blocked' : ''}`}><strong>{projection.label}</strong><small>{projection.exceedsSafeBalance ? 'Above safe balance' : `${projection.safeAfter} safe after`}</small></span>
              <button type="button" aria-label={`${expanded ? 'Collapse' : 'Expand'} ${providerLabels[providerKey]} request settings`} onClick={() => setExpandedProvider(expanded ? null : providerKey)}><i className={`pi pi-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true" /></button>
            </div>
            {expanded && <div className="rcl-provider-option__body">
              {providerKey === 'rentcast' ? <>
                {requestTargetMode === 'property' ? <>
                  <div className="rcl-builder-grid">
                    <div className="rcl-field"><label>Endpoint</label><Dropdown className="rcl-request-select" value={propertyApiDrafts[selectedObservations[0]?.observation_id]?.rentcast_endpoint || 'rent_comps'} options={rentCastEndpointChoices} panelClassName="rcl-request-select-panel" onChange={(event) => {
                      const selection = rentCastEndpointChoices.find((option) => option.value === event.value) || rentCastEndpointChoices[0];
                      selectedObservations.forEach((row) => updatePropertyApiDraft(row.observation_id, {
                        rentcast_endpoint: selection.value,
                        ...Object.fromEntries(rentCastPropertyEndpoints.map((option) => [option.key, selection.keys.includes(option.key)])),
                      }));
                    }} aria-label="RentCast endpoint" /></div>
                  </div>
                  {selectedObservations.map((row) => {
                    const draft = propertyApiDrafts[row.observation_id] || {};
                    if (draft.rentcast_endpoint === 'property_tax') return null;
                    return <div className="rcl-builder-grid" key={row.observation_id} aria-label={`RentCast subject details for ${row.formatted_address}`}>
                      <div className="rcl-field"><label>Bedrooms</label><InputNumber value={draft.bedrooms} min={0} max={20} onValueChange={(event) => updatePropertyApiDraft(row.observation_id, { bedrooms: event.value })} /></div>
                      <div className="rcl-field"><label>Bathrooms</label><InputNumber value={draft.bathrooms} min={0} max={20} onValueChange={(event) => updatePropertyApiDraft(row.observation_id, { bathrooms: event.value })} /></div>
                      <div className="rcl-field"><label>Square footage</label><InputNumber value={draft.square_footage} min={1} max={1000000} onValueChange={(event) => updatePropertyApiDraft(row.observation_id, { square_footage: event.value })} /></div>
                      <div className="rcl-field"><label>Property type</label><Dropdown className="rcl-request-select" value={draft.property_type} options={rentCastPropertyTypes.map((value) => ({ label: value, value }))} panelClassName="rcl-request-select-panel" onChange={(event) => updatePropertyApiDraft(row.observation_id, { property_type: event.value })} aria-label={`RentCast property type for ${row.formatted_address}`} /></div>
                    </div>;
                  })}
                </> : <><div className="rcl-builder-grid">
                  <div className="rcl-field"><label>Radius (miles)</label><InputNumber value={form.radius} min={1} max={100} onValueChange={(e) => update('radius', e.value)} /></div>
                  <div className="rcl-field"><label>Maximum requests</label><InputNumber value={form.request_cap} min={Math.max(1, selectedSearchTargets.length)} max={data.max_requests_per_run} onValueChange={(e) => update('request_cap', e.value)} /></div>
                  <div className="rcl-field"><label>Price range</label><InputText value={form.price} onChange={(e) => update('price', e.target.value)} /></div>
                  <div className="rcl-field"><label>Results per request</label><InputNumber value={form.limit} min={1} max={500} onValueChange={(e) => update('limit', e.value)} /></div>
                </div>
                <Button className="rcl-optional-button" label="Optional RentCast filters" icon="pi pi-sliders-h" text onClick={openOptionalFilters} /></>}
              </> : <div className="rcl-builder-grid">
                {providerKey === 'realtyapi_realtor' && <>
                  <div className="rcl-field"><label>Source</label><Dropdown className="rcl-request-select" value={realtyApiSource} options={realtyApiSources} optionDisabled="disabled" panelClassName="rcl-request-select-panel" disabled={requestTargetMode === 'property'} onChange={(event) => { setPreview(null); setRealtyApiSource(event.value); }} aria-label="RealtyAPI source" /></div>
                  {(() => {
                    const endpointOptions = (realtyApiEndpoints[realtyApiSource] || []).filter((option) => requestTargetMode === 'property' || !option.propertyOnly);
                    const endpointValue = realtyApiSource === 'realtyapi_zillow' ? zillowEndpoint : endpointOptions[0]?.value;
                    return <div className="rcl-field"><label>Endpoint</label><Dropdown className="rcl-request-select" value={endpointValue} options={endpointOptions} panelClassName="rcl-request-select-panel" disabled={endpointOptions.length < 2} onChange={(event) => { setPreview(null); updateProviderForm('realtyapi_zillow', 'zillow_endpoint', event.value); }} aria-label="RealtyAPI endpoint" /></div>;
                  })()}
                </>}
                {effectiveProviderKey === 'realtyapi_zillow' && zillowEndpoint === 'search_bycoordinates' && <div className="rcl-field"><label>Radius (miles)</label><InputNumber value={providerForms.realtyapi_zillow.radius} min={1} max={25} onValueChange={(e) => { setPreview(null); updateProviderForm('realtyapi_zillow', 'radius', e.value); }} aria-label="Zillow search radius in miles" /></div>}
                {effectiveProviderKey !== 'realtyapi_zillow' && <div className="rcl-field"><label>Radius (miles)</label><InputNumber value={providerForms[effectiveProviderKey].radius} min={1} max={25} onValueChange={(e) => updateProviderForm(effectiveProviderKey, 'radius', e.value)} /></div>}
                {zillowEndpoint !== 'taxinfo' && <div className="rcl-field"><label>{providerKey === 'zillapi' ? 'Maximum properties per region' : 'Maximum results per page'}</label><InputNumber value={providerForms[effectiveProviderKey].result_limit} min={providerKey === 'zillapi' ? 1 : 200} max={providerKey === 'zillapi' ? 5 : 200} disabled={providerKey === 'realtyapi_realtor'} onValueChange={(e) => updateProviderForm(effectiveProviderKey, 'result_limit', e.value)} /></div>}
                {providerKey === 'zillapi' && <div className="rcl-field"><label>Price range</label><InputText value={providerForms[effectiveProviderKey].price} onChange={(e) => updateProviderForm(effectiveProviderKey, 'price', e.target.value)} /></div>}
                {zillowEndpoint !== 'taxinfo' && <div className="rcl-field"><label>Property type</label><Dropdown className="rcl-request-select" value={providerForms[effectiveProviderKey].property_type} options={(realtyApiPropertyTypes[effectiveProviderKey] || [['Single family','single_family']]).map(([label, value]) => ({ label, value }))} panelClassName="rcl-request-select-panel" onChange={(event) => updateProviderForm(effectiveProviderKey, 'property_type', event.value)} aria-label={`${providerLabels[effectiveProviderKey] || providerLabels[providerKey]} property type`} /></div>}
                {providerKey === 'realtyapi_realtor' && zillowEndpoint !== 'taxinfo' && <div className="rcl-searches">
                  {SEARCH_STATUSES.map((status) => {
                    const search = (providerForms[effectiveProviderKey].searches || defaultSearches())[status.key];
                    const blockedRegions = status.key === 'Sold'
                      ? selectedRegions.filter((region) => soldNonDisclosureStates.has(region.state_abbr))
                      : [];
                    const allBlocked = blockedRegions.length > 0 && blockedRegions.length === selectedRegions.length;
                    return <div className={`rcl-search-row${search.enabled ? ' is-on' : ''}`} key={status.key}>
                      <label className="rcl-search-row__toggle">
                        <input type="checkbox" checked={Boolean(search.enabled)} onChange={(event) => updateProviderSearch(effectiveProviderKey, status.key, { enabled: event.target.checked })} />
                        <span><strong>{status.label}</strong><small>{status.question}</small></span>
                      </label>
                      {/* Price and More options stay visible whether or not the status is ticked. */}
                      <div className="rcl-search-row__body">
                        <div className="rcl-search-row__price">
                          <label htmlFor={`price-${effectiveProviderKey}-${status.key}`}>{status.priceLabel} <em>{status.unit}</em></label>
                          <InputText id={`price-${effectiveProviderKey}-${status.key}`} value={search.price || ''} placeholder="any" onChange={(event) => updateProviderSearch(effectiveProviderKey, status.key, { price: event.target.value })} />
                        </div>
                        {status.priceHint && <small className="rcl-search-row__hint">{status.priceHint}</small>}
                        {blockedRegions.length > 0 && <small className="rcl-search-row__blocked"><i className="pi pi-ban" aria-hidden="true" />{allBlocked ? 'No sold prices disclosed in ' : 'Will skip '}{blockedRegions.map((target) => target.state_abbr).filter((value, index, all) => all.indexOf(value) === index).join(', ')} — no credit charged</small>}
                        <Button className="rcl-optional-button" label="More" icon="pi pi-sliders-h" text onClick={() => { setRequestDialogOpen(false); setRealtyOptionsStatus(status.key); setRealtyOptionsOpen(true); }} aria-label={`More ${status.label} options`} />
                      </div>
                    </div>;
                  })}
                </div>}
                {/* <small className="rcl-provider-canary-note"><i className="pi pi-shield" aria-hidden="true" />{providerKey === 'zillapi' ? 'Each returned property uses one credit. This value is the hard per-region credit ceiling.' : 'This authorizes page one only for each region. After it returns, actual pagination metadata is shown before another credit can be authorized.'}</small> */}
              </div>}
            </div>}
            {providerContinuations.length > 0 && <div className="rcl-provider-pagination" aria-label={`${providerLabels[effectiveProviderKey] || providerLabels[providerKey]} pagination from current request`}>
              <strong>More pages from the request you just ran</strong>
              {providerContinuations.map((item) => {
                const evidence = item.pagination_evidence || {};
                const remainingPages = Math.max(1, Number(evidence.estimated_remaining_pages || 1));
                const selectedPageCount = Math.max(1, Math.min(remainingPages, Number(continuationPageCounts[item.request_id] || 1)));
                const setPageCount = (value) => setContinuationPageCounts((current) => ({ ...current, [item.request_id]: Math.max(1, Math.min(remainingPages, Number(value) || 1)) }));
                const operationLabel = { sold_comp_search: 'Sold comps', rental_comp_search: 'Rental comps', listing_search: 'For-sale listings' }[item.operation_key] || 'Listing search';
                const regionLabel = item.market_name || 'Selected region';
                return <div className="rcl-provider-pagination__row" key={item.request_id}>
                  <div><b>{operationLabel} · {regionLabel}</b><span>Page {Number(item.page || evidence.request?.page || 1).toLocaleString()} returned {Number(item.result_count || 0).toLocaleString()} records · {remainingPages} page{remainingPages === 1 ? '' : 's'} remain</span></div>
                  <div className="rcl-continuation-page-count" aria-label={`Pages to load for ${operationLabel} in ${regionLabel}`}><span>Pages to load</span><button type="button" onClick={() => setPageCount(selectedPageCount - 1)} disabled={selectedPageCount <= 1} aria-label="Load fewer pages"><i className="pi pi-chevron-left" aria-hidden="true" /></button><InputNumber value={selectedPageCount} min={1} max={remainingPages} useGrouping={false} onValueChange={(event) => setPageCount(event.value)} aria-label={`Page count for ${operationLabel} in ${regionLabel}`} /><span>of {remainingPages}</span><button type="button" onClick={() => setPageCount(selectedPageCount + 1)} disabled={selectedPageCount >= remainingPages} aria-label="Load more pages"><i className="pi pi-chevron-right" aria-hidden="true" /></button></div>
                  <Button className="rcl-continuation-action rcl-continuation-action--preview" label="Review request" icon="pi pi-eye" onClick={() => previewContinuation(item, selectedPageCount)} disabled={busy} />
                  <Button className="rcl-continuation-action rcl-continuation-action--stop" label="Dismiss" text onClick={() => stopContinuation(item)} disabled={busy} />
                </div>;
              })}
            </div>}
          </section>;
        })}
      </div>}
      <section className="rcl-preview-shell" aria-label="Request URL preview">
        <header><span>Request URLs</span></header>
        {preview ? <div className="rcl-preview">{preview.providers.map((providerPreview) => <section key={providerPreview.provider_key}>{providerPreview.requests.map((item, index) => <Fragment key={`${providerPreview.provider_key}-${item.market.target_key || item.market.id}-${item.operation_key || index}-${item.endpoint}`}><div className="rcl-preview__request"><div className="rcl-preview__request-line"><b>{item.method}</b><code>{item.endpoint}{item.method === 'GET' ? `?${new URLSearchParams(item.parameters).toString()}` : ''}</code><button type="button" onClick={() => removeComposerRequest(providerPreview.provider_key, previewPurpose(item.operation_key), item)} aria-label={`Remove ${providerLabels[providerPreview.provider_key] || providerPreview.provider_key} ${item.method} ${new URL(item.endpoint).pathname}`} title="Remove endpoint"><i className="pi pi-times" aria-hidden="true" /></button></div>{item.method === 'POST' && <pre>{JSON.stringify(item.parameters, null, 2)}</pre>}</div>{item.warning && <div className="rcl-preview__warning" role="note"><i className="pi pi-exclamation-triangle" aria-hidden="true" /><span>{item.warning}</span></div>}</Fragment>)}{(providerPreview.skipped_requests || []).map((item, index) => { const isDisclosure = /disclose/i.test(item.reason || ''); return <div className={`rcl-preview__skip${isDisclosure ? ' is-blocked' : ''}`} key={`skip-${providerPreview.provider_key}-${item.market.id}-${item.operation_key || index}`}><i className={`pi ${isDisclosure ? 'pi-ban' : 'pi-check-circle'}`} aria-hidden="true" /><span><strong>{{ sold_comp_search: 'Sold comps', rental_comp_search: 'Rent comps', property_tax_lookup: 'Property tax' }[item.operation_key] || 'Search'} · {item.market.name}</strong>{isDisclosure ? '' : 'Using saved evidence · '}{item.reason} · no API credit</span></div>; })}</section>)}</div> : <p>{busy ? 'Preparing request URL…' : 'Add an endpoint to see its exact request URL here.'}</p>}
      </section>
      {missingProviderCredentials.length > 0 && <small className="rcl-note">Live action is disabled for: {missingProviderCredentials.map((key) => providerLabels[key]).join(', ')}. Credential values are never displayed.</small>}
      <AppActionGroup className="rcl-actions rcl-actions--review"><AppButton variant="outline" label="Run" icon="pi pi-play" onClick={runRequest} disabled={!preview?.execution_token || busy || missingProviderCredentials.length > 0} loading={busy} /></AppActionGroup>
    </Dialog>

    <Dialog header={`Confirm ${continuationPreview?.maximum_http_requests || 1} additional RealtyAPI page${continuationPreview?.maximum_http_requests === 1 ? '' : 's'}`} visible={continuationConfirmOpen} onHide={() => setContinuationConfirmOpen(false)} className="rcl-confirm-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask={!busy} closable={!busy}>
      {continuationPreview && <>
        <div className="rcl-continuation-confirm-copy"><strong>Actual pagination evidence</strong><p>The provider fields below came from the preserved response that made this continuation available.</p></div>
        <div className="rcl-continuation-json"><div><span>Provider response fields</span><pre>{JSON.stringify(continuationPreview.pagination_evidence?.provider_response || {}, null, 2)}</pre></div><div><span>Requests to run</span><pre>{JSON.stringify((continuationPreview.requests || [continuationPreview.request]).map((item) => item.parameters), null, 2)}</pre></div></div>
        <div className="rcl-confirm-provider-units"><span><strong>RealtyAPI</strong><b>{continuationPreview.maximum_provider_units} additional request credit{continuationPreview.maximum_provider_units === 1 ? '' : 's'}</b></span></div>
        <small className="rcl-confirm-note"><i className="pi pi-shield" aria-hidden="true" />Only the pages listed above are authorized. Every response is preserved separately in full.</small>
        <AppActionGroup className="rcl-confirm-actions"><AppButton variant="text" label="Cancel" onClick={() => setContinuationConfirmOpen(false)} /><AppButton variant="primary" label={`Load ${continuationPreview.maximum_http_requests} page${continuationPreview.maximum_http_requests === 1 ? '' : 's'}`} icon="pi pi-play" onClick={executeContinuation} /></AppActionGroup>
      </>}
    </Dialog>

    <Dialog header="Confirm Property API pull" visible={confirmationDialogOpen} onHide={() => setConfirmationDialogOpen(false)} className="rcl-confirm-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask={!busy} closable={!busy}>
       <div className="rcl-confirm-regions" aria-label="Targets in confirmed Property API pull">{requestTargetMode === 'property' ? selectedObservations.map((row) => <span key={row.observation_id}>{row.formatted_address}</span>) : selectedMarkets.map((market) => <span key={market.id}>{market.name || market.display_label}</span>)}</div>

      {/* <div className="rcl-confirm-hero"><span><i className="pi pi-bolt" aria-hidden="true" /></span><div><strong>Ready to request live listing data?</strong><p>RentCast may count each request against this account’s usage and overage limit.</p></div></div> */}
      <div className="rcl-confirm-summary">
        <div><span>{requestTargetMode === 'property' ? 'Selected properties' : 'Selected regions'}</span><strong>{activeRequestTargets.length}</strong></div>
        <div><span>Providers</span><strong>{preview?.provider_count || 0}</strong></div>
        <div><span>Maximum requests</span><strong>{(preview?.providers || []).reduce((total, item) => total + Number(item.maximum_http_requests || 0), 0)}</strong></div>
      </div>
      <div className="rcl-confirm-provider-units" aria-label="Maximum provider billing commitments">{(preview?.providers || []).map((item) => <span key={item.provider_key}><strong>{providerLabels[item.provider_key]}</strong><b>{Number(item.maximum_provider_units || 0)} {item.billing_unit_type === 'result' ? 'result credit' : 'request credit'}{Number(item.maximum_provider_units || 0) === 1 ? '' : 's'}</b></span>)}</div>
      {(preview?.providers || []).flatMap((item) => item.requests || []).filter((item) => item.warning).map((item, index) => <small className="rcl-confirm-note rcl-confirm-note--warning" role="note" key={`warning-${index}`}><i className="pi pi-exclamation-triangle" aria-hidden="true" />{item.warning}</small>)}
      <small className="rcl-confirm-note"><i className="pi pi-shield" aria-hidden="true" />This confirms only the requests shown above. RealtyAPI continuation requires a separate decision after the first response is visible.</small>
      {/* <div className="rcl-confirm-regions" aria-label="Regions in confirmed RentCast pull">{selectedMarkets.map((market) => <span key={market.id}>{market.name || market.display_label}</span>)}</div> */}
      {/* <small className="rcl-confirm-note"><i className="pi pi-shield" aria-hidden="true" />The pull stops before exceeding the saved account overage limit.</small> */}
      <AppActionGroup className="rcl-confirm-actions"><AppButton variant="text" label="Go back" onClick={() => setConfirmationDialogOpen(false)} /><AppButton variant="primary" label="Start provider pull" icon="pi pi-play" onClick={executeConfirmedRequest} /></AppActionGroup>
    </Dialog>

    <Dialog header="Optional RentCast filters" visible={optionalFiltersOpen} onHide={closeOptionalFilters} className="rcl-filter-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask>
      <p className="rcl-filter-dialog__intro">Leave these fields blank to keep the search broad. They only apply to the next preview or confirmed request.</p>
      <div className="rcl-option-grid">
        {[['bedrooms','Bedrooms'],['bathrooms','Bathrooms'],['square_footage','Square footage'],['year_built','Year built'],['days_old','Listing age (days)']].map(([key,label]) => <div className="rcl-field" key={key}><label>{label}</label><InputText value={form[key]} placeholder="Omitted" onChange={(e) => update(key, e.target.value)} /></div>)}
      </div>
      <AppActionGroup className="rcl-filter-dialog__actions"><AppButton variant="primary" label="Done" icon="pi pi-check" onClick={closeOptionalFilters} /></AppActionGroup>
    </Dialog>

    <Dialog header={`${SEARCH_STATUSES.find((status) => status.key === realtyOptionsStatus)?.label || 'Search'} · More options`} visible={realtyOptionsOpen} onHide={() => { setRealtyOptionsOpen(false); setRequestDialogOpen(true); }} className="rcl-filter-dialog rcl-realty-options-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask>
      <p className="rcl-filter-dialog__intro">Only populated options are sent, and only options the provider documents for this listing status are shown. Region, first page, price, and property type stay in the main form.</p>
      <div className="rcl-option-grid">
        {(realtyApiSource === 'realtyapi_zillow' ? zillowAdvancedOptions : realtorAdvancedOptions).filter(([key]) => realtyApiSource !== 'realtyapi_zillow' || optionAllowedForStatus(key, realtyOptionsStatus)).map(([key, label, options, type]) => {
          const value = (providerForms[realtyApiSource].searches || defaultSearches())[realtyOptionsStatus]?.advanced_parameters?.[key];
          return <div className={`rcl-field${type === 'boolean' ? ' rcl-field--boolean' : ''}`} key={key}>
            <label>{label}</label>
            {type === 'boolean'
              ? <FastSelect value={value ?? ''} options={[{ label: 'Omit', value: '' }, { label: 'Yes', value: true }, { label: 'No', value: false }]} onChange={(nextValue) => updateRealtyAdvancedOption(key, nextValue)} ariaLabel={label} />
              : options
                ? <FastSelect value={value ?? ''} options={[{ label: 'Omit', value: '' }, ...options.map((option) => ({ label: displayLabel(option), value: option }))]} onChange={(nextValue) => updateRealtyAdvancedOption(key, nextValue)} ariaLabel={label} />
                : <InputText value={value ?? ''} placeholder="Omit" onChange={(event) => updateRealtyAdvancedOption(key, event.target.value)} />}
          </div>;
        })}
      </div>
      <AppActionGroup className="rcl-filter-dialog__actions"><AppButton variant="primary" label="Done" icon="pi pi-check" onClick={() => { setRealtyOptionsOpen(false); setRequestDialogOpen(true); }} /></AppActionGroup>
    </Dialog>

    <Dialog header="RentCast plan" visible={planDialogOpen} onHide={() => setPlanDialogOpen(false)} className="rcl-billing-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask>
      <div className="rcl-billing-form">
        <label><span>Plan</span><Dropdown value={planDraft} options={planOptions} panelClassName="rcl-billing-plan-panel" onChange={(event) => setPlanDraft(event.value)} /></label>
        <label><span>Current usage</span><InputNumber value={usageDraft} min={0} max={100000000} useGrouping onValueChange={(event) => setUsageDraft(event.value ?? 0)} /></label>
        <small>Use the request count currently shown in RentCast. This becomes the reconciliation baseline for our internal ledger.</small>
      </div>
      <AppActionGroup className="rcl-billing-actions"><AppButton variant="text" label="Cancel" onClick={() => setPlanDialogOpen(false)} /><AppButton variant="primary" label="Save plan" icon="pi pi-check" loading={settingsBusy} onClick={() => saveSubscription({ plan_code: planDraft, current_usage: usageDraft }, () => setPlanDialogOpen(false))} /></AppActionGroup>
    </Dialog>

    <Dialog header="RentCast renewal" visible={renewalDialogOpen} onHide={() => setRenewalDialogOpen(false)} className="rcl-billing-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask>
      <div className="rcl-billing-form">
        <label><span>Next renewal</span><input type="date" value={renewalDraft} onChange={(event) => setRenewalDraft(event.target.value)} /></label>
        <small>The dashboard will repeat this billing day monthly and automatically calculate the current period.</small>
      </div>
      <AppActionGroup className="rcl-billing-actions"><AppButton variant="text" label="Cancel" onClick={() => setRenewalDialogOpen(false)} /><AppButton variant="primary" label="Save date" icon="pi pi-check" loading={settingsBusy} disabled={!renewalDraft} onClick={() => saveSubscription({ renewal_anchor_date: renewalDraft }, () => setRenewalDialogOpen(false))} /></AppActionGroup>
    </Dialog>

    <Dialog header="RentCast overage limit" visible={overageDialogOpen} onHide={() => setOverageDialogOpen(false)} className="rcl-billing-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask>
      <div className="rcl-billing-form">
        <label><span>Maximum overage spend per billing cycle</span><InputNumber value={overageLimitDraft} min={0} max={1000000} mode="currency" currency="USD" locale="en-US" minFractionDigits={2} maxFractionDigits={2} onValueChange={(event) => setOverageLimitDraft(event.value ?? 0)} /></label>
        <small>Live RentCast searches stop before the next request would push this account above the saved limit. Set $0 to block all overage requests. RentCast remains the authority for actual billing.</small>
      </div>
      <AppActionGroup className="rcl-billing-actions"><AppButton variant="text" label="Cancel" onClick={() => setOverageDialogOpen(false)} /><AppButton variant="primary" label="Save limit" icon="pi pi-check" loading={settingsBusy} onClick={() => saveSubscription({ overage_limit: overageLimitDraft }, () => setOverageDialogOpen(false))} /></AppActionGroup>
    </Dialog>

    <Dialog header={`${effectiveCreditProvider?.display_name || 'Provider'} spending protection`} visible={providerProtectionDialogOpen} onHide={() => setProviderProtectionDialogOpen(false)} className="rcl-billing-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask>
      <div className="rcl-billing-form">
        <label><span>Protected credit reserve</span><InputNumber value={providerReserveDraft} min={0} max={Math.max(0, Number(effectiveCreditProvider?.budget?.max_units_per_billing_cycle || 1) - 1)} useGrouping={false} onValueChange={(event) => setProviderReserveDraft(event.value ?? 0)} /></label>
        <small>Requests stop before consuming this cushion. In-progress reservations are also deducted before every provider call.</small>
        <label className="rcl-paid-credit-toggle"><span>Allow paid or top-up credits</span><input type="checkbox" role="switch" checked={providerPaidDraft} disabled={effectiveCreditProvider?.plan?.overage_behavior === 'blocked_by_provider'} onChange={(event) => setProviderPaidDraft(event.target.checked)} /></label>
        {effectiveCreditProvider?.plan?.overage_behavior === 'blocked_by_provider' && <small>This recorded plan stops when its prepaid credits run out. Upgrade or record a paid/top-up plan before this can be enabled.</small>}
      </div>
      <AppActionGroup className="rcl-billing-actions"><AppButton variant="text" label="Cancel" onClick={() => setProviderProtectionDialogOpen(false)} /><AppButton variant="primary" label="Save protection" icon="pi pi-shield" loading={settingsBusy} onClick={saveProviderProtection} /></AppActionGroup>
    </Dialog>

    {/* <section className="rcl-cards">
      <StatCard title="TRACKED THIS MONTH" value={rentCastQuotaUsage} icon={FaReceipt} color="#0f9d8a" sub="Shared provider ledger" />
      <StatCard title="REQUESTS IN VIEW" value={summary.requests || 0} icon={FaExchangeAlt} color="#3b82f6" sub={`${summary.counted_requests || 0} billable or ambiguous`} />
      <StatCard title="ROWS RETURNED" value={summary.rows_returned || 0} icon={FaList} color="#8b5cf6" sub="Complete source rows" />
      <StatCard title="UNIQUE LEADS" value={summary.unique_leads || 0} icon={FaDatabase} color="#f59e0b" sub="Deduplicated identities" />
      <StatCard title="OUTSIDE MARKET" value={summary.outside_rows || 0} icon={FaMapMarkerAlt} color="#ef6f6c" sub="Inside requested radius" />
    </section> */}

    <section className="rcl-table">
      {/* <div className="rcl-table-head"><div><span>RESPONSE EXPLORER</span><h2>Saved listing output</h2></div><p>Expand any row to inspect the exact endpoint, parameters, and original JSON.</p></div> */}
      <OverlayPanel ref={bedroomSpaceInfo} className="rcl-sfbed-popover" dismissable>
        <span className="rcl-sfbed-popover__eyebrow">BEDROOM-SPACE SCREEN</span>
        <div className="rcl-sfbed-formula"><strong>SF / Bed</strong><span>=</span><span className="rcl-formula-fraction"><span>Total square feet</span><span>Current bedrooms</span></span></div>
        <p>Higher values may reveal underused space. <strong>800+</strong> is high opportunity; <strong>650–799</strong> deserves review.</p>
        <small>Verify layout, finished area, permits, bedroom rules, and comparable sales.</small>
      </OverlayPanel>
      <OverlayPanel ref={rentArvInfo} className="rcl-sfbed-popover rcl-rent-arv-popover" dismissable>
        <span className="rcl-sfbed-popover__eyebrow">{activeRentArv?.source === 'zillow' ? 'ZILLOW' : 'RENTCAST'} 1% RULE</span>
        <div className="rcl-sfbed-formula rcl-rent-arv-formula"><strong>Rent / ARV</strong><span>=</span><span>Low Rent ÷ Low ARV · High Rent ÷ High ARV</span></div>
        {activeRentArv?.range ? <p><strong>{moneyRange(activeRentArv.rentScenario)}</strong> ÷ <strong>{moneyRange(activeRentArv.arvScenario, true)}</strong> = <strong>{percentRange(activeRentArv.range)}</strong></p> : <p>Run rent and ARV comps to calculate the range.</p>}
      </OverlayPanel>
      <OverlayPanel ref={cashFlowInfo} className="rcl-sfbed-popover rcl-cash-flow-popover" dismissable>
        <div className="rcl-popover-title-row">
          <span className="rcl-sfbed-popover__eyebrow">PROJECTED MONTHLY CASH FLOW</span>
          <span className="rcl-sfbed-popover__eyebrow">{Number(cashFlowInfoView?.defaults?.interest_rate_pct ?? CASH_FLOW_DEFAULT_VALUES.interest_rate_pct).toFixed(1)}% RATE</span>
        </div>
        <div className="rcl-sfbed-formula rcl-max-rehab-formula rcl-cash-flow-popover__formula"><strong>Cash Flow</strong><span>=</span><span>Rent − reserves − operating costs − mortgage P&amp;I</span></div>
        {cashFlowInfoView?.calculation ? <>
          <p className="rcl-cash-flow-popover__summary"><strong>{moneyRange(cashFlowInfoView.rentScenario)}</strong> {cashFlowInfoView.rentAdjusted ? 'adjusted rent' : 'rent'} produces <strong>{moneyRange(cashFlowInfoView.calculation)}/mo</strong> after projected expenses.</p>
          {cashFlowInfoView.loan && <section className="rcl-loan-sizing" aria-label="How the refinance loan is sized">
            {cashFlowInfoView.loan.lanes.map((lane) => <div key={lane.key} className="rcl-loan-sizing__lane">
              <div className="rcl-loan-sizing__math">
                <span className="rcl-loan-sizing__label">{lane.label}</span>
                <strong>{money(lane.arv)}</strong>
                {cashFlowInfoView.loan.override == null
                  ? <><span>× {cashFlowInfoView.loan.ltvPct.toFixed(0)}% LTV =</span><strong className="rcl-loan-sizing__loan">{money(lane.loan)}</strong></>
                  : <><span>→ override</span><strong className="rcl-loan-sizing__loan">{money(lane.loan)}</strong></>}
                <span>→</span><strong>{lane.payment == null ? '—' : `${money(lane.payment)}/mo`}</strong>
              </div>
              <div className="rcl-loan-sizing__bar" role="img" aria-label={`${lane.label} ${money(lane.arv)}: loan ${money(lane.loan)}, equity ${money(lane.equity)}`}>
                <span className="rcl-loan-sizing__fill" style={{ width: `${lane.loanSharePct}%` }}>Loan {money(lane.loan)}</span>
                {lane.equity > 0 && <span className="rcl-loan-sizing__equity">Equity {money(lane.equity)}</span>}
              </div>
            </div>)}
          </section>}
          <CashFlowMathTable calculation={cashFlowInfoView.calculation} ltvPct={cashFlowInfoView.defaults.refi_ltv_pct ?? CASH_FLOW_DEFAULT_VALUES.refi_ltv_pct} rentAdjusted={cashFlowInfoView.rentAdjusted} rentSource={cashFlowInfoView.rentSource} propertyTaxSource={cashFlowInfoView.propertyTaxSource} className="is-compact" />
        </> : <p>Choose comps in Selected Rent and Selected Sold to calculate projected cash flow.</p>}
      </OverlayPanel>
      <OverlayPanel ref={maxRehabInfo} className="rcl-sfbed-popover rcl-max-rehab-popover" dismissable>
        <span className="rcl-sfbed-popover__eyebrow">MAX REHAB SCREEN</span>
        <div className="rcl-sfbed-formula rcl-max-rehab-formula"><strong>Max Rehab</strong><span>=</span><span>(Selected Sold range × 75%) − Offer Price − Fixed Costs − Holding Costs</span></div>
        {activeMaxRehab?.range ? <p><strong>{moneyRange(activeMaxRehab.arvScenario)} × 75%</strong> − <strong>{money(activeMaxRehab.purchasePrice)}</strong> − <strong>{money(activeMaxRehab.fixedCosts)}</strong> − <strong>{money(activeMaxRehab.holdingCosts)}</strong> = <strong>{moneyRange(activeMaxRehab.range)}</strong></p> : <p>Select sold comps or run RentCast ARV comps to establish the ARV range used by this screen.</p>}
        <div className="rcl-max-rehab-cash">
          <div className="rcl-sfbed-formula rcl-max-rehab-formula"><strong>Cash Needed</strong><span>=</span><span>Offer Price + Max Rehab</span></div>
          {activeMaxRehab?.range ? <p><strong>{money(activeMaxRehab.purchasePrice)}</strong> + <strong>{moneyRange(activeMaxRehab.range)}</strong> = <strong>{moneyRange({ low: activeMaxRehab.purchasePrice + activeMaxRehab.range.low, high: activeMaxRehab.purchasePrice + activeMaxRehab.range.high })}</strong></p> : <p>Select sold comps or run RentCast ARV comps to calculate cash needed.</p>}
        </div>
        <small className="rcl-max-rehab-note">Selected Sold is preferred when available; RentCast ARV is the fallback. Property-specific Holding Costs and shared Fixed Costs are deducted so the range represents the remaining repair budget.</small>
      </OverlayPanel>
      <OverlayPanel ref={rehabPerSqftInfo} className="rcl-sfbed-popover rcl-max-rehab-popover" dismissable>
        <span className="rcl-sfbed-popover__eyebrow">REHAB / SQ FT SCREEN</span>
        <div className="rcl-sfbed-formula rcl-max-rehab-formula"><strong>Rehab / Sq Ft</strong><span>=</span><span>Max Rehab ÷ Total Sq Ft</span></div>
        {activeRehabPerSqft?.perSqft != null ? <p><strong>{money(activeRehabPerSqft.allowance)}</strong> ÷ <strong>{compactNumber(activeRehabPerSqft.squareFootage)} sq ft</strong> = <strong>{moneyPerSquareFoot(activeRehabPerSqft.perSqft)}/sq ft</strong></p> : <p>Run ARV comps and confirm square footage to calculate rehab budget per square foot.</p>}
        <small className="rcl-max-rehab-note">&lt;$20 tight · $20–$40 light-to-moderate · $40–$65 full renovation · &gt;$65 high-margin/gut rehab.</small>
        <small className="rcl-max-rehab-note rcl-max-rehab-note--divider">The higher this number is, the more money you have available to spend per square foot on repairs.</small>
      </OverlayPanel>
      <OverlayPanel ref={lowHeadacheInfo} className="rcl-sfbed-popover rcl-low-headache-popover" dismissable>
        <div id="rcl-low-headache-tooltip" role="tooltip">
          <span className="rcl-sfbed-popover__eyebrow">LOW HEADACHE AREA</span>
          <p className="rcl-low-headache-popover__question">What are the most important rules of thumb you use when analyzing properties?</p>
          <blockquote>
            <p><strong>Jason Masotti:</strong> Location is the first thing that matters. There’s nothing you can do to get by in a bad location. You have to be comfortable—that’s the first thing when analyzing a deal. Don’t chase something you’re not comfortable with, because that’s when you’re really going to start getting yourself into trouble.</p>
          </blockquote>
          <blockquote>
            <p><strong>Brandon Turner:</strong> One of my random rules of thumb when it comes to neighborhoods is: if I feel comfortable with my wife jogging there at night, then I will buy in that neighborhood.</p>
          </blockquote>
          <a href="https://open.spotify.com/episode/0K2I6SzNeJMgK0cokK0hiD" target="_blank" rel="noopener noreferrer">Jason Masotti on BiggerPockets <i className="pi pi-external-link" aria-hidden="true" /></a>
        </div>
      </OverlayPanel>
      <OverlayPanel ref={crimeInfo} className="rcl-sfbed-popover rcl-crime-popover" dismissable onMouseEnter={keepCrimeInfoOpen} onMouseLeave={hideCrimeInfo}>
        {activeCrimeSummary && <div role="tooltip" aria-label="Department-level crime comparison details">
          <div className="rcl-crime-popover__top">
            <span className="rcl-sfbed-popover__eyebrow">DEPARTMENT CRIME SCREEN</span>
            <button
              type="button"
              className="rcl-crime-popover__copy"
              onClick={copyCrimeResearchPrompt}
              disabled={activeCrimeSummary?.loading}
              aria-label={`Copy address and department crime evidence for ${activeCrimeSummary?.formatted_address || 'this property'} as an AI research prompt`}
            >
              <i className={`pi ${crimePromptCopied ? 'pi-check' : 'pi-copy'}`} aria-hidden="true" />
              {crimePromptCopied ? 'Copied' : 'Copy for research'}
            </button>
          </div>
          <div className="rcl-crime-popover__heading">
            <strong>{activeCrimeSummary?.agency_name || 'Local department unresolved'}</strong>
            <span className={`rcl-crime-popover__status rcl-crime-popover__status--${activeCrimeSummary?.screen_status || 'unknown'}`}>{crimeStatusLabel(activeCrimeSummary?.screen_status)}</span>
          </div>
          {activeCrimeSummary?.loading ? <p className="rcl-crime-popover__loading"><i className="pi pi-spinner pi-spin" aria-hidden="true" />Loading comparison details…</p> : activeCrimeSummary?.violent_crime && activeCrimeSummary?.property_crime ? <div className="rcl-crime-popover__metrics">
            <div><span>Violent crime</span><strong>{crimeOrdinal(activeCrimeSummary.violent_crime.national_peer_percentile)} percentile</strong><small>{crimeMetricDetail(activeCrimeSummary.violent_crime)}</small>{activeCrimeSummary.violent_crime.state_peers && <small>{crimeRatio(activeCrimeSummary.violent_crime.state_peers.ratio)} state peers · {activeCrimeSummary.violent_crime.state_peers.agency_count} agencies</small>}</div>
            <div><span>Property crime</span><strong>{crimeOrdinal(activeCrimeSummary.property_crime.national_peer_percentile)} percentile</strong><small>{crimeMetricDetail(activeCrimeSummary.property_crime)}</small>{activeCrimeSummary.property_crime.state_peers && <small>{crimeRatio(activeCrimeSummary.property_crime.state_peers.ratio)} state peers · {activeCrimeSummary.property_crime.state_peers.agency_count} agencies</small>}</div>
          </div> : <p>Comparable department-level rates are unavailable for this address.</p>}
          <div className="rcl-crime-popover__coverage"><span>Reporting</span><strong>{activeCrimeSummary?.reporting_months == null ? 'Unavailable' : `${activeCrimeSummary.reporting_months}/12 months`}</strong><span>Population</span><strong>{compactNumber(activeCrimeSummary?.represented_population)}</strong></div>
          {(activeCrimeSummary?.issues || []).map((issue) => <p className="rcl-crime-popover__issue" key={issue}><i className="pi pi-exclamation-triangle" aria-hidden="true" />{crimeIssueLabels[issue] || issue}</p>)}
          <small>Department-jurisdiction evidence—not neighborhood, or parcel crime. 
            </small>
        </div>}
      </OverlayPanel>
      <OverlayPanel ref={scenarioMenu} className="rcl-scenario-menu" dismissable>
        {activeScenarioMenu && <div>
          <strong>{activeScenarioMenu.type.startsWith('zillow_') ? 'Zillow ' : 'RentCast '}{activeScenarioMenu.type.endsWith('rent') ? 'Rent' : 'Comp'} scenario</strong>
          {scenariosFor(activeScenarioMenu.row, activeScenarioMenu.type).map((scenario) => {
            const selected = selectedScenario(activeScenarioMenu.row, activeScenarioMenu.type)?.id === scenario.id;
            return <button type="button" key={scenario.id} className={selected ? 'is-selected' : ''} onClick={() => chooseScenario(activeScenarioMenu.row, activeScenarioMenu.type, scenario.id)}>
              <i className={`pi ${selected ? 'pi-check' : ''}`} aria-hidden="true" />
              <span>{scenarioLabel(scenario)}</span>
              <b>{moneyRange(scenario, activeScenarioMenu.type.endsWith('arv'))}</b>
            </button>;
          })}
        </div>}
      </OverlayPanel>
      <OverlayPanel ref={compFailureInfo} className="rcl-comp-failure-popover" dismissable>
        {activeCompFailure && <div>
          <strong>{activeCompFailure.type === 'rent' ? 'Rent comps unavailable' : 'ARV comps unavailable'}</strong>
          <p>{activeCompFailure.message}</p>
          <small>{activeCompFailure.status_code ? `RentCast HTTP ${activeCompFailure.status_code}` : 'RentCast request failed'} · Full error JSON is in the expanded property row.</small>
        </div>}
      </OverlayPanel>
      <div className="rcl-table-toolbar">
        <div className="rcl-result-count"><strong>{visibleObservations.length.toLocaleString()}</strong><span>{tableScope === 'latest' ? 'new unique properties from latest pull' : `unique properties from ${Number(summary.rows_returned || 0).toLocaleString()} observations`}</span><div className="rcl-table-scope" role="group" aria-label="Lead table scope"><button type="button" className={tableScope === 'latest' ? 'is-active' : ''} onClick={() => { setTableScope('latest'); resetTablePaging(); }} disabled={!data.latest_request?.comparison_batch_id}>Latest pull</button><button type="button" className={tableScope === 'all' ? 'is-active' : ''} onClick={showAllLeads}>All leads</button></div><div className="rcl-review-scope" role="group" aria-label="Property review filter">{[['active', 'Active'], ['interested', 'Interested'], ['passed', 'Passed']].map(([value, label]) => <button type="button" key={value} className={reviewScope === value ? 'is-active' : ''} aria-pressed={reviewScope === value} onClick={() => { setReviewScope(value); resetTablePaging(); }}>{label}</button>)}</div>{tableFiltersActive && <><b>Filtered</b><Button label="Clear" text onClick={clearTableFilters} aria-label="Clear table filters" /></>}{selectedObservations.length === 1 && <AppButton variant="secondary" label={underwritingCases[selectedObservations[0].lead_id] || underwritingCases[selectedObservations[0].observation_id] ? 'Open Underwriting' : 'Create Underwriting'} icon="pi pi-file-edit" loading={underwritingBusy} disabled={underwritingBusy} onClick={createOrOpenUnderwriting} />}{selectedObservations.length === 1 && <AppButton variant="accent" label={portfolioProperties[selectedObservations[0].lead_id] ? 'Open Property' : 'Add To Portfolio'} icon="pi pi-home" onClick={openPortfolioDialog} />}{selectedObservations.length > 0 && <AppButton variant="primary" label={`Run APIs · ${selectedObservations.length}`} icon="pi pi-bolt" onClick={openApiDialog} />}</div>
        <div className="rcl-table-filters">
          <DashboardTableFilters filters={opportunityFilters} listingTypes={saleTypeOptions} onApply={(next) => { setOpportunityFilters(next); resetTablePaging(); }} onClear={() => { setOpportunityFilters(EMPTY_DASHBOARD_FILTERS); resetTablePaging(); }} />
          <Dropdown className="rcl-table-period" panelClassName="rcl-period-panel" value={tableDays} options={rangeOptions} onChange={(event) => changeTableDays(event.value)} aria-label="Table timeline" />
          <span className="rcl-table-search"><i className="pi pi-search" aria-hidden="true" /><InputText value={tableSearch} onChange={(event) => { setTableSearch(event.target.value); resetTablePaging(); }} placeholder="Search listings" aria-label="Search saved listings" /></span>
        </div>
      </div>
      <div className="rcl-table-scroll">
        {resultsDataTable}
      </div>
    </section>

    <AppFormDialog title="Add To Portfolio" titleMeta={portfolioLead?.formatted_address} visible={Boolean(portfolioLead)} onHide={() => !portfolioBusy && setPortfolioLead(null)} className="rcl-portfolio-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask={!portfolioBusy} closable={!portfolioBusy}>
      <div className="rcl-portfolio-facts">
        <span>Offer <strong>{portfolioLead?.offer?.offer_price != null ? `$${Number(portfolioLead.offer.offer_price).toLocaleString()}` : 'required'}</strong></span>
        <span>Asking <strong>{portfolioLead?.asking_price != null ? `$${Number(portfolioLead.asking_price).toLocaleString()}` : '—'}</strong></span>
        <span>Source evidence <strong>preserved</strong></span>
      </div>
      <section className="rcl-composer rcl-portfolio-composer">
        <div className="rcl-composer__selectors">
          <AppField label="Property nickname" optional htmlFor="portfolio-nickname"><InputText id="portfolio-nickname" value={portfolioNickname} maxLength={100} onChange={(event) => setPortfolioNickname(event.target.value)} /></AppField>
          <AppField label="Purchase date" htmlFor="portfolio-purchase-date"><InputText id="portfolio-purchase-date" type="date" value={portfolioPurchaseDate} onChange={(event) => setPortfolioPurchaseDate(event.target.value)} /></AppField>
        </div>
        <label className="rcl-provider-canary-note rcl-portfolio-check"><input type="checkbox" checked={portfolioClosed} onChange={(event) => setPortfolioClosed(event.target.checked)} /><span>I confirm this acquisition closed.</span></label>
      </section>
      {portfolioError && <div className="rcl-pass-review-error" role="alert">{portfolioError}</div>}
      <AppActionGroup className="rcl-portfolio-actions"><AppButton variant="text" label="Cancel" disabled={portfolioBusy} onClick={() => setPortfolioLead(null)} /><AppButton variant="outline" label="Add To Portfolio" icon="pi pi-home" loading={portfolioBusy} disabled={!portfolioPurchaseDate || !portfolioClosed || portfolioLead?.offer?.offer_price == null} onClick={addToPortfolio} /></AppActionGroup>
    </AppFormDialog>
    <Dialog header={reviewNoteStatus === 'interested' ? 'Mark property interested' : 'Pass on property'} visible={Boolean(passReviewRow)} onHide={closeReviewNoteDialog} className="rcl-pass-review-dialog" maskClassName="rcl-dialog-mask" transitionOptions={CLEAN_DIALOG_TRANSITION} modal draggable={false} dismissableMask={!reviewBusyLeadId} closable={!reviewBusyLeadId}>
      <div className="rcl-pass-review-form">
        <p>{reviewNoteStatus === 'interested'
          ? 'Save what stood out about this lead so it is available when reviewing interested properties later.'
          : 'Save the reason with this lead so it is available when reviewing passed properties later.'}</p>
        <div className="rcl-pass-review-property"><i className="pi pi-map-marker" aria-hidden="true" /><strong>{passReviewRow?.formatted_address || 'Address unavailable'}</strong></div>
        <label htmlFor="rcl-pass-review-notes"><span>{reviewNoteStatus === 'interested' ? 'Interested notes' : 'Pass notes'}</span>
        <InputTextarea id="rcl-pass-review-notes" value={passReviewNotes} onChange={(event) => setPassReviewNotes(event.target.value)} rows={5} maxLength={2000} autoResize autoFocus placeholder={reviewNoteStatus === 'interested' ? 'Record the value-add, layout, location, rent, or other opportunity…' : 'Record the pricing, condition, location, underwriting, or other concern…'} />
        </label>
        <small>{passReviewNotes.length.toLocaleString()} / 2,000 characters{reviewNoteStatus === 'interested' ? ' · optional' : ''}</small>
        {reviewSaveError && <div className="rcl-pass-review-error" role="alert"><i className="pi pi-exclamation-circle" aria-hidden="true" />{reviewSaveError}</div>}
      </div>
      <div className="rcl-pass-review-actions"><Button label="Cancel" text onClick={closeReviewNoteDialog} />{reviewNoteStatus === 'interested'
        ? <Button label="Save and mark interested" icon="pi pi-check" className="rcl-review-save--interested" loading={reviewBusyLeadId === passReviewRow?.lead_id} onClick={submitPassReview} />
        : <Button label="Save and pass" icon="pi pi-times" severity="danger" loading={reviewBusyLeadId === passReviewRow?.lead_id} disabled={!passReviewNotes.trim()} onClick={submitPassReview} />}</div>
    </Dialog>
    <SoldCompPrototype key={soldCompLead?.lead_id || 'sold-comp-empty'} lead={soldCompLead} selectedCompIds={soldCompLead ? reviewedMembers(soldCompLead, 'zillow_arv').map((item) => item.comp_sale_id) : []} onToggleSelected={(comp) => toggleReviewedComp(soldCompLead, 'zillow_arv', comp)} onClose={() => setSoldCompLead(null)} />
    <SoldCompPrototype key={rentalCompLead?.lead_id || 'rental-comp-empty'} lead={rentalCompLead} compType="rent" selectedCompIds={rentalCompLead ? reviewedMembers(rentalCompLead, 'zillow_rent').map((item) => item.comp_rental_id) : []} onToggleSelected={(comp) => toggleReviewedComp(rentalCompLead, 'zillow_rent', comp)} onClose={() => setRentalCompLead(null)} />
    {hoveredPassReview && <div className={`rcl-pass-review-tooltip${hoverReviewEditing ? ' is-editing' : ''}`} role={hoverReviewEditing ? 'dialog' : 'tooltip'} aria-label={hoverReviewEditing ? `Edit ${hoveredPassReview.row.review_status} note for ${hoveredPassReview.row.formatted_address || 'property'}` : undefined} onClick={(event) => event.stopPropagation()} onMouseEnter={keepPassTooltipOpen} onMouseLeave={hidePassTooltip} style={{ left: Math.max(12, Math.min(hoveredPassReview.x + 14, window.innerWidth - 366)), top: Math.max(12, Math.min(hoveredPassReview.y + 14, window.innerHeight - 270)) }}>
      <div className="rcl-pass-review-tooltip__header"><span>{hoveredPassReview.row.review_status === 'interested' ? 'INTERESTED NOTES' : 'PASS NOTES'}</span>{!hoverReviewEditing && <button type="button" onClick={startHoverReviewEdit}><i className="pi pi-pencil" aria-hidden="true" />Edit</button>}</div>
      <strong>{hoveredPassReview.row.formatted_address}</strong>
      {hoverReviewEditing ? <><textarea aria-label={`${hoveredPassReview.row.review_status === 'interested' ? 'Interested' : 'Passed'} note`} value={hoverReviewDraft} onChange={(event) => setHoverReviewDraft(event.target.value)} maxLength={2000} autoFocus /><div className="rcl-pass-review-tooltip__edit-footer"><small>{hoverReviewDraft.length.toLocaleString()} / 2,000</small><div><button type="button" onClick={cancelHoverReviewEdit}>Cancel</button><button type="button" className="is-save" disabled={reviewBusyLeadId === hoveredPassReview.row.lead_id || (hoveredPassReview.row.review_status === 'passed' && !hoverReviewDraft.trim())} onClick={saveHoverReviewNote}>{reviewBusyLeadId === hoveredPassReview.row.lead_id ? 'Saving…' : 'Save'}</button></div></div></> : <><p>{hoveredPassReview.row.review_notes || (hoveredPassReview.row.review_status === 'interested' ? 'No interested notes have been recorded yet.' : 'No pass notes were recorded for this earlier review.')}</p><small>Recorded {formatPulledDateTime(hoveredPassReview.row.reviewed_at)}</small></>}
    </div>}
  </main>;
}
